import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { yearMonthBadge } from "@/lib/periodBadge";
import { useParams, useLocation, useSearch } from "wouter";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { ClaireTranscripts } from "@/components/ClaireTranscripts";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  api,
  fetchWithAuth,
  emailApi,
  Account,
  ClientEmail,
  PinnedEmail,
  ClientNote,
  Contact,
  Project,
  ProjectTask,
  ProjectMessage,
  ClientTopic,
  ClientTopicMessage,
  User as ApiUser,
  AccountRelationship,
  AccountResource,
  AccountAssignee,
  AccountActivity,
  SmsLogEntry,
  AccountService,
  uploadClientTopicMessageAttachment,
  deleteClientTopicMessageAttachment,
  getClientTopicMessageAttachments,
  getMessageAttachments,
  uploadMessageAttachment,
  deleteMessageAttachment,
  type ChatAttachment,
  provisionSmartVaultClient,
  getEmailAutomationLog,
  type EmailAutomationEntry,
  TaskSection,
  SectionReminderConfig,
  Tag,
  getAccountServices,
  createAccountService,
  updateAccountService,
  deleteAccountService,
  getVaultRecords,
  getFinancialSources,
  getMeetings,
  getMeetingFreshStartUrl,
  type Meeting,
  getAccountSignatureRequests,
  getAgreementTemplates,
  sendSignatureReminder,
  voidSignatureRequest,
  syncSignatureRequest,
  type SignatureRequest,
  type AgreementTemplate,
  WorkflowPipeline,
} from "@/lib/api";
import { portalApi, PortalInvite } from "@/lib/portalApi";
import { cancelMeetingFull, updateMeetingFull } from "@/lib/calendarApi";
import { parseEasternDateTime } from "@/lib/etTime";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/authContext";
import { ProjectMessagesSheet, MessageRow } from "@/components/custom/ProjectMessagesSheet";
import { SectionAutomationsModal } from "@/components/custom/SectionAutomationsModal";
import { TagPicker, TagChip } from "@/components/custom/TagPicker";
import { ProjectEditDialog } from "@/components/custom/ProjectEditDialog";
import { TaskDetailRow } from "@/components/custom/TaskDetailRow";
import { AccountFinancialSources } from "@/components/custom/AccountFinancialSources";
import { AccountClientVault } from "@/components/custom/AccountClientVault";
import { SmartVaultPanel } from "@/components/custom/SmartVaultPanel";
import { ClientDocuments } from "@/components/custom/ClientDocuments";
import { ClientScopeItems } from "@/components/ClientScopeItems";
import { SendNotificationModal } from "@/components/custom/SendNotificationModal";
import { ScheduleMeetingModal } from "@/components/custom/ScheduleMeetingModal";
import { SendForSignatureModal } from "@/components/custom/SendForSignatureModal";
import { EmailReplyModal } from "@/components/custom/EmailReplyModal";
import { EmailRow } from "@/components/custom/email/EmailRow";
import { ClaimEmailDialog } from "@/components/custom/email/ClaimEmailDialog";
import { EmailForwardDialog } from "@/components/custom/email/EmailForwardDialog";
import { ComposeEmailDialog } from "@/components/custom/email/ComposeEmailDialog";
import { BulkEmailActionBar } from "@/components/custom/email/BulkEmailActionBar";
import { useEmailActions, type BulkEmailAction } from "@/components/custom/email/useEmailActions";
import { useEmailSelection } from "@/components/custom/email/useEmailSelection";
import { useStickyPill } from "@/components/custom/email/useStickyPill";
import { useLoadOlder } from "@/components/custom/email/useLoadOlder";
import { fromClientEmail, clientEmailFromInboxMessage, toReplyEmail, type UnifiedEmail } from "@/lib/email/types";
import { type EmailPill } from "@/lib/email/filters";
import { matchesPillSticky } from "@/lib/email/stickyPill";
import { CatchMeUpModal } from "@/components/custom/CatchMeUpModal";
import { MakeClientInactiveDialog, type InactiveTarget } from "@/components/custom/MakeClientInactiveDialog";
import { projectTypeBadgeClasses, computePeriodLabel } from "@/lib/projectTypeColors";
import { WorkflowStatusBadge } from "@/components/custom/WorkflowStatusBadge";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/lib/confirmContext";
import { buildProjectDeleteWarning } from "@/lib/projectDeleteWarning";
import { cn } from "@/lib/utils";
import { deepLinkReveal, isFutureProject } from "@/lib/projectView";
import { projectsFilterFromFlags, flagsFromProjectsFilter, recentPortalUploads, type ProjectsFilter } from "@/lib/accountOverview";
import { OverviewCard } from "@/components/custom/OverviewCard";
import { reminderNextDisplay } from "@/lib/reminderNextDisplay";
import { computeAutoCollapsedSectionIds } from "@/lib/sectionAutoCollapse";
import { buildProjectCloseWarning } from "@/lib/projectCloseWarning";
import { CompleteConfirmDialog } from "@/components/custom/CompleteConfirmDialog";
import { format, parseISO } from "date-fns";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Globe,
  Phone,
  Mail,
  ExternalLink,
  StickyNote,
  Pin,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Briefcase,
  Link2,
  CheckCircle2,
  Clock,
  Trash2,
  Loader2,
  User,
  Plus,
  Users2,
  Activity,
  ListTodo,
  X,
  MessageSquare,
  Send,
  Reply,
  Paperclip,
  Inbox,
  RefreshCw,
  Search,
  Check,
  FileText,
  Image,
  Lock,
  Pencil,
  HardDrive,
  AlertCircle,
  UserCheck,
  Zap,
  XCircle,
  Copy,
  ClipboardCheck,
  Calendar,
  Smartphone,
  MessageSquareText,
  DollarSign,
  FolderKanban,
  Calculator,
  Grid3X3,
  Bell,
  BellOff,
  Eye,
  Download,
  KeyRound,
  Video,
  ExternalLink as ExternalLinkIcon,
  FileSignature,
  CheckCircle2 as CheckCircle2Icon,
  ArrowUpDown,
  Clock as ClockIcon,
  Send as SendIcon,
  Maximize2,
  AtSign,
  Sparkles,
  ListOrdered,
  PanelRightClose,
  PanelRightOpen,
  LayoutGrid,
  Rows3,
} from "lucide-react";
import { LikeButton } from "@/components/custom/LikeButton";
import { triggerDownload } from "@/lib/download";
import { useUserPref, PREF_ACCOUNT_RIGHT_COLLAPSED, useAccountOverviewView } from "@/lib/userPrefs";

// ─── helpers ─────────────────────────────────────────────────────────────────

function CopyBtn({ value }: { value: string }) {
  const { toast } = useToast();
  return (
    <button
      className="text-slate-500 hover:text-foreground transition-colors shrink-0 ml-1 p-0.5 rounded hover:bg-muted"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigator.clipboard.writeText(value); toast({ title: "Copied!" }); }}
      title="Copy to clipboard"
    >
      <Copy className="w-4 h-4" />
    </button>
  );
}

function UserAvatar({ name, email }: { name: string | null; email: string | null }) {
  const initials = name
    ? name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : email?.slice(0, 2).toUpperCase() || "??";
  return (
    <span
      className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-300 text-slate-700 text-[9px] font-bold ring-1 ring-white shrink-0"
      title={name ?? email ?? ""}
    >
      {initials}
    </span>
  );
}

function triggerProjectConfetti(x: number, y: number) {
  const container = document.createElement("div");
  container.style.cssText =
    "position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9999;overflow:hidden;";
  document.body.appendChild(container);
  const colors = ["#22c55e", "#3b82f6", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4", "#f97316"];
  const count = 20;
  for (let i = 0; i < count; i++) {
    const dot = document.createElement("div");
    const color = colors[i % colors.length];
    const angle = (i / count) * 360 + Math.random() * 20;
    const dist = 45 + Math.random() * 70;
    const dx = Math.cos((angle * Math.PI) / 180) * dist;
    const dy = Math.sin((angle * Math.PI) / 180) * dist - 30;
    const size = 5 + Math.random() * 6;
    const isRect = Math.random() > 0.5;
    dot.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:${size}px;height:${isRect ? size * 0.5 : size}px;background:${color};border-radius:${isRect ? "2px" : "50%"};transform:translate(-50%,-50%) rotate(${Math.random() * 360}deg);opacity:1;transition:transform 0.7s cubic-bezier(.2,.8,.4,1),opacity 0.7s ease-out;`;
    container.appendChild(dot);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      dot.style.transform = `translate(calc(-50% + ${dx}px),calc(-50% + ${dy}px)) rotate(${Math.random() * 720}deg)`;
      dot.style.opacity = "0";
    }));
  }
  setTimeout(() => document.body.removeChild(container), 800);
}

function fmt(dateStr: string | null | undefined) {
  if (!dateStr) return null;
  try { return format(parseISO(dateStr), "MMM d, yyyy"); } catch { return dateStr; }
}

function fmtDuration(minutes: number) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

const STATUS_COLORS: Record<string, string> = {
  open: "bg-blue-100 text-blue-800",
  in_progress: "bg-green-100 text-green-800",
  on_hold: "bg-yellow-100 text-yellow-800",
  waiting_on_client: "bg-orange-100 text-orange-800",
  closed: "bg-gray-100 text-gray-600",
  completed: "bg-gray-100 text-gray-600",
};

const STATUS_LABELS: Record<string, string> = {
  open: "Open",
  in_progress: "In Progress",
  on_hold: "On Hold",
  waiting_on_client: "Waiting on Client",
  closed: "Closed",
  completed: "Completed",
};

const RELATIONSHIP_SUGGESTIONS = ["Owner", "Partner", "Spouse", "Parent", "Subsidiary", "Sibling entity", "Related party"];

// ─── sub-components ──────────────────────────────────────────────────────────

/** Card section with a collapsible header. defaultOpen controls initial state. */
function Section({
  title,
  count,
  defaultOpen = true,
  action,
  children,
}: {
  title: string;
  count?: number;
  defaultOpen?: boolean;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border bg-card shadow-sm">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors rounded-t-lg"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flex items-center gap-2">
          <span className="text-sm font-bold text-foreground tracking-tight">{title}</span>
          {count !== undefined && (
            <span className="text-xs text-muted-foreground bg-muted rounded-full px-1.5 py-0.5 font-medium leading-none">
              {count}
            </span>
          )}
        </span>
        <span className="flex items-center gap-2">
          {open && action && <span onClick={(e) => e.stopPropagation()}>{action}</span>}
          {open ? <ChevronUp className="w-4 h-4 text-foreground/60" /> : <ChevronDown className="w-4 h-4 text-foreground/60" />}
        </span>
      </button>
      {open && <div className="px-4 pb-4 pt-1">{children}</div>}
    </div>
  );
}

/** Sidebar section — no card border, just a label + optional action + collapsible body. */
function SideSection({
  title,
  defaultOpen = true,
  count,
  action,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <button
          type="button"
          className="flex items-center gap-1 group"
          onClick={() => setOpen((v) => !v)}
        >
          {open
            ? <ChevronUp className="w-3.5 h-3.5 text-foreground shrink-0" />
            : <ChevronDown className="w-3.5 h-3.5 text-foreground shrink-0" />}
          <span className="text-sm font-bold text-foreground tracking-tight group-hover:text-foreground/80 transition-colors">
            {title}
          </span>
          {(count ?? 0) > 0 && (
            <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
          )}
        </button>
        {action}
      </div>
      {open && <div>{children}</div>}
    </div>
  );
}

function NoteCard({ note, onEdit, onDelete, onTogglePin }: {
  note: ClientNote;
  onEdit?: (note: ClientNote) => void;
  onDelete?: (note: ClientNote) => void;
  onTogglePin?: (note: ClientNote) => void;
}) {
  const [expanded, setExpanded] = useState(note.is_pinned);

  return (
    <div className={`rounded-lg border p-3 ${note.is_pinned ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20" : "border-border bg-muted/20"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {note.is_pinned && <Pin className="w-3 h-3 text-amber-500 shrink-0" />}
          <span className="text-sm font-medium truncate">{note.title || "Note"}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-xs text-muted-foreground mr-1">{fmt(note.created_at)}</span>
          {onTogglePin && (
            <button
              onClick={() => onTogglePin(note)}
              title={note.is_pinned ? "Unpin" : "Pin"}
              className={`p-1 rounded transition-colors ${note.is_pinned ? "text-amber-500 hover:text-amber-600" : "text-muted-foreground hover:text-amber-500"}`}
            >
              <Pin className="w-3.5 h-3.5" />
            </button>
          )}
          {onEdit && (
            <button
              onClick={() => onEdit(note)}
              title="Edit note"
              className="p-1 rounded text-muted-foreground hover:text-foreground transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(note)}
              title="Delete note"
              className="p-1 rounded text-muted-foreground hover:text-destructive transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={() => setExpanded((e) => !e)}
            className="p-1 rounded text-muted-foreground hover:text-foreground transition-colors"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-2">
          {note.body ? (
            <div
              className="text-sm prose prose-sm max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: note.body }}
            />
          ) : note.plain_text ? (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{note.plain_text}</p>
          ) : (
            <p className="text-sm text-muted-foreground italic">No content</p>
          )}
        </div>
      )}

      {!expanded && note.plain_text && (
        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{note.plain_text}</p>
      )}
    </div>
  );
}

function ProjectRow({ project, onDelete, accountName, openTaskId, openProjectId, clientResponseCount, taskPortalMap, accountContacts }: { project: Project; onDelete?: (id: string) => void; accountName?: string; openTaskId?: string | null; openProjectId?: string | null; clientResponseCount?: number; taskPortalMap?: Record<string, number>; accountContacts?: Contact[] }) {
  const [, navigate] = useLocation();
  const [expanded, setExpanded] = useState(() => !!openProjectId && project.id === openProjectId);
  const rowRef = useRef<HTMLDivElement>(null);
  // Expand when openProjectId changes after mount (same-page notification click)
  useEffect(() => {
    if (openProjectId && project.id === openProjectId && !expanded) {
      setExpanded(true);
    }
  }, [openProjectId, project.id]);
  // Scroll the deep-linked project into view once the list has rendered, so a click from the
  // Projects table lands ON the project (expanded) rather than at the top of the client's list.
  useEffect(() => {
    if (openProjectId && project.id === openProjectId) {
      const t = setTimeout(() => rowRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
      return () => clearTimeout(t);
    }
  }, [openProjectId, project.id]);
  const [tasks, setTasks] = useState<ProjectTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [projectStatus, setProjectStatus] = useState(project.status);
  const [addingTask, setAddingTask] = useState(false);
  const [addingTaskSectionId, setAddingTaskSectionId] = useState<string | null>(null);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [lastNotifiedAt, setLastNotifiedAt] = useState<string | null>(project.client_tasks_last_notified_at ?? null);
  const [sectionsList, setSectionsList] = useState<TaskSection[]>([]);
  const [addingSection, setAddingSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [newSectionIsClient, setNewSectionIsClient] = useState(false);
  const [newSectionIsSequential, setNewSectionIsSequential] = useState(false);
  const [markingReady, setMarkingReady] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState<{ x: number; y: number } | null>(null);
  const [projectTags, setProjectTags] = useState<Tag[]>(project.project_tags ?? []);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [statusPending, setStatusPending] = useState(false);
  const [clientSectionId, setClientSectionId] = useState<string | null>(null);
  const [clientSectionReminderState, setClientSectionReminderState] = useState<{ is_active: boolean; days: number; schedule_type: string; monthly_day?: number | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchWithAuth<TaskSection[]>(`/api/projects/${project.id}/sections`)
      .then((secs) => {
        if (cancelled) return;
        const cs = secs.find((s) => s.section_type === "client_tasks");
        if (cs) {
          setClientSectionId(cs.id);
          api.getSectionReminder(cs.id).then(cfg => {
            if (!cancelled) setClientSectionReminderState(cfg ? { is_active: cfg.is_active, days: cfg.days, schedule_type: cfg.schedule_type ?? "interval", monthly_day: cfg.monthly_day } : null);
          }).catch(() => {});
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [project.id]);

  const { data: workflowPipelines = [] } = useQuery<WorkflowPipeline[]>({
    queryKey: ["workflow-pipelines"],
    queryFn: () => api.getWorkflowPipelines(),
    staleTime: 5 * 60 * 1000,
  });

  const handleWorkflowStatusChange = async (projectId: string, statusId: string) => {
    setStatusPending(true);
    try {
      await api.updateProject(projectId, { workflow_status_id: statusId } as any);
    } finally {
      setStatusPending(false);
    }
  };
  // Per-section automation rules — simple open/close state; modal manages its own data
  const [sectionRulesOpen, setSectionRulesOpen] = useState<string | null>(null);
  // Per-section collapse state
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  // ENG-86: seed collapsedSections with fully-done sections exactly once per row mount —
  // a later loadTasks() re-fetch (e.g. after completing the project's last task, or the
  // v1.5.39-class refetches) must never re-collapse a section the user has interacted with.
  const autoCollapseSeededRef = useRef(false);

  // Per-section reminder config state
  const [reminderOpen, setReminderOpen] = useState<string | null>(null); // section id
  const [sectionReminders, setSectionReminders] = useState<Record<string, SectionReminderConfig | null>>({});
  const [reminderDraft, setReminderDraft] = useState<{ days: number; is_active: boolean; contacts: string; schedule_type: "interval" | "monthly" | "section"; monthly_day: number; trigger_section_id: string }>({ days: 3, is_active: false, contacts: "", schedule_type: "interval", monthly_day: 1, trigger_section_id: "" });
  const [reminderSaving, setReminderSaving] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const { user: projectRowUser } = useAuth();
  const queryClient = useQueryClient();

  // Default "Send to" contacts from the account's contacts on file (no extra fetch — passed down from AccountDetail)
  const defaultReminderContacts = (accountContacts ?? []).map(c => c.email).filter((e): e is string => !!e).join(", ");

  const seedReminderDraft = (cfg: SectionReminderConfig | null) => {
    if (cfg) {
      setReminderDraft({ days: cfg.days, is_active: cfg.is_active, contacts: (cfg.contacts ?? []).length ? cfg.contacts.join(", ") : defaultReminderContacts, schedule_type: (cfg.schedule_type ?? "interval") as "interval" | "monthly" | "section", monthly_day: cfg.monthly_day ?? 1, trigger_section_id: cfg.trigger_section_id ?? "" });
    } else {
      setReminderDraft({ days: 3, is_active: false, contacts: defaultReminderContacts, schedule_type: "interval", monthly_day: 1, trigger_section_id: "" });
    }
  };

  const openReminderPanel = async (sectionId: string) => {
    if (reminderOpen === sectionId) { setReminderOpen(null); return; }
    setReminderOpen(sectionId);
    // Seed instantly from any cached copy, then ALWAYS refetch — a cached copy can be stale,
    // and trusting it for the whole session hid failed saves for months (ENG-57).
    if (sectionId in sectionReminders) seedReminderDraft(sectionReminders[sectionId]);
    try {
      const cfg = await api.getSectionReminder(sectionId);
      setSectionReminders(prev => ({ ...prev, [sectionId]: cfg }));
      seedReminderDraft(cfg);
    } catch {
      // Failed re-fetch: do NOT clear the draft to empty/Off — that would let a subsequent
      // save silently wipe out live config. Leave whatever draft is already in state and warn.
      toast({ title: "Couldn't load saved reminder settings", description: "Showing your unsaved changes only — reopen to retry.", variant: "destructive" });
    }
  };

  const saveReminder = async (sectionId: string) => {
    setReminderSaving(true);
    try {
      const contacts = reminderDraft.contacts.split(",").map(s => s.trim()).filter(Boolean);
      const saved = await api.upsertSectionReminder(sectionId, {
        days: reminderDraft.days,
        is_active: reminderDraft.is_active,
        contacts,
        schedule_type: reminderDraft.schedule_type,
        monthly_day: reminderDraft.schedule_type === "monthly" ? reminderDraft.monthly_day : null,
        trigger_section_id: reminderDraft.schedule_type === "section" ? reminderDraft.trigger_section_id || null : null,
      });
      setSectionReminders(prev => ({ ...prev, [sectionId]: saved }));
      // Keep the project-header Auto-Reminders chip in sync (it's seeded once on mount otherwise).
      if (sectionId === clientSectionId) {
        setClientSectionReminderState({ is_active: saved.is_active, days: saved.days, schedule_type: saved.schedule_type ?? "interval", monthly_day: saved.monthly_day });
      }
      toast({ title: saved.is_active ? "Auto-reminder activated" : "Reminder saved (inactive)" });
    } catch {
      toast({ title: "Failed to save reminder", variant: "destructive" });
    } finally {
      setReminderSaving(false);
    }
  };

  const sendReminderNow = async (sectionId: string) => {
    setReminderSending(true);
    try {
      // Persist whatever is currently typed in the "Send to" field FIRST — the server sends to
      // its saved contacts, so an un-saved edit must be committed before Send Now fires.
      const contacts = reminderDraft.contacts.split(",").map(s => s.trim()).filter(Boolean);
      const saved = await api.upsertSectionReminder(sectionId, {
        days: reminderDraft.days,
        is_active: reminderDraft.is_active || contacts.length > 0,
        contacts,
        schedule_type: reminderDraft.schedule_type,
        monthly_day: reminderDraft.schedule_type === "monthly" ? reminderDraft.monthly_day : null,
        trigger_section_id: reminderDraft.schedule_type === "section" ? reminderDraft.trigger_section_id || null : null,
      });
      setSectionReminders(prev => ({ ...prev, [sectionId]: saved }));

      const res = await api.sendSectionReminderNow(sectionId) as {
        sent: boolean;
        taskCount: number;
        emailed_to?: string[];
        error?: string;
      };
      const cfg = await api.getSectionReminder(sectionId);
      setSectionReminders(prev => ({ ...prev, [sectionId]: cfg }));
      if (sectionId === clientSectionId && cfg) {
        setClientSectionReminderState({ is_active: cfg.is_active, days: cfg.days, schedule_type: cfg.schedule_type ?? "interval", monthly_day: cfg.monthly_day });
      }
      setLastNotifiedAt(new Date().toISOString());
      if (project.account_id) {
        queryClient.invalidateQueries({ queryKey: ["projects", project.account_id] });
      }
      if (res.emailed_to !== undefined) {
        if (res.emailed_to.length === 0) {
          toast({ title: "Nothing was sent", description: res.error || "No contacts could be emailed.", variant: "destructive" });
        } else {
          toast({ title: `Sent to ${res.emailed_to.join(", ")}` });
        }
      } else {
        // Older server response shape without emailed_to — fall back to prior behavior
        toast({ title: `Reminder sent (${res.taskCount} task${res.taskCount !== 1 ? "s" : ""})` });
      }
    } catch {
      toast({ title: "Failed to send reminder", variant: "destructive" });
    } finally {
      setReminderSending(false);
    }
  };

  const handleMarkReadyForReview = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setMarkingReady(true);
    try {
      await api.markProjectReadyForReview(project.id);
      toast({ title: "Notified", description: "Team members have been notified this project is ready for review." });
    } catch {
      toast({ title: "Failed to send notification", variant: "destructive" });
    } finally {
      setMarkingReady(false);
    }
  };

  const setProjectDone = async (newStatus: "open" | "closed", coords?: { x: number; y: number }) => {
    const prevStatus = projectStatus;
    setProjectStatus(newStatus);
    if (newStatus === "closed" && coords) {
      // Confetti from the checkbox position
      triggerProjectConfetti(coords.x, coords.y);
    }
    try {
      await api.updateProject(project.id, { status: newStatus });
      // Closing cascades server-side: PUT /api/projects/:id completes the project's remaining
      // open tasks (root AND sub-tasks) and triggers the recurrence spawn. Reflect that in the
      // rows this page has already loaded, then re-read so sub-tasks and completion stamps are
      // the server's version rather than a guess.
      if (newStatus === "closed") {
        setTasks((prev) => prev.map((t) => (t.status === "done" ? t : { ...t, status: "done" })));
        if (fetched) loadTasks();
      }
    } catch {
      setProjectStatus(prevStatus); // revert on error
    }
  };

  const toggleProjectDone = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (projectStatus === "closed") {
      setProjectDone("open");
      return;
    }
    setConfirmComplete({ x: e.clientX, y: e.clientY });
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const [result, secs] = await Promise.all([
        api.getProjectTasks(project.id),
        api.getProjectSections(project.id),
      ]);
      setTasks(result);
      setSectionsList(secs);
      setFetched(true);

      // ENG-86: one-shot auto-collapse seed, computed off the local result/secs
      // (not state, which hasn't committed yet). openTaskId is the exemption —
      // a "Your turn"/bell/email deep link into a task inside a fully-done
      // section must still land on a visible, auto-expanding row (TaskDetailRow
      // unmounts while its section is collapsed).
      if (!autoCollapseSeededRef.current) {
        autoCollapseSeededRef.current = true;
        const bucket = new Map<string, ProjectTask[]>();
        for (const task of result) {
          const sectionId = task.section_id || "__none__";
          const list = bucket.get(sectionId);
          if (list) list.push(task);
          else bucket.set(sectionId, [task]);
        }
        const toCollapse = computeAutoCollapsedSectionIds(secs, bucket, openTaskId);
        if (toCollapse.size > 0) {
          setCollapsedSections(prev => {
            const next = new Set(prev);
            for (const sectionId of toCollapse) next.add(sectionId);
            return next;
          });
        }
      }
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  };

  // Deep-link paths (?openProject= from notification emails, the bell, search)
  // set `expanded` without going through handleToggle — fetch the tasks so the
  // row doesn't render an empty body and ?openTask= can auto-expand its row.
  useEffect(() => {
    if (expanded && !fetched && !loading) loadTasks();
  }, [expanded]);

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    const params = new URLSearchParams(window.location.search);
    if (next) params.set("openProject", project.id);
    else params.delete("openProject");
    const qs = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? "?" + qs : ""));
    // Task fetch is owned by the expanded-effect above (single fetch path for
    // both chevron clicks and deep links).
  };

  const handleAddSection = async () => {
    const trimmed = newSectionName.trim();
    const isClient = newSectionIsClient;
    const isSequential = newSectionIsSequential;
    setAddingSection(false);
    setNewSectionName("");
    setNewSectionIsClient(false);
    setNewSectionIsSequential(false);
    if (!trimmed) return;
    try {
      const maxIdx = sectionsList.reduce((m, s) => Math.max(m, s.list_index), -1);
      const created = await api.createProjectSection(project.id, {
        name: trimmed,
        section_type: isClient ? "client_tasks" : "firm_tasks",
        is_client_facing: isClient,
        list_index: maxIdx + 1,
        is_sequential: isSequential,
      });
      setSectionsList(prev => [...prev, created]);
    } catch {
      toast({ title: "Failed to add section", variant: "destructive" });
    }
  };

  const handleToggleSectionType = async (sectionId: string, currentType: string) => {
    const newType = currentType === "client_tasks" ? "firm_tasks" : "client_tasks";
    try {
      await api.updateTaskSection(sectionId, { section_type: newType, is_client_facing: newType === "client_tasks" });
      setSectionsList(prev => prev.map(s => s.id === sectionId ? { ...s, section_type: newType } : s));
    } catch {
      toast({ title: "Failed to update section type", variant: "destructive" });
    }
  };

  // Toggle "tasks must be done in order" (advisory sequencing + "your turn" notifications)
  const handleToggleSequential = async (sectionId: string, current: boolean) => {
    try {
      await api.updateTaskSection(sectionId, { is_sequential: !current });
      setSectionsList(prev => prev.map(s => s.id === sectionId ? { ...s, is_sequential: !current } : s));
    } catch {
      toast({ title: "Failed to update section ordering", variant: "destructive" });
    }
  };

  const handleStatusChange = (taskId: string, done: boolean) => {
    setTasks(prev => prev.map(t =>
      t.id === taskId ? { ...t, status: done ? "done" : "open", completed_at: done ? new Date().toISOString() : null } : t
    ));
  };

  const handleTaskUpdate = (taskId: string, updates: Partial<ProjectTask>) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updates } : t));
  };

  const handleTaskDelete = (taskId: string) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
  };

  const handleAddTask = async (sectionId?: string | null) => {
    const title = newTaskTitle.trim();
    if (!title) { setAddingTask(false); setAddingTaskSectionId(null); return; }
    setNewTaskTitle("");
    setAddingTask(false);
    setAddingTaskSectionId(null);
    try {
      const created = await api.createProjectTask(project.id, {
        title,
        section_id: sectionId ?? undefined,
      });
      setTasks(prev => [...prev, created]);
    } catch {
      // silently ignore — task wasn't created
    }
  };

  // Group tasks by section. Use sectionsList as source of truth so empty sections show up.
  const sections = (() => {
    const map = new Map<string, { id: string | null; name: string; sectionListIndex: number; sectionType: string; isSequential: boolean; tasks: ProjectTask[] }>();
    // Seed from fetched sections list first
    for (const sec of sectionsList) {
      map.set(sec.id, { id: sec.id, name: sec.name, sectionListIndex: sec.list_index, sectionType: sec.section_type, isSequential: !!sec.is_sequential, tasks: [] });
    }
    // Bucket tasks into their section
    for (const task of tasks) {
      const key = task.section_id || "__none__";
      if (!map.has(key)) {
        const sec = task.task_sections as any;
        map.set(key, { id: key === "__none__" ? null : key, name: sec?.name || "", sectionListIndex: sec?.list_index ?? 999, sectionType: sec?.section_type || "", isSequential: !!sec?.is_sequential, tasks: [] });
      }
      map.get(key)!.tasks.push(task);
    }
    return [...map.values()].sort((a, b) => a.sectionListIndex - b.sectionListIndex);
  })();

  const done = projectStatus === "closed";

  return (
    <div ref={rowRef} className="border-b last:border-0 overflow-hidden scroll-mt-20">
      <div className="py-2 hover:bg-muted/40 -mx-1 px-1 rounded transition-colors group">
        {/* Row 1: checkbox + chevron + title + type badges + actions */}
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={toggleProjectDone}
            className={`shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${done ? "bg-green-500 border-green-500 hover:bg-green-600" : "border-gray-300 hover:border-green-400"}`}
            title={done ? "Reopen project" : "Mark project complete"}
          >
            {done && <Check className="w-2.5 h-2.5 text-white" />}
          </button>
          <CompleteConfirmDialog
            open={!!confirmComplete}
            onOpenChange={(open) => { if (!open) setConfirmComplete(null); }}
            subject="project"
            name={project.title}
            lines={buildProjectCloseWarning(project).lines}
            onConfirm={() => {
              const coords = confirmComplete;
              setConfirmComplete(null);
              setProjectDone("closed", coords ?? undefined);
            }}
          />
          <button
            type="button"
            className="shrink-0 text-muted-foreground"
            onClick={handleToggle}
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {/* Title — click to open in Projects page (chevron handles in-place expand) */}
          <button
            type="button"
            className="flex items-center gap-1.5 flex-1 text-left min-w-0 group/title"
            onClick={() => {
              sessionStorage.setItem("projectsNav", JSON.stringify({
                accountId: project.account_id,
                accountName: accountName ?? "",
                projectId: project.id,
                focusProjectId: project.id,
              }));
              navigate("/projects");
            }}
          >
            <span className={`text-sm font-medium truncate group-hover/title:text-blue-600 group-hover/title:underline transition-colors ${done ? "line-through text-muted-foreground" : ""}`}>
              {project.title}
            </span>
            {/* Period badge — next to title text */}
            {(project.recurring_pattern === "weekly" || project.recurring_pattern === "bi-weekly") && (project.accounting_period_date || project.due_date) ? (
              <span className="text-xs px-2 py-0 rounded bg-sky-50 text-sky-700 border border-sky-300 font-semibold shrink-0">
                {computePeriodLabel((project.accounting_period_date || project.due_date)!, project.recurring_pattern)}
              </span>
            ) : project.recurring_pattern === "quarterly" && (project.accounting_period_date || project.due_date) ? (
              <span className="text-xs px-2 py-0 rounded bg-sky-50 text-sky-700 border border-sky-300 font-semibold shrink-0">
                {computePeriodLabel((project.accounting_period_date || project.due_date)!, "quarterly")}
              </span>
            ) : (project.tax_year || project.period_label) ? (
              <span className="text-xs px-2 py-0 rounded bg-sky-50 text-sky-700 border border-sky-300 font-semibold shrink-0">
                {yearMonthBadge(project.tax_year, project.period_label)}
              </span>
            ) : null}
          </button>
          {/* Client response indicator — bold amber banner */}
          {!!clientResponseCount && clientResponseCount > 0 && (
            <a
              href={`/accounts/${project.account_id ?? ""}?tab=portal`}
              onClick={e => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500 text-white text-xs font-bold shrink-0 hover:bg-amber-600 transition-colors shadow-sm"
              title={`${clientResponseCount} client portal response${clientResponseCount !== 1 ? "s" : ""} — click to view`}
            >
              <Paperclip className="w-3.5 h-3.5" />
              {clientResponseCount} CLIENT {clientResponseCount === 1 ? "RESPONSE" : "RESPONSES"}
            </a>
          )}

          {/* Assignee avatars — skip entries where user data didn't resolve (deleted/inactive) */}
          {(project.assigned_users ?? []).filter((au) => au.users?.name || au.users?.email).length > 0 && (
            <div className="flex -space-x-1 shrink-0">
              {(project.assigned_users ?? []).filter((au) => au.users?.name || au.users?.email).slice(0, 3).map((au) => (
                <UserAvatar key={au.user_id ?? au.id} name={au.users?.name ?? null} email={au.users?.email ?? null} />
              ))}
              {(project.assigned_users ?? []).filter((au) => au.users?.name || au.users?.email).length > 3 && (
                <span className="text-[10px] text-muted-foreground ml-1.5 self-center">+{(project.assigned_users ?? []).filter((au) => au.users?.name || au.users?.email).length - 3}</span>
              )}
            </div>
          )}
          {/* Type badge */}
          {project.project_type && (
            <span className={`text-xs px-2 py-0 rounded font-semibold border shrink-0 ${projectTypeBadgeClasses[project.project_type] ?? "bg-gray-100 text-gray-700 border-gray-300"}`}>
              {project.project_type}
            </span>
          )}
          {/* Project-level client task shortcuts — visible without expanding */}
          {clientSectionId && (
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); if (!expanded) setExpanded(true); setTimeout(() => openReminderPanel(clientSectionId), 100); }}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded border font-semibold transition-colors shrink-0 ${
                  clientSectionReminderState?.is_active
                    ? "text-amber-700 border-amber-400 bg-amber-50 hover:bg-amber-100"
                    : "text-slate-600 border-slate-300 bg-white hover:border-amber-400 hover:text-amber-700"
                }`}
              >
                <Bell className="w-3.5 h-3.5" />
                Auto-Reminders{" "}
                {clientSectionReminderState?.is_active ? (
                  <span className="text-amber-600 font-bold">
                    ({clientSectionReminderState.schedule_type === "monthly"
                      ? `day ${clientSectionReminderState.monthly_day ?? "?"}`
                      : `every ${clientSectionReminderState.days}d`})
                  </span>
                ) : (
                  <span className="text-red-500 font-bold">(Off)</span>
                )}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setSectionRulesOpen(clientSectionId); }}
                className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors font-semibold shrink-0"
              >
                <Zap className="w-3.5 h-3.5" />
                Automations
              </button>
            </div>
          )}
          {/* Action icons — soft tinted */}
          <div className="flex items-center gap-1 shrink-0">
            <button type="button" title="Messages" onClick={(e) => { e.stopPropagation(); setShowMessages(true); }} className="h-7 w-7 flex items-center justify-center rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors">
              <MessageSquare className="w-4 h-4" />
            </button>
            <TagPicker
              appliedTags={projectTags}
              onAdd={async (tagId) => { await api.addProjectTag(project.id, tagId); setProjectTags(prev => [...prev, { id: tagId } as Tag]); }}
              onRemove={async (tagId) => { await api.removeProjectTag(project.id, tagId); setProjectTags(prev => prev.filter(t => t.id !== tagId)); }}
              align="end"
            />
            <button type="button" title="Mark ready for review" onClick={handleMarkReadyForReview} disabled={markingReady} className="h-7 w-7 flex items-center justify-center rounded-md bg-amber-100 text-amber-600 hover:bg-amber-200 transition-colors disabled:opacity-50">
              <ClipboardCheck className="w-4 h-4" />
            </button>
            <button type="button" title="Edit project" onClick={(e) => { e.stopPropagation(); setEditDialogOpen(true); }} className="h-7 w-7 flex items-center justify-center rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">
              <Pencil className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Focus on this project"
              onClick={(e) => {
                e.stopPropagation();
                sessionStorage.setItem("projectsNav", JSON.stringify({
                  accountId: project.account_id,
                  accountName: accountName ?? "",
                  projectId: project.id,
                  focusProjectId: project.id,
                }));
                navigate("/projects");
              }}
              className="h-7 w-7 flex items-center justify-center rounded-md bg-indigo-100 text-indigo-600 hover:bg-indigo-200 transition-colors"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            {onDelete && (
              <button type="button" title="Delete project" onClick={async (e) => {
                e.stopPropagation();
                if (await askConfirm({
                  title: "Delete this project?",
                  subject: "project",
                  name: project.title,
                  body: "will be deleted.",
                  lines: buildProjectDeleteWarning(project),
                  confirmLabel: "Delete",
                  destructive: true,
                })) onDelete(project.id);
              }} className="h-7 w-7 flex items-center justify-center rounded-md bg-rose-100 text-rose-600 hover:bg-rose-200 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Row 2: tags + recurring + task count + due date */}
        <div className="flex items-center gap-2 mt-1 pl-10 flex-wrap">
          {project.recurring_pattern && (
            <span className="inline-flex items-center gap-0.5 text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-600 text-white capitalize">
              ↺ {project.recurring_pattern}
            </span>
          )}
          {project.recurring_pattern && (project as any).next_spawn_date && (
            <span className="inline-flex items-center gap-1 text-[11px] text-blue-700 font-medium bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
              <span className="text-blue-400">Next:</span>
              {(() => { try { const d = parseISO((project as any).next_spawn_date); return isNaN(d.getTime()) ? (project as any).next_spawn_date : format(d, "MMM d, yyyy"); } catch { return (project as any).next_spawn_date; } })()}
            </span>
          )}
          {projectTags.map(tag => <TagChip key={tag.id} tag={tag} />)}
          <span className="text-[10px] text-muted-foreground ml-auto shrink-0 flex items-center gap-2">
            {workflowPipelines.length > 0 && (
              <WorkflowStatusBadge
                project={project}
                pipelines={workflowPipelines}
                onStatusChange={handleWorkflowStatusChange}
                isPending={statusPending}
              />
            )}
            {fetched && tasks.length > 0 && (
              <span>{tasks.filter(t => t.status === "done" || t.status === "completed").length}/{tasks.length} tasks</span>
            )}
            {project.due_date && (() => {
              const overdue = !done && new Date(project.due_date) < new Date();
              return (
                <span className={`inline-flex items-center gap-1 text-[11px] font-medium rounded px-1.5 py-0.5 border ${overdue ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                  <span className={overdue ? "text-rose-400" : "text-slate-400"}>Due</span>
                  {fmt(project.due_date)}
                </span>
              );
            })()}
          </span>
        </div>
      </div>
      <ProjectMessagesSheet
        open={showMessages}
        onClose={() => setShowMessages(false)}
        projectId={project.id}
        projectTitle={project.title}
      />
      {editDialogOpen && (
        <ProjectEditDialog
          project={project}
          onClose={() => setEditDialogOpen(false)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ["projects", project.account_id] })}
        />
      )}

      {expanded && (
        <div className="pb-2 pl-4 pr-1 overflow-x-hidden">
          {loading && (
            <div className="flex items-center gap-2 py-1 text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading tasks…
            </div>
          )}
          {!loading && tasks.length === 0 && sectionsList.length === 0 && fetched && (
            <p className="text-xs text-muted-foreground py-1">No tasks</p>
          )}
          {sections.map((section, idx) => {
            const doneCount = section.tasks.filter((t: ProjectTask) => t.status === "done" || t.status === "completed").length;
            const totalCount = section.tasks.length;
            const isClientSection = section.sectionType === "client_tasks";
            const outstandingClientTasks = isClientSection
              ? section.tasks.filter((t: ProjectTask) => t.status !== "done" && t.status !== "completed")
              : [];
            const sectionReminder = section.id ? sectionReminders[section.id] : undefined;
            const reminderIsOpen = section.id ? reminderOpen === section.id : false;
            const isSectionCollapsed = section.id ? collapsedSections.has(section.id) : false;
            const toggleSectionCollapse = () => {
              if (!section.id) return;
              setCollapsedSections(prev => {
                const next = new Set(prev);
                if (next.has(section.id!)) next.delete(section.id!); else next.add(section.id!);
                return next;
              });
            };
            return (
              <div key={idx} className="mb-1">
                {section.name && (
                  <div className="flex items-center justify-between py-1 mt-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <button
                        type="button"
                        onClick={toggleSectionCollapse}
                        className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                        title={isSectionCollapsed ? "Expand section" : "Collapse section"}
                      >
                        {isSectionCollapsed
                          ? <ChevronRight className="w-3 h-3" />
                          : <ChevronDown className="w-3 h-3" />}
                      </button>
                      <button
                        type="button"
                        onClick={toggleSectionCollapse}
                        className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors text-left truncate max-w-[180px]"
                        title={section.name}
                      >
                        {section.name}
                      </button>
                      {section.id && (
                        <button
                          type="button"
                          title={section.isSequential ? "Tasks must be done in order — click for any order" : "Tasks may be done in any order — click to require order (notifies the next assignee as each task completes)"}
                          onClick={() => handleToggleSequential(section.id!, section.isSequential)}
                          className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded border font-bold shadow-sm transition-colors shrink-0 ${section.isSequential ? "border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700" : "border-amber-500 bg-amber-400 text-amber-950 hover:bg-amber-300"}`}
                        >
                          <ListOrdered className="w-3.5 h-3.5" />
                          Ordered & Notified? {section.isSequential ? "(Yes)" : "(No)"}
                        </button>
                      )}
                      {section.id && (
                        <button
                          type="button"
                          title={isClientSection ? "Client tasks section — click to convert to firm tasks" : "Firm tasks section — click to mark as client tasks"}
                          onClick={() => handleToggleSectionType(section.id!, section.sectionType)}
                          className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded border font-semibold shadow-sm transition-colors shrink-0 ${isClientSection ? "border-blue-600 bg-blue-600 text-white hover:bg-blue-700" : "border-slate-300 bg-white text-slate-600 hover:border-blue-400 hover:text-blue-600"}`}
                        >
                          <UserCheck className="w-3.5 h-3.5" />
                          For Client? {isClientSection ? "(Yes)" : "(No)"}
                        </button>
                      )}
                      {isClientSection && (
                        lastNotifiedAt ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 border border-green-300 text-green-700 font-medium shrink-0">
                            Sent {(() => { try { const d = parseISO(lastNotifiedAt); return isNaN(d.getTime()) ? lastNotifiedAt : format(d, "MMM d"); } catch { return lastNotifiedAt; } })()}
                          </span>
                        ) : outstandingClientTasks.length > 0 ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 border border-red-300 text-red-700 font-semibold shrink-0">Not Sent</span>
                        ) : null
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                      <span className="text-[10px] text-muted-foreground">{doneCount}/{totalCount} done</span>
                      {isClientSection && outstandingClientTasks.length > 0 && (
                        <button
                          type="button"
                          className="flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-blue-400 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors font-semibold shrink-0"
                          onClick={() => {
                            if (!project.account_id) { toast({ title: "No account linked to this project", variant: "destructive" }); return; }
                            setShowNotifyModal(true);
                          }}
                        >
                          ✉ Send Now
                        </button>
                      )}
                      {isClientSection && section.id && (
                        <button
                          type="button"
                          onClick={() => openReminderPanel(section.id!)}
                          className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded border font-semibold transition-colors shrink-0 ${
                            sectionReminder?.is_active
                              ? "text-amber-700 border-amber-400 bg-amber-50 hover:bg-amber-100"
                              : "text-slate-600 border-slate-300 bg-white hover:border-amber-400 hover:text-amber-700"
                          }`}
                        >
                          <Bell className="w-3.5 h-3.5" />
                          Auto-Reminders{" "}
                          {sectionReminder?.is_active ? (
                            <span className="text-amber-600 font-bold">
                              ({sectionReminder.schedule_type === "monthly"
                                ? `day ${sectionReminder.monthly_day ?? "?"}`
                                : sectionReminder.schedule_type === "section"
                                ? "on section"
                                : `every ${sectionReminder.days}d`})
                            </span>
                          ) : (
                            <span className="text-red-500 font-bold">(Off)</span>
                          )}
                        </button>
                      )}
                      {section.id && (
                        <button
                          type="button"
                          className="flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors font-semibold shrink-0"
                          onClick={() => setSectionRulesOpen(section.id!)}
                        >
                          <Zap className="w-3.5 h-3.5" />
                          Automations
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {/* Per-section reminder config panel */}
                {isClientSection && section.id && reminderIsOpen && (
                  <div className="mb-2 p-3 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-950/30 text-xs space-y-2">
                    <div className="font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-1.5">
                      <Bell className="w-3 h-3" /> Auto-Reminder Config
                    </div>
                    {/* Active toggle */}
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={reminderDraft.is_active}
                        onChange={e => setReminderDraft(d => ({ ...d, is_active: e.target.checked }))}
                        className="rounded"
                      />
                      <span className="text-amber-900 dark:text-amber-100 font-medium">Active</span>
                    </label>
                    {/* Schedule type */}
                    <div>
                      <label className="block text-amber-800 dark:text-amber-200 mb-1">Schedule</label>
                      <div className="flex gap-3 flex-wrap">
                        {(["interval", "monthly", "section"] as const).map(t => (
                          <label key={t} className="flex items-center gap-1 cursor-pointer">
                            <input
                              type="radio"
                              name={`sched-${section.id}`}
                              value={t}
                              checked={reminderDraft.schedule_type === t}
                              onChange={() => setReminderDraft(d => ({ ...d, schedule_type: t }))}
                            />
                            <span className="text-amber-900 dark:text-amber-100">
                              {t === "interval" ? "Every N days" : t === "monthly" ? "Monthly (day of month)" : "When section completes"}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                    {/* Conditional schedule fields */}
                    {reminderDraft.schedule_type === "interval" && (
                      <label className="flex items-center gap-1.5">
                        <span className="text-amber-800 dark:text-amber-200">Every</span>
                        <select
                          value={reminderDraft.days}
                          onChange={e => setReminderDraft(d => ({ ...d, days: Number(e.target.value) }))}
                          className="border border-input rounded px-1 py-0.5 bg-background text-foreground text-xs"
                        >
                          {[1, 2, 3, 5, 7, 10, 14, 21, 30].map(d => (
                            <option key={d} value={d}>{d} days</option>
                          ))}
                        </select>
                      </label>
                    )}
                    {reminderDraft.schedule_type === "monthly" && (
                      <label className="flex items-center gap-1.5">
                        <span className="text-amber-800 dark:text-amber-200">On day</span>
                        <select
                          value={reminderDraft.monthly_day}
                          onChange={e => setReminderDraft(d => ({ ...d, monthly_day: Number(e.target.value) }))}
                          className="border border-input rounded px-1 py-0.5 bg-background text-foreground text-xs"
                        >
                          {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                        <span className="text-amber-800 dark:text-amber-200">of each month</span>
                      </label>
                    )}
                    {reminderDraft.schedule_type === "section" && (
                      <div>
                        <label className="block text-amber-800 dark:text-amber-200 mb-1">Fire when this firm section completes</label>
                        <select
                          value={reminderDraft.trigger_section_id}
                          onChange={e => setReminderDraft(d => ({ ...d, trigger_section_id: e.target.value }))}
                          className="w-full border border-input rounded px-2 py-1 bg-background text-foreground text-xs"
                        >
                          <option value="">— select a section —</option>
                          {sectionsList
                            .filter(s => s.section_type !== "client_tasks" && s.id !== section.id)
                            .map(s => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                      </div>
                    )}
                    {/* Contacts */}
                    <div>
                      <label className="block text-amber-800 dark:text-amber-200 mb-1">Send to (comma-separated emails)</label>
                      <input
                        type="text"
                        value={reminderDraft.contacts}
                        onChange={e => {
                          const value = e.target.value;
                          // Entering contacts implies the reminder should be active — matches the
                          // server's default (is_active=true whenever contacts are present) so a
                          // saved contact list never silently stays Off.
                          const hasContacts = value.split(",").map(s => s.trim()).filter(Boolean).length > 0;
                          setReminderDraft(d => ({ ...d, contacts: value, is_active: hasContacts ? true : d.is_active }));
                        }}
                        placeholder="client@example.com, other@example.com"
                        className="w-full border border-input rounded px-2 py-1 bg-background text-foreground text-xs"
                      />
                    </div>
                    {sectionReminder?.last_sent_at && (
                      <p className="text-amber-700 dark:text-amber-300">Last sent: {(() => { try { const d = parseISO(sectionReminder.last_sent_at); return isNaN(d.getTime()) ? sectionReminder.last_sent_at : format(d, "MMM d, yyyy"); } catch { return sectionReminder.last_sent_at; } })()}</p>
                    )}
                    {(() => {
                      // A reminder on a closed project keeps is_active + a future next_send_at
                      // (the 9 AM pass skips without mutating the row), so naming the date here
                      // promised a send that will never happen. projectStatus is live local
                      // state, so this flips the instant the row's complete/reopen box is ticked.
                      const nextDisplay = reminderNextDisplay(sectionReminder, { status: projectStatus, deleted_at: project.deleted_at });
                      if (nextDisplay.kind === "hidden") return null;
                      if (nextDisplay.kind === "paused") {
                        return <p className="text-amber-700/70 dark:text-amber-300/70 italic">Paused — {nextDisplay.reason}</p>;
                      }
                      return <p className="text-amber-700 dark:text-amber-300">Next: {(() => { try { const d = parseISO(nextDisplay.at); return isNaN(d.getTime()) ? nextDisplay.at : format(d, "MMM d, yyyy"); } catch { return nextDisplay.at; } })()}</p>;
                    })()}
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        disabled={reminderSaving}
                        onClick={() => saveReminder(section.id!)}
                        className="text-[10px] px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 font-medium"
                      >
                        {reminderSaving ? "Saving…" : "Save"}
                      </button>
                      {sectionReminder && (
                        <button
                          type="button"
                          disabled={reminderSending || !reminderDraft.contacts.trim()}
                          onClick={() => sendReminderNow(section.id!)}
                          className="text-[10px] px-2 py-1 rounded border border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-50 font-medium"
                        >
                          {reminderSending ? "Sending…" : "Send Now"}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setReminderOpen(null)}
                        className="text-[10px] px-2 py-1 rounded text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 font-medium"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
                {!isSectionCollapsed && (
                  <div>
                    {section.tasks.map((task: ProjectTask) => (
                      <TaskDetailRow
                        key={task.id}
                        task={task}
                        onStatusChange={handleStatusChange}
                        onUpdate={handleTaskUpdate}
                        onDelete={handleTaskDelete}
                        accountId={project.account_id ?? undefined}
                        autoExpand={openTaskId === task.id}
                        portalResponseCount={(taskPortalMap ?? {})[task.id] ?? 0}
                        sequentialBlockedOn={(() => {
                          // Advisory: in an "in order" section, a task waits on the earliest
                          // incomplete root task above it (tasks are list_index-sorted).
                          if (!section.isSequential) return null;
                          if (task.status === "done" || task.status === "completed" || task.parent_task_id) return null;
                          const earlier = section.tasks.find((t) =>
                            t.id !== task.id && !t.parent_task_id &&
                            (t.list_index ?? 0) < (task.list_index ?? 0) &&
                            t.status !== "done" && t.status !== "completed");
                          return earlier ? earlier.title : null;
                        })()}
                      />
                    ))}
                    {/* Per-section add task row */}
                    {section.id && addingTask && addingTaskSectionId === section.id ? (
                      <div className="flex items-center gap-1.5 py-1 pl-1">
                        <Input
                          autoFocus
                          value={newTaskTitle}
                          onChange={(e) => setNewTaskTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleAddTask(section.id);
                            if (e.key === "Escape") { setAddingTask(false); setAddingTaskSectionId(null); setNewTaskTitle(""); }
                          }}
                          onBlur={() => handleAddTask(section.id)}
                          placeholder={isClientSection ? "New client task…" : "New task title…"}
                          className="h-7 text-xs"
                        />
                      </div>
                    ) : section.id && !done ? (
                      <button
                        type="button"
                        onClick={() => { setAddingTask(true); setAddingTaskSectionId(section.id!); }}
                        className="flex items-center gap-1 text-xs text-muted-foreground/60 hover:text-foreground transition-colors py-0.5 pl-1"
                      >
                        <Plus className="w-3 h-3" /> {isClientSection ? "Add client task" : "Add task"}
                      </button>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
          {showNotifyModal && project.account_id && (
            <SendNotificationModal
              projectId={project.id}
              projectTitle={project.title}
              accountId={project.account_id}
              onClose={() => setShowNotifyModal(false)}
              onSent={(ts) => {
                setLastNotifiedAt(ts);
                // Re-read from the server so the "Not Sent" badge reflects DB truth, not this stale prop
                if (project.account_id) {
                  queryClient.invalidateQueries({ queryKey: ["projects", project.account_id] });
                }
              }}
            />
          )}

          {/* Section Automation Rules Modal */}
          <SectionAutomationsModal
            sectionId={sectionRulesOpen}
            onClose={() => setSectionRulesOpen(null)}
            accountId={project.account_id}
          />
          {/* Global add task (no section) — only show when no section-level add is open */}
          {!addingTaskSectionId && (
            addingTask ? (
              <div className="flex items-center gap-1.5 py-1 pl-1">
                <Input
                  autoFocus
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddTask(null);
                    if (e.key === "Escape") { setAddingTask(false); setNewTaskTitle(""); }
                  }}
                  onBlur={() => handleAddTask(null)}
                  placeholder="New task title…"
                  className="h-7 text-xs"
                />
              </div>
            ) : !done ? (
              <button
                type="button"
                onClick={() => setAddingTask(true)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors py-1 pl-1 mt-0.5"
              >
                <Plus className="w-3 h-3" /> Add task
              </button>
            ) : null
          )}

          {/* Add section row */}
          {addingSection ? (
            <div className="flex items-center gap-1.5 mt-1 pt-1 border-t border-dashed border-border/40">
              <input
                autoFocus
                className="flex-1 text-xs border border-border rounded px-2 py-1 bg-background outline-none focus:ring-1 focus:ring-primary"
                placeholder="Section name…"
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddSection();
                  if (e.key === "Escape") { setAddingSection(false); setNewSectionName(""); setNewSectionIsClient(false); }
                }}
                onBlur={handleAddSection}
              />
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setNewSectionIsClient(v => !v); }}
                title={newSectionIsClient ? "Client tasks section — click to switch to firm" : "Firm tasks section — click to switch to client"}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors shrink-0 ${newSectionIsClient ? "border-blue-400 bg-blue-50 text-blue-700 font-semibold" : "border-slate-200 bg-white text-slate-400 hover:border-blue-300"}`}
              >
                <UserCheck className="w-3 h-3" />
                {newSectionIsClient ? "Client" : "Firm"}
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setNewSectionIsSequential(v => !v); }}
                title={newSectionIsSequential ? "Tasks must be done in order — click for any order" : "Tasks may be done in any order — click to require order"}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors shrink-0 ${newSectionIsSequential ? "border-emerald-400 bg-emerald-50 text-emerald-700 font-semibold" : "border-slate-200 bg-white text-slate-400 hover:border-emerald-300"}`}
              >
                <ListOrdered className="w-3 h-3" />
                {newSectionIsSequential ? "In order" : "Any order"}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { setAddingSection(true); setNewSectionName(""); setNewSectionIsClient(false); setNewSectionIsSequential(false); }}
              className="flex items-center gap-1 mt-1 pt-1 border-t border-dashed border-border/40 w-full text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
            >
              <Plus className="w-3 h-3" /> Add section
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── portal invite dialog ─────────────────────────────────────────────────────

function PortalDialog({ account, onClose }: { account: Account; onClose: () => void }) {
  const [inviteEmail, setInviteEmail] = useState("");
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const { toast } = useToast();

  const { data: invites = [], refetch } = useQuery<PortalInvite[]>({
    queryKey: ["portal-invites", account.id],
    queryFn: () => portalApi.listInvites(account.id),
  });

  const createMutation = useMutation({
    mutationFn: () => portalApi.createInvite(account.id, inviteEmail.trim()),
    onSuccess: (data) => {
      setGeneratedUrl(data.invite_url);
      setInviteEmail("");
      refetch();
      toast({ title: "Invite link generated" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => portalApi.revokeInvite(id),
    onSuccess: () => { refetch(); toast({ title: "Invite revoked" }); },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => portalApi.deleteInvite(id),
    onSuccess: () => { refetch(); toast({ title: "Invite deleted" }); },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const resendMutation = useMutation({
    mutationFn: (email: string) => portalApi.createInvite(account.id, email),
    onSuccess: (data) => {
      setGeneratedUrl(data.invite_url);
      refetch();
      toast({ title: "New invite link generated — copy and send to client" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-4 h-4" />
            Client Portal Invites
          </DialogTitle>
          <DialogDescription>
            {account.account_name} — Generate a sign-in link to give this client portal access.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="client@email.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && inviteEmail.trim() && createMutation.mutate()}
            />
            <Button onClick={() => createMutation.mutate()} disabled={!inviteEmail.trim() || createMutation.isPending}>
              {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate"}
            </Button>
          </div>

          {generatedUrl && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">One-time use link — copy and send to client.</p>
              <div className="flex gap-2">
                <Input readOnly value={generatedUrl} className="text-xs font-mono" />
                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(generatedUrl); toast({ title: "Copied" }); }}>
                  Copy
                </Button>
              </div>
            </div>
          )}

          {invites.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Existing Invites</p>
              {invites.map((invite) => (
                <div key={invite.id} className="flex items-center justify-between gap-2 text-sm py-1">
                  <div className="flex items-center gap-2 min-w-0">
                    {invite.accepted_at && !invite.revoked_at ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                    ) : invite.revoked_at ? (
                      <XCircle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    ) : (
                      <Clock className="w-3.5 h-3.5 text-yellow-500 shrink-0" />
                    )}
                    <span className="truncate">{invite.email}</span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {invite.revoked_at ? "Revoked" : invite.accepted_at ? "Accepted" : "Pending"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {invite.accepted_at && !invite.revoked_at && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => { navigator.clipboard.writeText("https://portal.carpentercpas.com"); toast({ title: "Portal URL copied" }); }}
                        title="Copy portal login URL"
                      >
                        Copy URL
                      </Button>
                    )}
                    {!invite.accepted_at && !invite.revoked_at && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700"
                        onClick={() => resendMutation.mutate(invite.email)}
                        disabled={resendMutation.isPending}
                        title="Generate a new invite link for this email"
                      >
                        Resend
                      </Button>
                    )}
                    {!invite.revoked_at && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-destructive hover:text-destructive"
                        onClick={() => revokeMutation.mutate(invite.id)}
                        disabled={revokeMutation.isPending || deleteMutation.isPending}
                      >
                        Revoke
                      </Button>
                    )}
                    <button
                      className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                      onClick={() => deleteMutation.mutate(invite.id)}
                      disabled={deleteMutation.isPending || revokeMutation.isPending}
                      title="Delete invite"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── portal activity ──────────────────────────────────────────────────────────

interface PortalActivityItem {
  id: string;
  type: "upload" | "comment";
  account_id: string;
  account_name: string;
  project_id: string;
  project_title: string;
  task_id: string;
  task_title: string;
  name: string;
  file_path?: string;
  file_type?: string;
  file_size?: number;
  created_at: string;
  // requires migration 099
  read?: boolean;
  // firm-wide filed stamp, requires migration 116 (set on the Portal Responses page)
  filed?: { at: string; by_user_id: string | null; by_name: string | null; note: string | null } | null;
}

function PortalActivityFeed({ items, loading, accountId, variant = "grouped", emptyText }: { items: PortalActivityItem[]; loading: boolean; accountId: string; /** "compact" = flat upload rows for the Overview grid card (no group headers, no mark-all banner, no Go-to-task). */ variant?: "grouped" | "compact"; emptyText?: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState<string>("");

  // patch optimistically and reconcile on server side
  const patchRead = (keys: Set<string>) => {
    queryClient.setQueryData<PortalActivityItem[]>(["portal-activity", accountId], (prev) =>
      prev?.map((i) => (keys.has(`${i.type}:${i.id}`) ? { ...i, read: true } : i))
    );
  };

  const refetchPortalActivity = () => {
    queryClient.invalidateQueries({ queryKey: ["portal-activity"] });
    queryClient.invalidateQueries({ queryKey: ["portal-activity-accounts"] });
  };

  const markRead = (item: PortalActivityItem) => {
    if (item.read) return;
    patchRead(new Set([`${item.type}:${item.id}`]));
    api.markPortalActivityRead([{ type: item.type, id: item.id, account_id: item.account_id }])
      .then(refetchPortalActivity)
      .catch(() => {
        toast({ title: "Couldn't mark as read", variant: "destructive" });
        refetchPortalActivity();
      });
  };

  const markAllRead = () => {
    patchRead(new Set(items.filter((i) => !i.read).map((i) => `${i.type}:${i.id}`)));
    api.markAccountPortalActivityRead(accountId)
      .then(refetchPortalActivity)
      .catch(() => {
        toast({ title: "Couldn't mark all as read", variant: "destructive" });
        refetchPortalActivity();
      });
  };

  // await the write then navigate
  const goToTask = async (e: React.MouseEvent<HTMLAnchorElement>, item: PortalActivityItem) => {
    if (item.read) return;
    e.preventDefault();
    const href = e.currentTarget.getAttribute("href")!;
    try {
      await api.markPortalActivityRead([{ type: item.type, id: item.id, account_id: item.account_id }]);
    } catch { /* navigate anyway */ }
    window.location.href = href;
  };

  const unreadCount = items.filter((i) => !i.read).length;

  const isItemPreviewable = (item: PortalActivityItem) => {
    const t = (item.file_type ?? "").toLowerCase();
    const n = (item.name ?? "").toLowerCase();
    return item.type === "upload" && (t === "application/pdf" || n.endsWith(".pdf") || t.startsWith("image/") || /\.(jpe?g|png|gif|webp|heic|bmp)$/.test(n));
  };

  const openPreview = async (item: PortalActivityItem) => {
    try {
      const { url } = await fetchWithAuth<{ url: string }>(`/api/project-tasks/${item.task_id}/attachments/${item.id}/download`);
      setPreviewName(item.name); setPreviewUrl(url);
      markRead(item);
    } catch { toast({ title: "Preview failed", variant: "destructive" }); }
  };

  const download = async (item: PortalActivityItem) => {
    try {
      // mode=download: a real save, not the inline URL Preview uses (ENG-109).
      const { url } = await fetchWithAuth<{ url: string }>(`/api/project-tasks/${item.task_id}/attachments/${item.id}/download?mode=download`);
      triggerDownload(url, item.name);
      markRead(item);
    } catch { toast({ title: "Download failed", variant: "destructive" }); }
  };

  const fmtRel = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 2) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days === 1) return "yesterday";
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  // Group items by project, preserving date order within each group
  const grouped = useMemo(() => {
    const map = new Map<string, PortalActivityItem[]>();
    items.forEach(item => {
      const group = map.get(item.project_id) ?? [];
      group.push(item);
      map.set(item.project_id, group);
    });
    // Sort groups by most recent item
    return [...map.entries()].sort(([, a], [, b]) =>
      new Date(b[0].created_at).getTime() - new Date(a[0].created_at).getTime()
    );
  }, [items]);

  if (loading) return <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>;
  if (items.length === 0) return <div className="py-10 text-center text-sm text-muted-foreground">{emptyText ?? "No client portal activity yet."}</div>;

  return (
    <>
      {variant !== "compact" && unreadCount > 0 && (
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-foreground">
            {unreadCount} unread {unreadCount === 1 ? "response" : "responses"}
          </span>
          <button
            onClick={markAllRead}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-muted text-foreground text-xs font-medium hover:bg-muted/80 transition-colors border border-border"
          >
            <Check className="w-3 h-3" />Mark all read
          </button>
        </div>
      )}
      {variant === "compact" ? (
        <div className="divide-y divide-border">
          {items.map((item) => (
            <div key={`${item.type}-${item.id}`} className={`flex items-start gap-2 py-2 ${item.read ? "" : "bg-amber-50/50"}`}>
              <button
                onClick={() => markRead(item)}
                disabled={item.read}
                title={item.read ? "Read" : "Mark as read"}
                aria-label={item.read ? "Read" : "Mark as read"}
                className="mt-1.5 shrink-0 w-2.5 h-2.5 flex items-center justify-center disabled:cursor-default"
              >
                <span className={`block w-2 h-2 rounded-full ${item.read ? "bg-transparent" : "bg-amber-500"}`} />
              </button>
              <Paperclip className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <p className={`text-sm truncate ${item.read ? "font-medium text-foreground/80" : "font-bold text-foreground"}`} title={item.name}>{item.name}</p>
                <p className="text-xs text-muted-foreground truncate" title={`${item.project_title} · ${item.task_title}`}>
                  {item.project_title} · {item.task_title} · {fmtRel(item.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {isItemPreviewable(item) && (
                  <button onClick={() => openPreview(item)} title="Preview" aria-label="Preview" className="inline-flex items-center p-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors border border-blue-200">
                    <Eye className="w-3 h-3" />
                  </button>
                )}
                {item.type === "upload" && (
                  <button onClick={() => download(item)} title="Download" aria-label="Download" className="inline-flex items-center p-1 rounded bg-muted text-foreground hover:bg-muted/80 transition-colors border border-border">
                    <Download className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
      <div className="space-y-3">
        {grouped.map(([projectId, projectItems]) => {
          const first = projectItems[0];
          const groupUnread = projectItems.filter((i) => !i.read).length;
          return (
            <div key={projectId} className="rounded-lg border border-border bg-card shadow-sm overflow-hidden">
              {/* Project group header */}
              <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/50 border-b border-border">
                <span className="text-sm font-bold text-foreground">{first.project_title}</span>
                {(first as any).project_type && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 border border-sky-200 shrink-0">
                    {(first as any).project_type}{(first as any).project_tax_year ? ` · ${(first as any).project_tax_year}` : ""}
                  </span>
                )}
                <span className="ml-auto flex items-center gap-1.5 shrink-0">
                  {groupUnread > 0 && (
                    <span className="text-xs font-semibold bg-amber-400 text-amber-900 rounded-full px-2 py-0.5">{groupUnread} new</span>
                  )}
                  <span className="text-xs font-medium text-muted-foreground">{projectItems.length}</span>
                </span>
              </div>
              {/* Items */}
              <div className="divide-y divide-border">
                {projectItems.map((item) => (
                  <div key={`${item.type}-${item.id}`} className={`flex items-start gap-3 px-4 py-3.5 hover:bg-muted/30 transition-colors ${item.read ? "" : "bg-amber-50/50"}`}>
                    {/* Unread dot — also the click target to mark a single item read */}
                    <button
                      onClick={() => markRead(item)}
                      disabled={item.read}
                      title={item.read ? "Read" : "Mark as read"}
                      aria-label={item.read ? "Read" : "Mark as read"}
                      className="mt-1.5 shrink-0 w-2.5 h-2.5 flex items-center justify-center disabled:cursor-default"
                    >
                      <span className={`block w-2 h-2 rounded-full ${item.read ? "bg-transparent" : "bg-amber-500"}`} />
                    </button>
                    <span className="mt-0.5 shrink-0 text-muted-foreground">
                      {item.type === "upload" ? <Paperclip className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
                    </span>
                    <div className="flex-1 min-w-0 space-y-0.5">
                      {/* Task — line 1, bold */}
                      <p className={`text-sm truncate ${item.read ? "font-semibold text-foreground/80" : "font-bold text-foreground"}`}>{item.task_title}</p>
                      {/* File name or comment — line 2 */}
                      <p className="text-sm font-medium text-foreground/70 leading-snug truncate">{item.name}</p>
                      {item.file_size && <p className="text-xs text-muted-foreground">{(item.file_size / 1024).toFixed(0)} KB</p>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                      {/* v1.5.84 filed chip (migration 116) */}
                      {item.filed && (
                        <span
                          className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200 whitespace-nowrap"
                          title={item.filed.note ? `${new Date(item.filed.at).toLocaleString()} — ${item.filed.note}` : new Date(item.filed.at).toLocaleString()}
                        >
                          <Check className="w-3 h-3" />Filed · {item.filed.by_name ?? "Staff"} · {fmtRel(item.filed.at)}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground w-14 text-right">{fmtRel(item.created_at)}</span>
                      {isItemPreviewable(item) && (
                        <button onClick={() => openPreview(item)} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors border border-blue-200">
                          <Eye className="w-3 h-3" />Preview
                        </button>
                      )}
                      {item.type === "upload" && (
                        <button onClick={() => download(item)} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-muted text-foreground text-xs font-medium hover:bg-muted/80 transition-colors border border-border">
                          <Download className="w-3 h-3" />Download
                        </button>
                      )}
                      <a href={`/accounts/${accountId}?openProject=${item.project_id}&openTask=${item.task_id}`}
                        onClick={(e) => goToTask(e, item)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors">
                        <ExternalLink className="w-3 h-3" />Go to task
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      )}
      {previewUrl && (
        <Dialog open={true} onOpenChange={() => { setPreviewUrl(null); setPreviewName(""); }}>
          <DialogContent className="max-w-4xl w-full" style={{ height: "88vh", display: "flex", flexDirection: "column" }}>
            <DialogHeader><DialogTitle className="truncate text-sm font-semibold">{previewName}</DialogTitle></DialogHeader>
            <div className="flex-1 overflow-hidden rounded">
              {/\.(jpe?g|png|gif|webp|heic|bmp)$/i.test(previewName) ? (
                <div className="w-full h-full flex items-center justify-center bg-muted/30 overflow-auto">
                  <img src={previewUrl} alt={previewName} className="max-w-full max-h-full object-contain" />
                </div>
              ) : (
                <iframe src={previewUrl} title={previewName} className="w-full h-full border-0" />
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

/**
 * v1.5.75 — one-line project row for the Overview grid's Projects card.
 * Title (click → Projects page with this project focused, same as ProjectRow's
 * title click) · period badge · recurring mark · tags, with the due date on the
 * right. Everything else (assignees, reminders, automations, status, actions)
 * stays on the full ProjectRow in the classic layout.
 */
function ProjectCompactRow({ project, onOpen }: { project: Project; onOpen: () => void }) {
  const done = project.status === "closed" || project.status === "completed";
  const overdue = !done && !!project.due_date && new Date(project.due_date) < new Date();
  const tags: Tag[] = project.project_tags ?? [];
  return (
    <div className="flex items-center gap-2 py-1.5 border-b last:border-0 min-w-0">
      <button
        type="button"
        onClick={onOpen}
        className="flex items-center gap-1.5 flex-1 min-w-0 text-left group/compact"
        title={project.title}
      >
        <span className={`text-sm font-medium truncate group-hover/compact:text-primary group-hover/compact:underline transition-colors ${done ? "line-through text-muted-foreground" : ""}`}>
          {project.title}
        </span>
        {project.period_display && (
          <span className="text-[11px] px-1.5 py-0 rounded bg-sky-50 text-sky-700 border border-sky-300 font-semibold shrink-0">
            {project.period_display}
          </span>
        )}
        {project.recurring_pattern && (
          <span className="text-[11px] text-blue-600 shrink-0 capitalize" title={`Recurs ${project.recurring_pattern}`}>↺</span>
        )}
        {tags.map((tag) => <TagChip key={tag.id} tag={tag} />)}
      </button>
      {project.due_date && (
        <span className={`text-[11px] shrink-0 tabular-nums ${overdue ? "text-rose-600 font-semibold" : "text-muted-foreground"}`} title={overdue ? "Overdue" : "Due"}>
          {fmt(project.due_date)}
        </span>
      )}
    </div>
  );
}

// ─── main page ────────────────────────────────────────────────────────────────

function toEasternDateTimeInputs(iso: string): { date: string; time: string } {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const parts = formatter.formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return { date: `${get("year")}-${get("month")}-${get("day")}`, time: `${hour}:${get("minute")}` };
}

function RescheduleMeetingDialog({
  meeting,
  onClose,
  onSave,
  saving,
}: {
  meeting: Meeting;
  onClose: () => void;
  onSave: (patch: { topic: string; scheduled_at: string; duration_minutes: number }) => void;
  saving: boolean;
}) {
  const initial = meeting.scheduled_at ? toEasternDateTimeInputs(meeting.scheduled_at) : { date: "", time: "" };
  const [topic, setTopic] = useState(meeting.topic);
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [duration, setDuration] = useState(meeting.duration_minutes);

  const handleSave = () => {
    if (!date || !time) return;
    const scheduled_at = parseEasternDateTime(date, time);
    onSave({ topic: topic.trim() || meeting.topic, scheduled_at, duration_minutes: duration });
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reschedule Meeting</DialogTitle>
          <DialogDescription>Updates the Zoom meeting and resends the invite to attendees.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Topic</Label>
            <Input value={topic} onChange={(e) => setTopic(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div>
              <Label>Time (ET)</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Duration (minutes)</Label>
            <Input
              type="number"
              min={5}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value) || meeting.duration_minutes)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !date || !time}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AccountDetail() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const searchStr = useSearch();
  const openTaskId = useMemo(() => new URLSearchParams(searchStr).get("openTask"), [searchStr]);
  const openProjectId = useMemo(() => new URLSearchParams(searchStr).get("openProject"), [searchStr]);
  // Chat deep-link params (?tab= / ?chatProject= / ?topic= from chat notification emails + the bell)
  const tabParam = useMemo(() => new URLSearchParams(searchStr).get("tab"), [searchStr]);
  const chatProjectParam = useMemo(() => new URLSearchParams(searchStr).get("chatProject"), [searchStr]);
  const topicParam = useMemo(() => new URLSearchParams(searchStr).get("topic"), [searchStr]);
  // ?email=<graph message id> — deep link from an "email assigned to you" notification email
  // (rides with ?tab=shared-inbox). Opens, scrolls to and highlights that one message.
  const emailParam = useMemo(() => new URLSearchParams(searchStr).get("email"), [searchStr]);
  const [activeTab, setActiveTab] = useState(tabParam ?? "overview");
  const { toast } = useToast();
  const askConfirm = useConfirm();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Role-based access control
  const isStaffOrManager = user?.role === "staff" || user?.role === "manager" || user?.role === "admin";
  const isContractor = user?.role === "contractor";
  const [showPortalDialog, setShowPortalDialog] = useState(false);
  const [catchMeUpOpen, setCatchMeUpOpen] = useState(false);

  // ── Contact edit state ──
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [editContactForm, setEditContactForm] = useState<{
    name: string; email: string; phone: string; phone_label: string; mobile: string; mobile_label: string; address: string; notes: string; sms_ok: boolean; is_primary: boolean;
  }>({ name: "", email: "", phone: "", phone_label: "", mobile: "", mobile_label: "", address: "", notes: "", sms_ok: false, is_primary: false });

  const updateContactMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.updateContact(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts", id] });
      setEditingContactId(null);
    },
    onError: () => toast({ title: "Failed to update contact", variant: "destructive" }),
  });

  // ── SMS send modal state ──
  const [smsSendContact, setSmsSendContact] = useState<Contact | null>(null);
  const [smsDraftBody, setSmsDraftBody] = useState("");
  const [smsConfirming, setSmsConfirming] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  // ── SMS inline reply bar ──
  const [smsReplyBody, setSmsReplyBody] = useState("");
  const [smsReplySending, setSmsReplySending] = useState(false);
  const [smsReplyContactId, setSmsReplyContactId] = useState<string>("");

  const SMS_PRESETS = [
    "Reminder: your appointment is scheduled for [date/time]. Please reply to confirm.",
    "We'll be sending a verification request to your bank shortly. Please text us back the code you receive.",
    "Here is the Zoom link for our meeting: [paste link]",
    "Quick question — please text us back when you have a moment.",
  ];

  // ── Add-form state ──
  const [showAddContact, setShowAddContact] = useState(false);
  const [showAllContacts, setShowAllContacts] = useState(false);
  const [newContactName, setNewContactName] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [newContactPhone, setNewContactPhone] = useState("");
  const [newContactAddress, setNewContactAddress] = useState("");
  const [editingAbout, setEditingAbout] = useState(false);
  const [aboutForm, setAboutForm] = useState<Record<string, string>>({});
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [showAddRelationship, setShowAddRelationship] = useState(false);
  const [newRelOtherAccountId, setNewRelOtherAccountId] = useState("");
  const [newRelLabel, setNewRelLabel] = useState("");

  // ── Services state ──
  const [showAddService, setShowAddService] = useState(false);
  const [newSvcName, setNewSvcName] = useState("");
  const [newSvcUrl, setNewSvcUrl] = useState("");
  const [newSvcCategory, setNewSvcCategory] = useState("other");
  const [newSvcDesc, setNewSvcDesc] = useState("");
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
  const [editSvcForm, setEditSvcForm] = useState({ name: "", url: "", category: "other", description: "" });

  const [showAddResource, setShowAddResource] = useState(false);
  const [newResLabel, setNewResLabel] = useState("");
  const [newResUrl, setNewResUrl] = useState("");
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [showFutureProjects, setShowFutureProjects] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useUserPref(PREF_ACCOUNT_RIGHT_COLLAPSED, false);
  const [overviewView, setOverviewView] = useAccountOverviewView();
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [collapsedTaskProjects, setCollapsedTaskProjects] = useState<Set<string>>(new Set());
  const [taskSortOrder, setTaskSortOrder] = useState<"asc" | "desc">("asc");
  const [overviewOpen, setOverviewOpen] = useState<Record<string, boolean>>({
    notes: true, activity: false, sms: false, sources: false, vault: false,
  });
  const toggleOverviewSection = (key: string) =>
    setOverviewOpen(prev => ({ ...prev, [key]: !prev[key] }));

  const [showAddAssignee, setShowAddAssignee] = useState(false);
  const [newAssigneeUserId, setNewAssigneeUserId] = useState("");

  // ── Messages tab state ──
  const [selectedMsgProjectId, setSelectedMsgProjectId] = useState<string | null>(chatProjectParam);
  const [teamChatSearch, setTeamChatSearch] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [tcFileQueue, setTcFileQueue] = useState<File[]>([]);
  const [tcUploading, setTcUploading] = useState(false);
  const [tcMentionQuery, setTcMentionQuery] = useState<string | null>(null);
  const [tcMentionIndex, setTcMentionIndex] = useState(0);
  const [tcExpandedAttachments, setTcExpandedAttachments] = useState<Record<string, boolean>>({});
  const tcFileInputRef = useRef<HTMLInputElement>(null);
  const tcTextareaRef = useRef<HTMLTextAreaElement>(null);
  // Client Chat state
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(topicParam);
  const [clientChatBody, setClientChatBody] = useState("");
  const [clientFileQueue, setClientFileQueue] = useState<File[]>([]);
  const [clientUploading, setClientUploading] = useState(false);
  const [clientMsgAttachments, setClientMsgAttachments] = useState<Record<string, ChatAttachment[] | null>>({});
  const [clientAttExpanded, setClientAttExpanded] = useState<Record<string, boolean>>({});
  const [editingClientMsgId, setEditingClientMsgId] = useState<string | null>(null);
  const [editingClientMsgBody, setEditingClientMsgBody] = useState("");
  const clientFileInputRef = useRef<HTMLInputElement>(null);
  const [newTopicName, setNewTopicName] = useState("");

  // Chat notification deep-links: react to param changes (covers same-page bell clicks).
  // No ?tab= means the Overview tab — task/"Your turn" deep-links land there as before.
  useEffect(() => {
    setActiveTab(tabParam ?? "overview");
  }, [tabParam]);
  useEffect(() => {
    if (chatProjectParam) setSelectedMsgProjectId(chatProjectParam);
  }, [chatProjectParam]);
  useEffect(() => {
    if (topicParam) setSelectedTopicId(topicParam);
  }, [topicParam]);
  const [showNewTopicInput, setShowNewTopicInput] = useState(false);

  // Note dialog state
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteDialogNote, setNoteDialogNote] = useState<ClientNote | null>(null); // null = create mode
  const [noteDialogTitle, setNoteDialogTitle] = useState("");
  const [noteDialogBody, setNoteDialogBody] = useState("");
  const [noteDialogPinned, setNoteDialogPinned] = useState(false);
  const [noteDialogSaving, setNoteDialogSaving] = useState(false);

  const openNoteDialog = (note?: ClientNote) => {
    setNoteDialogNote(note ?? null);
    setNoteDialogTitle(note?.title ?? "");
    setNoteDialogBody(note?.plain_text ?? "");
    setNoteDialogPinned(note?.is_pinned ?? false);
    setNoteDialogOpen(true);
  };

  const saveNote = async () => {
    if (!id) return;
    setNoteDialogSaving(true);
    try {
      const payload = { title: noteDialogTitle.trim() || undefined, plain_text: noteDialogBody.trim() || undefined, is_pinned: noteDialogPinned };
      if (noteDialogNote) {
        await api.updateAccountNote(id, noteDialogNote.id, payload);
      } else {
        await api.createAccountNote(id, payload);
      }
      queryClient.invalidateQueries({ queryKey: ["account-notes", id] });
      setNoteDialogOpen(false);
      toast({ title: noteDialogNote ? "Note updated" : "Note added" });
    } catch {
      toast({ title: "Failed to save note", variant: "destructive" });
    } finally {
      setNoteDialogSaving(false);
    }
  };

  const deleteNote = async (note: ClientNote) => {
    if (!id) return;
    if (!(await askConfirm({
      title: "Delete this note?",
      subject: "note",
      body: "will be deleted.",
      confirmLabel: "Delete",
      destructive: true,
    }))) return;
    try {
      await api.deleteAccountNote(id, note.id);
      queryClient.invalidateQueries({ queryKey: ["account-notes", id] });
      toast({ title: "Note deleted" });
    } catch {
      toast({ title: "Failed to delete note", variant: "destructive" });
    }
  };

  const togglePinNote = async (note: ClientNote) => {
    if (!id) return;
    try {
      await api.updateAccountNote(id, note.id, { is_pinned: !note.is_pinned });
      queryClient.invalidateQueries({ queryKey: ["account-notes", id] });
    } catch {
      toast({ title: "Failed to update note", variant: "destructive" });
    }
  };

  // Email surfaces (unification): shared EmailRow/dialogs — see components/custom/email/.
  const [claimDialogEmail, setClaimDialogEmail] = useState<UnifiedEmail | null>(null);
  const [forwardDialogEmail, setForwardDialogEmail] = useState<UnifiedEmail | null>(null);
  const [replyEmail, setReplyEmail] = useState<{ email: ClientEmail; replyAll?: boolean } | null>(null);
  const [sharedInboxPill, setSharedInboxPill] = useState<EmailPill>("all");
  const [emailSearch, setEmailSearch] = useState("");
  const [emailDateFrom, setEmailDateFrom] = useState("");
  const [emailDateTo, setEmailDateTo] = useState("");
  const [emailShowCount, setEmailShowCount] = useState(25);
  const [filteredEmailsExpanded, setFilteredEmailsExpanded] = useState(false);
  const [emailSort, setEmailSort] = useState<"date-desc" | "date-asc" | "sender" | "pinned">("date-desc");


  // ── Queries ──
  const { data: account, isLoading: accountLoading } = useQuery<Account>({
    queryKey: ["account", id],
    queryFn: () => api.getAccount(id!),
    enabled: !!id,
  });

  const canChangeActiveStatus = user?.role === "manager" || user?.role === "admin";
  // Any staff may maintain account assignees/workflow roles (server enforces the same);
  // contractors get read-only — mirrors the v1.5.59 contractor gate on the endpoints.
  const canEditAssignees = user?.role !== "contractor";
  const [pendingInactive, setPendingInactive] = useState<InactiveTarget | null>(null);
  const reactivateMutation = useMutation({
    mutationFn: () => api.updateAccount(id!, { is_active: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account", id] });
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      toast({ title: "Client reactivated" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message || "Failed to update client", variant: "destructive" });
    },
  });

  // Prefetch documents as soon as the account loads so the Documents tab is instant
  useEffect(() => {
    if (!account?.id) return;
    queryClient.prefetchQuery({
      queryKey: ["client-documents", account.id, "all"],
      queryFn: () => import("@/lib/api").then((m) => m.getClientDocuments(account.id, "all")),
      staleTime: 30_000,
    });
  }, [account?.id, queryClient]);

  const { data: notes = [], isLoading: notesLoading } = useQuery<ClientNote[]>({
    queryKey: ["account-notes", id],
    queryFn: () => api.getAccountNotes(id!),
    enabled: !!id,
  });

  const { data: rawContacts = [] } = useQuery<Contact[]>({
    queryKey: ["contacts", id],
    queryFn: () => api.getContacts({ accountId: id }),
    enabled: !!id,
  });
  const contacts = [...rawContacts].sort((a, b) => {
    if (a.is_primary && !b.is_primary) return -1;
    if (!a.is_primary && b.is_primary) return 1;
    return (a.name || "").localeCompare(b.name || "");
  });

  const [composeOpen, setComposeOpen] = useState(false);
  const { data: sendableMailboxes = [] } = useQuery({
    queryKey: ["sendable-mailboxes"],
    queryFn: () => emailApi.getSendableMailboxes(),
    staleTime: 5 * 60 * 1000,
  });
  const accountMappedMailbox = sendableMailboxes.find((m) => m.account_id === id)?.mailbox_address;

  const { data: smsLog = [], refetch: refetchSmsLog } = useQuery<SmsLogEntry[]>({
    queryKey: ["sms-log", id],
    queryFn: () => api.getSmsLog(id!),
    enabled: !!id && isStaffOrManager,
    refetchInterval: 15000,
  });

  const { data: vaultRecords = [] } = useQuery({
    queryKey: ["vault", id],
    queryFn: () => getVaultRecords(id!),
    enabled: !!id && !isContractor,
  });

  const { data: financialSources = [] } = useQuery({
    queryKey: ["financial-sources", id],
    queryFn: () => getFinancialSources(id!),
    enabled: !!id,
  });

  const { data: meetings = [] } = useQuery<Meeting[]>({
    queryKey: ["meetings", id],
    queryFn: () => getMeetings(id!),
    enabled: !!id,
  });

  const [showScheduleMeeting, setShowScheduleMeeting] = useState(false);
  const [startingMeetingId, setStartingMeetingId] = useState<string | null>(null);
  const [rescheduleMeeting, setRescheduleMeeting] = useState<Meeting | null>(null);

  // E-Signatures
  const { data: signatureRequests = [] } = useQuery<SignatureRequest[]>({
    queryKey: ["account-signature-requests", id],
    queryFn: () => getAccountSignatureRequests(id!),
    enabled: !!id,
  });
  const { data: agreementTemplates = [] } = useQuery<AgreementTemplate[]>({
    queryKey: ["agreement-templates"],
    queryFn: () => getAgreementTemplates(),
    enabled: !!id,
  });
  const [showSendSignature, setShowSendSignature] = useState(false);
  const remindSignatureMutation = useMutation({
    mutationFn: (requestId: string) => sendSignatureReminder(requestId),
    onSuccess: () => toast({ title: "Reminder sent" }),
    onError: () => toast({ title: "Failed to send reminder", variant: "destructive" }),
  });
  const voidSignatureMutation = useMutation({
    mutationFn: (requestId: string) => voidSignatureRequest(requestId),
    onSuccess: () => {
      toast({ title: "Request voided" });
      queryClient.invalidateQueries({ queryKey: ["account-signature-requests", id] });
    },
    onError: () => toast({ title: "Failed to void", variant: "destructive" }),
  });
  const syncSignatureMutation = useMutation({
    mutationFn: (requestId: string) => syncSignatureRequest(requestId),
    onSuccess: () => {
      toast({ title: "Status synced" });
      queryClient.invalidateQueries({ queryKey: ["account-signature-requests", id] });
    },
    onError: () => toast({ title: "Sync failed", variant: "destructive" }),
  });

  const cancelMeetingMutation = useMutation({
    mutationFn: (meetingId: string) => {
      const attendeeEmails = contacts.map((c) => c.email).filter((e): e is string => !!e);
      return cancelMeetingFull(meetingId, attendeeEmails);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["meetings", id] }),
    onError: () => toast({ title: "Failed to cancel meeting", variant: "destructive" }),
  });

  const rescheduleMeetingMutation = useMutation({
    mutationFn: (patch: { meetingId: string; topic: string; scheduled_at: string; duration_minutes: number }) => {
      const attendeeEmails = contacts.map((c) => c.email).filter((e): e is string => !!e);
      return updateMeetingFull(patch.meetingId, {
        topic: patch.topic,
        scheduled_at: patch.scheduled_at,
        duration_minutes: patch.duration_minutes,
        attendee_emails: attendeeEmails,
      });
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["meetings", id] });
      setRescheduleMeeting(null);
      const problems: string[] = Array.isArray(data?.problems) ? data.problems : [];
      if (problems.length) {
        toast({ title: "Meeting rescheduled with issues", description: problems.join("; "), variant: "destructive" });
      } else {
        toast({ title: "Meeting rescheduled" });
      }
    },
    onError: () => toast({ title: "Failed to reschedule meeting", variant: "destructive" }),
  });

  const handleStartMeeting = async (meeting: Meeting) => {
    if (!meeting.zoom_meeting_id) {
      if (meeting.start_url) window.open(meeting.start_url, "_blank");
      return;
    }
    setStartingMeetingId(meeting.id);
    try {
      const startUrl = await getMeetingFreshStartUrl(meeting.id);
      window.open(startUrl, "_blank");
    } catch {
      if (meeting.start_url) window.open(meeting.start_url, "_blank");
      toast({ title: "Could not get fresh start link — using saved link instead" });
    } finally {
      setStartingMeetingId(null);
    }
  };

  const patchSmsOkMutation = useMutation({
    mutationFn: ({ contactId, sms_ok }: { contactId: string; sms_ok: boolean }) =>
      api.patchContactSmsOk(contactId, sms_ok),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["contacts", id] }),
    onError: () => toast({ title: "Failed to update SMS opt-in", variant: "destructive" }),
  });

  const { data: projects = [], isLoading: projectsLoading } = useQuery<Project[]>({
    queryKey: ["projects", id],
    queryFn: () => api.getProjects({ accountId: id, includeAll: true }),
    enabled: !!id,
  });

  const deepLinkWidenedRef = useRef<string | null>(null);
  // switching clients goes back to "Open Projects"
  useEffect(() => {
    setShowAllProjects(false);
    setShowFutureProjects(false);
    deepLinkWidenedRef.current = null;
  }, [id]);

  useEffect(() => {
    if (!openProjectId) return;
    const key = `${id}:${openProjectId}`;
    if (deepLinkWidenedRef.current === key) return;
    const target = projects.find((p) => p.id === openProjectId);
    if (!target) return; // projects not loaded yet, retry when they arrive
    deepLinkWidenedRef.current = key;
    const reveal = deepLinkReveal(target);
    if (reveal === "all") setShowAllProjects(true);
    else if (reveal === "future") setShowFutureProjects(true);
  }, [openProjectId, projects, id]);

  const { data: relationships = [] } = useQuery<AccountRelationship[]>({
    queryKey: ["account-relationships", id],
    queryFn: () => api.getAccountRelationships(id!),
    enabled: !!id,
  });

  const { data: services = [] } = useQuery<AccountService[]>({
    queryKey: ["account-services", id],
    queryFn: () => getAccountServices(id!),
    enabled: !!id,
  });

  const { data: resources = [] } = useQuery<AccountResource[]>({
    queryKey: ["account-resources", id],
    queryFn: () => api.getAccountResources(id!),
    enabled: !!id,
  });

  const { data: assignees = [] } = useQuery<AccountAssignee[]>({
    queryKey: ["account-assignees", id],
    queryFn: () => api.getAccountAssignees(id!),
    enabled: !!id,
  });

  const { data: recentActivities = [] } = useQuery<AccountActivity[]>({
    queryKey: ["account-activities", id],
    queryFn: () => api.getAccountRecentActivities(id!),
    enabled: !!id,
  });

  const { data: accountTasks = [] } = useQuery<ProjectTask[]>({
    queryKey: ["account-tasks", id, showCompletedTasks],
    queryFn: () => api.getAccountTasks(id!, showCompletedTasks),
    enabled: !!id,
  });

  const { data: allAccounts = [] } = useQuery<Account[]>({
    queryKey: ["accounts"],
    queryFn: () => api.getAccounts(true),
    enabled: showAddRelationship,
  });

  const { data: allUsers = [] } = useQuery<ApiUser[]>({
    queryKey: ["users"],
    queryFn: () => api.getUsers(),
    enabled: showAddAssignee,
  });

  // ── Mutations ──
  const deleteProjectMutation = useMutation({
    mutationFn: (projectId: string) => api.deleteProject(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", id] });
      toast({ title: "Project deleted" });
    },
    onError: (err: any) => toast({ title: "Error deleting project", description: err.message, variant: "destructive" }),
  });

  const createContactMutation = useMutation({
    mutationFn: () => api.createContact({
      account_id: id!,
      name: newContactName.trim(),
      email: newContactEmail.trim() || undefined,
      phone: newContactPhone.trim() || undefined,
      address: newContactAddress.trim() || undefined,
    } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts", id] });
      setShowAddContact(false);
      setNewContactName(""); setNewContactEmail(""); setNewContactPhone(""); setNewContactAddress("");
      toast({ title: "Contact added" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const updateAboutMutation = useMutation({
    mutationFn: () => api.updateAccountAbout(id!, aboutForm),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account", id] });
      setEditingAbout(false);
      toast({ title: "Account updated" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const saveNameMutation = useMutation({
    mutationFn: (name: string) => api.updateAccountAbout(id!, { account_name: name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account", id] });
      setIsEditingName(false);
      toast({ title: "Client name updated" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const createRelMutation = useMutation({
    mutationFn: () => api.createAccountRelationship(id!, { otherAccountId: newRelOtherAccountId, label: newRelLabel }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-relationships", id] });
      setShowAddRelationship(false);
      setNewRelOtherAccountId("");
      setNewRelLabel("");
      toast({ title: "Relationship added" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteRelMutation = useMutation({
    mutationFn: (relId: string) => api.deleteAccountRelationship(id!, relId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-relationships", id] });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const createServiceMutation = useMutation({
    mutationFn: () => {
      if (newSvcUrl && !/^https?:\/\//i.test(newSvcUrl.trim())) {
        throw new Error("URL must start with http:// or https://");
      }
      return createAccountService(id!, {
        name: newSvcName.trim(),
        url: newSvcUrl.trim() || undefined,
        category: newSvcCategory,
        description: newSvcDesc.trim() || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-services", id] });
      setShowAddService(false);
      setNewSvcName(""); setNewSvcUrl(""); setNewSvcCategory("other"); setNewSvcDesc("");
      toast({ title: "Service added" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const updateServiceMutation = useMutation({
    mutationFn: (serviceId: string) => {
      if (editSvcForm.url && !/^https?:\/\//i.test(editSvcForm.url.trim())) {
        throw new Error("URL must start with http:// or https://");
      }
      return updateAccountService(id!, serviceId, {
        name: editSvcForm.name.trim(),
        url: editSvcForm.url.trim() || null,
        category: editSvcForm.category,
        description: editSvcForm.description.trim() || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-services", id] });
      setEditingServiceId(null);
      toast({ title: "Service updated" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteServiceMutation = useMutation({
    mutationFn: (serviceId: string) => deleteAccountService(id!, serviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-services", id] });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const createResMutation = useMutation({
    mutationFn: () => api.createAccountResource(id!, { label: newResLabel, url: newResUrl }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-resources", id] });
      setShowAddResource(false);
      setNewResLabel("");
      setNewResUrl("");
      toast({ title: "Resource added" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteResMutation = useMutation({
    mutationFn: (resId: string) => api.deleteAccountResource(id!, resId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-resources", id] });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const addAssigneeMutation = useMutation({
    mutationFn: () => api.addAccountAssignee(id!, { userId: newAssigneeUserId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-assignees", id] });
      queryClient.invalidateQueries({ queryKey: ["account-members-bulk"] });
      setNewAssigneeUserId("");
      toast({ title: "Staff assigned" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const removeAssigneeMutation = useMutation({
    mutationFn: (userId: string) => api.removeAccountAssignee(id!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-assignees", id] });
      queryClient.invalidateQueries({ queryKey: ["account-members-bulk"] });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const workflowRoleMutation = useMutation({
    mutationFn: ({ userId, workflowRole }: { userId: string; workflowRole: string | null }) =>
      api.setAssigneeWorkflowRole(id!, userId, workflowRole),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account-assignees", id] });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  // ── SmartVault provisioning (admin only) ─────────────────────────────────
  const [svPanelOpen, setSvPanelOpen] = useState(false);
  const provisionSvMutation = useMutation({
    mutationFn: () => provisionSmartVaultClient(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account", id] });
      toast({ title: "SmartVault provisioned", description: "Client vault created successfully." });
    },
    onError: (err: any) => toast({ title: "Provisioning failed", description: err.message, variant: "destructive" }),
  });

  // ── Project messages query (lazy — only runs when a project is selected) ──
  const { data: projectMessages = [], isLoading: messagesLoading } = useQuery<ProjectMessage[]>({
    queryKey: ["project-messages", selectedMsgProjectId],
    queryFn: () => api.getProjectMessages(selectedMsgProjectId!),
    enabled: !!selectedMsgProjectId,
    refetchInterval: 30_000,
  });

  const likeTeamMessageMutation = useMutation({
    mutationFn: (messageId: string) =>
      fetchWithAuth<{ liked: boolean; count: number; likers: string[] }>(`/api/project-messages/${messageId}/like`, { method: "POST" }),
    onSuccess: (res, messageId) => {
      queryClient.setQueryData<ProjectMessage[]>(["project-messages", selectedMsgProjectId], (prev) =>
        prev?.map((msg) => (msg.id === messageId ? { ...msg, likedByMe: res.liked, likeCount: res.count, likers: res.likers } : msg))
      );
    },
    onError: (err: any) => toast({ title: "Failed to like message", description: err.message, variant: "destructive" }),
  });

  const editTeamMessageMutation = useMutation({
    mutationFn: ({ messageId, body }: { messageId: string; body: string }) =>
      api.updateProjectMessage(messageId, body),
    // patch in place
    onSuccess: (updated) => {
      queryClient.setQueryData<ProjectMessage[]>(["project-messages", selectedMsgProjectId], (prev) =>
        prev?.map((msg) => (msg.id === updated.id ? { ...msg, body: updated.body, edited_at: updated.edited_at } : msg))
      );
    },
    onError: (err: any) => toast({ title: "Failed to edit message", description: err.message, variant: "destructive" }),
  });

  const { data: emailData, isLoading: emailsLoading, refetch: refetchEmails, error: emailError } = useQuery<{
    emails: ClientEmail[];
    filtered?: ClientEmail[];
    sharedUnread?: number;
    makershubForwardConfigured?: boolean;
    warning?: string;
  }>({
    queryKey: ["account-emails", id],
    queryFn: () => emailApi.getAccountEmails(id!),
    // Auto-load when either email tab is open (was fetch-on-Refresh-click only) — a
    // ?tab=shared-inbox deep-link no longer lands on an empty "click Refresh" state,
    // and the tab's unread badge can populate. 2-min staleTime bounds the cost.
    enabled: !!id && (activeTab === "emails" || activeTab === "shared-inbox"),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // ── ?email= deep link (assignment notification) ──
  // Two jobs once the email list arrives: make sure the target row is actually rendered
  // (pills/search/pagination can all hide it), then scroll to it and flash a highlight.
  const handledEmailParamRef = useRef<string | null>(null);
  useEffect(() => {
    if (!emailParam || !emailData) return;
    if (handledEmailParamRef.current === emailParam) return;
    handledEmailParamRef.current = emailParam;

    const shared = (emailData.emails ?? []).filter((e) => e.isSharedMailbox);
    // Widen anything that could filter the target out of the list.
    setSharedInboxPill("all");
    setEmailSearch("");
    setEmailDateFrom("");
    setEmailDateTo("");
    setEmailShowCount((c) => Math.max(c, shared.length, (emailData.emails ?? []).length));
    // Claimed emails are noise-exempt server-side, but if the target only exists in the
    // filtered bucket (assignment not yet visible to the noise partition), open it.
    if (
      !(emailData.emails ?? []).some((e) => e.id === emailParam) &&
      (emailData.filtered ?? []).some((e) => e.id === emailParam)
    ) {
      setFilteredEmailsExpanded(true);
    }

    // Same 300ms settle as the ?openProject deep link — the list re-renders after the
    // state changes above, so the anchor may not exist on this tick.
    const timer = setTimeout(() => {
      const el = document.getElementById(`email-row-${emailParam}`);
      if (!el) return; // aged out of the server's recent window — the tab itself is still right
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-emerald-400");
      setTimeout(() => el.classList.remove("ring-2", "ring-emerald-400"), 4000);
    }, 300);
    return () => clearTimeout(timer);
  }, [emailParam, emailData]);

  const { data: emailTasksRaw = [], refetch: refetchEmailTasks } = useQuery<any[]>({
    queryKey: ["email-tasks", id],
    queryFn: () => fetchWithAuth(`/api/email-tasks?accountId=${id}`),
    enabled: !!id,
    staleTime: 60_000,
  });
  // Map by graph_message_id for O(1) row lookup
  const emailTaskMap = useMemo(() =>
    new Map(emailTasksRaw.map((t: any) => [t.graph_message_id, t])),
    [emailTasksRaw]
  );

  const { data: automationLogRaw = [] } = useQuery<EmailAutomationEntry[]>({
    queryKey: ["email-automation-log", id],
    queryFn: () => getEmailAutomationLog(id!),
    enabled: !!id,
    staleTime: 2 * 60_000,
  });
  // Set of message IDs that automation has touched — O(1) lookup
  const automatedMessageIds = useMemo(() =>
    new Set(automationLogRaw.map((e) => e.graph_message_id)),
    [automationLogRaw]
  );
  const automationDetailMap = useMemo(() =>
    new Map(automationLogRaw.map((e) => [e.graph_message_id, e])),
    [automationLogRaw]
  );

  // Pinned-emails map: graph_message_id → { project_id, project_title }
  const { data: pinnedEmailsRaw = [] } = useQuery<any[]>({
    queryKey: ["pinned-emails-map", id],
    queryFn: () => fetchWithAuth(`/api/accounts/${id}/pinned-emails-map`),
    enabled: !!id,
    staleTime: 60_000,
  });
  const pinnedEmailsMap = useMemo(() =>
    new Map(pinnedEmailsRaw.map((r: any) => [r.graph_message_id, r])),
    [pinnedEmailsRaw]
  );

  const { data: staffUsers = [] } = useQuery<any[]>({
    queryKey: ["users"],
    queryFn: () => api.getUsers(),
    staleTime: 5 * 60_000,
  });

  // Assignable roster for email pickers ONLY (excludes portal clients; contractors assignable since Sept 2 2026) —
  // the unfiltered staffUsers query above still feeds team-chat @mentions. Shares the
  // /inbox page's cache key.
  const { data: assignableUsers = [] } = useQuery({
    queryKey: ["users", "assignable"],
    queryFn: () => api.getUsers({ assignable: true }),
    staleTime: 5 * 60_000,
  });

  // ENG-96: rows the Shared Inbox tab has optimistically patched stay listed until the
  // pill or the account changes (epoch), so opening an email under Unread cannot make it
  // vanish mid-click.
  const sharedInboxSticky = useStickyPill(`${sharedInboxPill}|${id ?? "*"}`);

  // Shared email machinery (unification) — same hook /inbox and the pop-out use;
  // optimistic patches land on BOTH the ["inbox"] and ["account-emails", id] caches.
  const emailActions = useEmailActions(
    { surface: "account", accountId: id ?? "" },
    { onTouch: sharedInboxSticky.mark },
  );

  const { data: categoryNamesData } = useQuery({
    queryKey: ["email-category-names"],
    queryFn: () => emailApi.getCategoryNames(),
    staleTime: Infinity,
  });
  const knownCategories = useMemo(() => {
    const names = new Set<string>(categoryNamesData?.names ?? []);
    for (const e of emailData?.emails ?? []) for (const c of e.categories ?? []) names.add(c);
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [categoryNamesData, emailData]);

  // ClientEmail → UnifiedEmail with the account page's LIVE overlay maps layered on top
  // (they receive optimistic updates; the server-embedded overlay is the fallback).
  const toUnifiedEmail = (e: ClientEmail): UnifiedEmail =>
    fromClientEmail(e, id ?? "", {
      task: emailTaskMap.get(e.id),
      pin: pinnedEmailsMap.get(e.id),
    });

  // ── Shared Inbox tab list ──
  // Hoisted out of the tab's JSX so the bulk multi-select can read the visible row ids in
  // render order (a shift-click range must be exactly what is on screen). The JSX renders
  // straight from this.
  //
  // ⚠️ The sticky pill predicate below is invoked exactly ONCE in this whole file, and the
  // ENG-96 ratchet asserts that — the Emails tab has no pill and both Filtered buckets render
  // unfiltered. Selection pruning is a pure id-set intersection and must never be expressed
  // as a second pill gate.
  // "Load older" for the Shared Inbox tab (v1.5.66). Until now this tab could only ever
  // show the newest 100 archived rows per mailbox while its unread badge counted the WHOLE
  // archive — so unread mail older than the window was counted and could never be opened.
  // Live at the time of writing, Hostetler had 3 of its 4 unread emails unreachable here,
  // the oldest 64 days old, which is exactly the mail its red My Accounts bubble points at.
  const olderPages = useLoadOlder({
    epochKey: id ?? "*",
    logTag: "[account-emails]",
    onError: () => toast({ title: "Failed to load older emails", variant: "destructive" }),
  });

  // One paging target per mapped mailbox. An account can map several (mappedMailboxes is
  // an array server-side), and this tab renders them as one flat list rather than per-mailbox
  // groups, so a single button fans out across all of them with independent cursors.
  const sharedInboxTargets = useMemo(() => {
    const byMailbox = new Map<string, ClientEmail[]>();
    for (const e of emailData?.emails ?? []) {
      if (!e.isSharedMailbox) continue;
      const list = byMailbox.get(e.mailboxAddress);
      if (list) list.push(e);
      else byMailbox.set(e.mailboxAddress, [e]);
    }
    return [...byMailbox.entries()].map(([mailbox, baseRows]) => ({ mailbox, baseRows }));
  }, [emailData]);

  const loadOlderSharedInbox = async () => {
    const { added } = await olderPages.loadOlder(sharedInboxTargets);
    // Widening the render cap is not cosmetic: under the default date-desc sort every newly
    // fetched row lands BELOW the current cap, so without this the button fetches a page and
    // nothing visibly happens.
    if (added > 0) setEmailShowCount((c) => c + added);
  };

  const sharedInbox = useMemo(() => {
    const serverRows = (emailData?.emails ?? []).filter((e) => e.isSharedMailbox);
    // Older pages are merged here, in the memo — NEVER written into the ["account-emails"]
    // cache, which invalidateEmailSurfaces refetches on every claim/pin/flag/read. The id
    // dedupe covers the window shift: new mail arriving between the first fetch and the
    // paging click can put a row in both lists.
    const olderRows = Object.entries(olderPages.rowsByMailbox).flatMap(([mailbox, msgs]) =>
      msgs.map((m) => clientEmailFromInboxMessage(m, { mailbox })),
    );
    const seenIds = new Set(serverRows.map((e) => e.id));
    const all = [...serverRows, ...olderRows.filter((e) => !seenIds.has(e.id))];
    const filtered = all.filter((e) => {
      // Shared pill predicate (parity with /inbox — same lib/email/filters).
      if (sharedInboxPill !== "all" && !matchesPillSticky(toUnifiedEmail(e), sharedInboxPill, user?.id ?? "", sharedInboxSticky.ids)) return false;
      const q = emailSearch.toLowerCase();
      if (q && !e.subject?.toLowerCase().includes(q) && !e.from?.emailAddress?.name?.toLowerCase().includes(q) && !e.from?.emailAddress?.address?.toLowerCase().includes(q)) return false;
      if (emailDateFrom && new Date(e.receivedDateTime) < new Date(emailDateFrom)) return false;
      if (emailDateTo && new Date(e.receivedDateTime) > new Date(emailDateTo + "T23:59:59")) return false;
      return true;
    });
    const sorted = [...filtered].sort((a, b) => {
      if (emailSort === "date-asc") return new Date(a.receivedDateTime).getTime() - new Date(b.receivedDateTime).getTime();
      if (emailSort === "sender") return (a.from?.emailAddress?.name || a.from?.emailAddress?.address || "").localeCompare(b.from?.emailAddress?.name || b.from?.emailAddress?.address || "");
      // "Pinned first" now actually works (was a dead option — no comparator branch).
      if (emailSort === "pinned")
        return (pinnedEmailsMap.has(b.id) ? 1 : 0) - (pinnedEmailsMap.has(a.id) ? 1 : 0) ||
          new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime();
      return new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime();
    });
    const visible = sorted.slice(0, emailShowCount);
    // Cross-mailbox dupe stubs (ENG-93) render as a one-line jump, not an email row, so they
    // carry no checkbox and must not join the selectable order.
    const selectable = visible.filter((e) => !e.hidden_by_cross_mailbox_dupe);
    return {
      all,
      sorted,
      visible,
      visibleIds: selectable.map((e) => e.id),
      unifiedById: new Map(selectable.map((e) => [e.id, toUnifiedEmail(e)])),
    };
    // sharedInboxSticky.ids is ref-backed — .token is the sound dependency (see useStickyPill).
    // emailTaskMap/pinnedEmailsMap/id are the overlays toUnifiedEmail closes over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    emailData, sharedInboxPill, emailSearch, emailDateFrom, emailDateTo, emailSort, emailShowCount,
    sharedInboxSticky.token, emailTaskMap, pinnedEmailsMap, user?.id, id,
    // olderPages.rowsByMailbox is replaced wholesale on every page, but .token is the
    // declared dependency for the same reason sharedInboxSticky.token is.
    olderPages.rowsByMailbox, olderPages.token,
  ]);

  // Bulk multi-select for the Shared Inbox tab only. Epoch = pill + sort + account; search
  // and the date range narrow the same list, and `visibleSelected` prunes whatever they hide.
  const sharedInboxSelection = useEmailSelection(
    sharedInbox.visibleIds,
    `${sharedInboxPill}|${emailSort}|${id ?? "*"}`,
  );

  const runSharedInboxBulk = async (action: BulkEmailAction) => {
    const emails = sharedInboxSelection.visibleSelected
      .map((mid) => sharedInbox.unifiedById.get(mid))
      .filter((e): e is UnifiedEmail => !!e);
    if (emails.length === 0) return;
    const { failed } = await emailActions.bulkApply(emails, action);
    // Leave the selection intact on a partial failure so the user can retry the same rows.
    if (failed === 0) sharedInboxSelection.clear();
  };

  const tcMentionMatches = tcMentionQuery !== null
    ? staffUsers.filter((u: any) =>
        u.name?.toLowerCase().startsWith(tcMentionQuery.toLowerCase()) ||
        (u.name?.toLowerCase().split(" ") ?? []).some((w: string) => w.startsWith(tcMentionQuery.toLowerCase()))
      ).slice(0, 6)
    : [];

  useEffect(() => { setTcMentionIndex(0); }, [tcMentionQuery]);

  const tcInsertMention = useCallback((user: any) => {
    const ta = tcTextareaRef.current;
    if (!ta) return;
    const cursor = ta.selectionStart ?? messageBody.length;
    const before = messageBody.slice(0, cursor);
    const after = messageBody.slice(cursor);
    const handle = (user.name ?? "Unknown").replace(/\s+/g, "");
    const replaced = before.replace(/@\w*$/, `@${handle} `);
    setMessageBody(replaced + after);
    setTcMentionQuery(null);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(replaced.length, replaced.length);
    });
  }, [messageBody]);

  const tcHandleBodyChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setMessageBody(val);
    const cursor = e.target.selectionStart ?? val.length;
    const match = val.slice(0, cursor).match(/@(\w*)$/);
    setTcMentionQuery(match ? match[1] : null);
  }, []);

  const tcHandlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const images = Array.from(e.clipboardData.items).filter((i) => i.type.startsWith("image/"));
    if (images.length === 0) return;
    e.preventDefault();
    setTcFileQueue((q) => [...q, ...(images.map((i) => i.getAsFile()).filter(Boolean) as File[])]);
  }, []);

  const tcHandleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) setTcFileQueue((q) => [...q, ...files]);
    e.target.value = "";
  }, []);

  const deleteTeamChatAttachmentMutation = useMutation({
    mutationFn: (attachmentId: string) => deleteMessageAttachment(attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["msg-attachments"] });
      toast({ title: "Attachment deleted" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const postMessageMutation = useMutation({
    mutationFn: () => api.postProjectMessage(selectedMsgProjectId!, messageBody.trim(), false),
    onSuccess: async (newMsg: ProjectMessage) => {
      if (tcFileQueue.length > 0) {
        setTcUploading(true);
        for (const file of tcFileQueue) {
          try { await uploadMessageAttachment(newMsg.id, file); } catch {}
        }
        setTcUploading(false);
        setTcFileQueue([]);
      }
      setMessageBody("");
      setTcMentionQuery(null);
      queryClient.invalidateQueries({ queryKey: ["project-messages", selectedMsgProjectId] });
      toast({ title: "Message sent" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  // ── Client Chat queries ──
  const { data: clientTopics = [], isLoading: topicsLoading, refetch: refetchTopics } = useQuery<ClientTopic[]>({
    queryKey: ["client-topics", id],
    queryFn: () => api.getClientTopics(id!),
    enabled: !!id,
  });

  const { data: topicMessages = [], isLoading: topicMessagesLoading } = useQuery<ClientTopicMessage[]>({
    queryKey: ["client-topic-messages", selectedTopicId],
    queryFn: () => api.getClientTopicMessages(selectedTopicId!),
    enabled: !!selectedTopicId,
    refetchInterval: 30_000,
  });

  const likeClientTopicMessageMutation = useMutation({
    mutationFn: (messageId: string) =>
      fetchWithAuth<{ liked: boolean; count: number; likers: string[] }>(`/api/client-topics/${selectedTopicId}/messages/${messageId}/like`, { method: "POST" }),
    onSuccess: (res, messageId) => {
      queryClient.setQueryData<ClientTopicMessage[]>(["client-topic-messages", selectedTopicId], (prev) =>
        prev?.map((msg) => (msg.id === messageId ? { ...msg, likedByMe: res.liked, likeCount: res.count, likers: res.likers } : msg))
      );
    },
    onError: (err: any) => toast({ title: "Failed to like message", description: err.message, variant: "destructive" }),
  });

  const editClientTopicMessageMutation = useMutation({
    mutationFn: ({ messageId, body }: { messageId: string; body: string }) =>
      api.updateClientTopicMessage(selectedTopicId!, messageId, body),
    // patch in place
    onSuccess: (updated) => {
      setEditingClientMsgId(null);
      queryClient.setQueryData<ClientTopicMessage[]>(["client-topic-messages", selectedTopicId], (prev) =>
        prev?.map((msg) => (msg.id === updated.id ? { ...msg, body: updated.body, edited_at: updated.edited_at } : msg))
      );
    },
    onError: (err: any) => toast({ title: "Failed to edit message", description: err.message, variant: "destructive" }),
  });

  const { data: portalActivity = [], isLoading: portalActivityLoading } = useQuery<PortalActivityItem[]>({
    queryKey: ["portal-activity", id],
    queryFn: () => fetchWithAuth<PortalActivityItem[]>(`/api/accounts/${id}/portal-activity`),
    enabled: !!id,
    staleTime: 30_000,
  });

  // Project/task row badges count UNREAD only (matches Projects.tsx and the Portal tab badge),
  // so they clear as responses get handled rather than sticking at the all-time total.
  const projectPortalActivityCount = useMemo(() =>
    portalActivity.reduce<Record<string, number>>((acc, item) => {
      if (item.read) return acc;
      acc[item.project_id] = (acc[item.project_id] ?? 0) + 1;
      return acc;
    }, {}),
    [portalActivity]
  );

  // v1.5.75 — grid view "Recent Documents" card: portal uploads only, newest first.
  const recentUploads = useMemo(() => recentPortalUploads(portalActivity), [portalActivity]);

  const taskPortalActivityCount = useMemo(() =>
    portalActivity.reduce<Record<string, number>>((acc, item) => {
      if (item.read || !item.task_id) return acc;
      acc[item.task_id] = (acc[item.task_id] ?? 0) + 1;
      return acc;
    }, {}),
    [portalActivity]
  );

  // requires migration 099
  const unreadPortalActivityCount = useMemo(
    () => portalActivity.filter((item) => !item.read).length,
    [portalActivity]
  );

  const createTopicMutation = useMutation({
    mutationFn: (name: string) => api.createClientTopic(id!, name),
    onSuccess: (topic) => {
      setNewTopicName("");
      setShowNewTopicInput(false);
      queryClient.invalidateQueries({ queryKey: ["client-topics", id] });
      setSelectedTopicId(topic.id);
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const postClientMsgMutation = useMutation({
    mutationFn: () => api.postClientTopicMessage(selectedTopicId!, clientChatBody.trim()),
    onSuccess: async (newMsg: ClientTopicMessage) => {
      if (clientFileQueue.length > 0) {
        setClientUploading(true);
        for (const file of clientFileQueue) {
          try { await uploadClientTopicMessageAttachment(newMsg.id, file); } catch {}
        }
        setClientUploading(false);
        setClientFileQueue([]);
      }
      setClientChatBody("");
      queryClient.invalidateQueries({ queryKey: ["client-topic-messages", selectedTopicId] });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const handleClientFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) setClientFileQueue((q) => [...q, ...files]);
    e.target.value = "";
  }, []);

  const handleClientPaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData.items);
    const imageItems = items.filter((i) => i.type.startsWith("image/"));
    if (imageItems.length === 0) return;
    e.preventDefault();
    const files = imageItems.map((i) => i.getAsFile()).filter(Boolean) as File[];
    setClientFileQueue((q) => [...q, ...files]);
  }, []);

  const fetchClientMsgAttachments = useCallback(async (messageId: string) => {
    if (clientMsgAttachments[messageId] !== undefined) return;
    const data = await getClientTopicMessageAttachments(messageId);
    setClientMsgAttachments((prev) => ({ ...prev, [messageId]: data }));
  }, [clientMsgAttachments]);

  if (accountLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!account) {
    return (
      <Layout>
        <div className="py-24 text-center text-muted-foreground">
          <Building2 className="w-12 h-12 mx-auto mb-4 opacity-40" />
          <p className="text-lg font-medium">Client not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate("/accounts")}>
            Back to Clients
          </Button>
        </div>
      </Layout>
    );
  }

  const openProjectsRaw = projects.filter((p) => p.status !== "closed" && p.status !== "completed" && !p.deleted_at);
  const closedProjects = projects.filter((p) => (p.status === "closed" || p.status === "completed") && !p.deleted_at);
  const sortByDue = (a: Project, b: Project) => {
    if (!a.due_date && !b.due_date) return 0;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return new Date(b.due_date).getTime() - new Date(a.due_date).getTime();
  };
  const currentProjects = openProjectsRaw.filter(p => !isFutureProject(p)).sort(sortByDue);
  const futureProjects = openProjectsRaw.filter(p => isFutureProject(p)).sort(sortByDue);
  const openProjects = openProjectsRaw.sort(sortByDue);
  const pinnedNotes = notes.filter((n) => n.is_pinned);
  const unpinnedNotes = notes.filter((n) => !n.is_pinned);
  const hasAddress = account.address_1 || account.city || account.state || account.zip;
  const hasResources = account.smartvault_url || resources.length > 0;
  const assignedUserIds = new Set<string>(assignees.map((a) => a.userId));
  const availableUsers = allUsers.filter((u: ApiUser) => !assignedUserIds.has(u.id));

  // Shared row renderer for the "Emails" tab list — reused by both the normal list and the
  // collapsible "Filtered" (noise-filter) bucket so Preview/Reply/Pin/etc. behave identically.
  // One shared row for BOTH email tabs (unification): Emails tab = contact-matched
  // (via/Sent badges), Shared Inbox tab = mapped-mailbox rows with the prominent
  // Claim pill. All actions flow through emailActions so /inbox stays in sync.
  // ENG-93: a personal-mailbox copy whose content already lives in the client's Shared Inbox
  // (transport-rule BCC, same internet_message_id) renders as a single compact muted line
  // instead of a second full email — no unread dot, just a jump to the real copy.
  // `sel` is passed ONLY by the Shared Inbox tab's visible list — the Emails tab and both
  // Filtered (noise) buckets call this bare, so they render no checkbox.
  const renderEmailRow = (
    email: ClientEmail,
    sel?: { selected: boolean; active: boolean; onToggle: (shiftKey: boolean) => void },
  ) =>
    email.hidden_by_cross_mailbox_dupe ? (
      <div
        key={email.id}
        id={`email-row-${email.id}`}
        className="flex items-center gap-3 px-3 py-2 text-sm text-foreground border border-emerald-300 border-l-4 border-l-emerald-600 rounded-md bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800"
      >
        <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
          Shared Inbox
        </span>
        <span className="truncate flex-1">
          <span className="font-medium">{email.subject || "(no subject)"}</span>
          {" — "}
          {email.from?.emailAddress?.name || email.from?.emailAddress?.address || "unknown sender"} ·{" "}
          {new Date(email.receivedDateTime).toLocaleDateString()}
        </span>
        <button
          type="button"
          className="shrink-0 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium px-3 py-1"
          onClick={() => setActiveTab("shared-inbox")}
        >
          Open in Shared Inbox
        </button>
      </div>
    ) : (
    <div key={email.id} id={`email-row-${email.id}`} className="border rounded-md overflow-hidden">
      <EmailRow
        email={toUnifiedEmail(email)}
        variant={email.isSharedMailbox ? "account-shared" : "account-contact"}
        currentUserId={user?.id ?? ""}
        users={assignableUsers}
        makershubForwardConfigured={!!emailData?.makershubForwardConfigured}
        knownCategories={knownCategories}
        actions={emailActions}
        onReply={(u, opts) => setReplyEmail({ email: toReplyEmail(u), replyAll: opts?.replyAll })}
        onForward={setForwardDialogEmail}
        onOpenClaimDialog={setClaimDialogEmail}
        automationEntry={automationDetailMap.get(email.id) ?? null}
        prominentClaim={email.isSharedMailbox === true}
        initiallyExpanded={email.id === emailParam}
        canSend={!isContractor}
        selectable={!!sel}
        selected={sel?.selected}
        selectionActive={sel?.active}
        onToggleSelect={sel?.onToggle}
      />
    </div>
    );

  // ── v1.5.75 — Overview bodies shared by the Classic and Grid layouts ──
  // Called as functions (never rendered as inner components) so ProjectRow /
  // NoteCard keep their identity across parent renders — the renderEmailRow
  // pattern above. `capped` = the classic viewport-height scroll box; the grid
  // OverviewCard supplies its own cap, so it must not be double-wrapped.
  // The list both Projects renderers show: classic = ProjectRows, grid = compact rows.
  const visibleProjects: Project[] = showAllProjects
    ? [...openProjects, ...closedProjects]
    : [...currentProjects, ...(showFutureProjects ? futureProjects : [])];

  const renderProjectsBody = ({ capped }: { capped: boolean }) => (
    <>
                      {projectsLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                        </div>
                      ) : openProjects.length === 0 && !showAllProjects ? (
                        <div className="py-8 text-center text-sm text-muted-foreground">
                          <Briefcase className="w-7 h-7 mx-auto mb-2 opacity-30" />
                          No open projects
                          {closedProjects.length > 0 && (
                            <button className="block mx-auto mt-2 text-xs text-primary underline" onClick={() => setShowAllProjects(true)}>
                              Show {closedProjects.length} closed / completed
                            </button>
                          )}
                        </div>
                      ) : (
                        <div>
                          <div className={capped ? "max-h-[calc(100vh-320px)] overflow-y-auto overflow-x-hidden" : "overflow-x-hidden"}>
                            {visibleProjects.map((p) => (
                              <ProjectRow key={p.id} project={p} onDelete={(pid) => deleteProjectMutation.mutate(pid)} accountName={account?.account_name ?? ""} openTaskId={openTaskId} openProjectId={openProjectId} clientResponseCount={projectPortalActivityCount[p.id] ?? 0} taskPortalMap={taskPortalActivityCount} accountContacts={contacts} />
                            ))}
                          </div>
                          {!showAllProjects && closedProjects.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-3 pt-2 border-t">
                              ... {closedProjects.length} Others Closed/Completed
                            </p>
                          )}
                        </div>
                      )}
    </>
  );

  // Grid Projects card: one compact line per project. Opening a row goes to the
  // Projects page with that project focused (the classic title-click behaviour).
  const renderProjectsCompact = () => (
    projectsLoading ? (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    ) : visibleProjects.length === 0 ? (
      <div className="py-6 text-center text-sm text-muted-foreground">
        <Briefcase className="w-7 h-7 mx-auto mb-2 opacity-30" />
        {showAllProjects ? "No projects" : "No open projects"}
      </div>
    ) : (
      <div>
        {visibleProjects.map((p) => (
          <ProjectCompactRow
            key={p.id}
            project={p}
            onOpen={() => {
              sessionStorage.setItem("projectsNav", JSON.stringify({
                accountId: p.account_id,
                accountName: account?.account_name ?? "",
                projectId: p.id,
                focusProjectId: p.id,
              }));
              navigate("/projects");
            }}
          />
        ))}
        {!showAllProjects && closedProjects.length > 0 && (
          <p className="text-xs text-muted-foreground mt-2 pt-2 border-t">
            ... {closedProjects.length} Others Closed/Completed
          </p>
        )}
      </div>
    )
  );

  // Classic caps the list at 5 with a "+N more" line; the grid card shows every note.
  const renderNotesBody = (limit?: number) => {
    const orderedNotes = [...pinnedNotes, ...unpinnedNotes];
    const shownNotes = limit ? orderedNotes.slice(0, limit) : orderedNotes;
    return (
      <>
                        {notesLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                          </div>
                        ) : notes.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-2">No notes yet.</p>
                        ) : (
                          <div className="space-y-2">
                            {shownNotes.map((n) => (
                              <NoteCard key={n.id} note={n} onEdit={openNoteDialog} onDelete={deleteNote} onTogglePin={togglePinNote} />
                            ))}
                            {limit != null && notes.length > limit && (
                              <p className="text-xs text-muted-foreground pt-1">+{notes.length - limit} more</p>
                            )}
                          </div>
                        )}
      </>
    );
  };

  const contactsAddButton = (
                          <Button variant="ghost" size="sm" className="h-6 text-xs text-primary" onClick={() => setShowAddContact(v => !v)}>
                            <Plus className="w-3 h-3 mr-1" />Add
                          </Button>
  );

  // Grid view lists every contact (always open); classic keeps its Primary-only default.
  const contactsBody = (
    <>
                        {showAddContact && (
                          <div className="mb-3 space-y-1.5 p-2 rounded-md border bg-muted/30">
                            <input className="w-full h-7 px-2 text-xs border rounded bg-background" placeholder="Name *" value={newContactName} onChange={e => setNewContactName(e.target.value)} />
                            <input className="w-full h-7 px-2 text-xs border rounded bg-background" placeholder="Email" value={newContactEmail} onChange={e => setNewContactEmail(e.target.value)} />
                            <input className="w-full h-7 px-2 text-xs border rounded bg-background" placeholder="Phone" value={newContactPhone} onChange={e => setNewContactPhone(e.target.value)} />
                            <input className="w-full h-7 px-2 text-xs border rounded bg-background" placeholder="Address" value={newContactAddress} onChange={e => setNewContactAddress(e.target.value)} />
                            <div className="flex gap-1.5">
                              <Button size="sm" className="h-6 text-xs flex-1" disabled={!newContactName.trim() || createContactMutation.isPending} onClick={() => createContactMutation.mutate()}>Save</Button>
                              <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setShowAddContact(false)}>Cancel</Button>
                            </div>
                          </div>
                        )}
                        {contacts.length === 0 && !showAddContact ? (
                          <p className="text-sm text-muted-foreground">No contacts on file</p>
                        ) : (
                          <div className="space-y-3">
                            {((overviewView === "grid" || showAllContacts) ? contacts : contacts.slice(0, 1)).map((c) => (
                              <div key={c.id} className="space-y-0.5">
                                {editingContactId === c.id ? (
                                  <div className="rounded-lg border bg-muted/20 p-2.5 space-y-1.5">
                                    <Input placeholder="Name *" className="h-7 text-xs" value={editContactForm.name} onChange={(e) => setEditContactForm((f) => ({ ...f, name: e.target.value }))} />
                                    <Input placeholder="Email" className="h-7 text-xs" value={editContactForm.email} onChange={(e) => setEditContactForm((f) => ({ ...f, email: e.target.value }))} />
                                    <div className="flex gap-1">
                                      <Input placeholder="Phone" className="h-7 text-xs flex-1" value={editContactForm.phone} onChange={(e) => setEditContactForm((f) => ({ ...f, phone: e.target.value }))} />
                                      <Input placeholder="Label (e.g. Direct)" className="h-7 text-xs w-28" value={editContactForm.phone_label} onChange={(e) => setEditContactForm((f) => ({ ...f, phone_label: e.target.value }))} />
                                    </div>
                                    <div className="flex gap-1">
                                      <Input placeholder="Mobile" className="h-7 text-xs flex-1" value={editContactForm.mobile} onChange={(e) => setEditContactForm((f) => ({ ...f, mobile: e.target.value }))} />
                                      <Input placeholder="Label (e.g. Work Cell)" className="h-7 text-xs w-28" value={editContactForm.mobile_label} onChange={(e) => setEditContactForm((f) => ({ ...f, mobile_label: e.target.value }))} />
                                    </div>
                                    <Input placeholder="Address" className="h-7 text-xs" value={editContactForm.address} onChange={(e) => setEditContactForm((f) => ({ ...f, address: e.target.value }))} />
                                    <Input placeholder="Notes" className="h-7 text-xs" value={editContactForm.notes} onChange={(e) => setEditContactForm((f) => ({ ...f, notes: e.target.value }))} />
                                    <label className="flex items-center gap-1.5 cursor-pointer select-none pt-0.5">
                                      <input type="checkbox" className="w-3 h-3 accent-primary" checked={!!editContactForm.sms_ok} onChange={(e) => setEditContactForm((f) => ({ ...f, sms_ok: e.target.checked }))} />
                                      <span className="text-xs text-muted-foreground">SMS opt-in</span>
                                    </label>
                                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                      <input type="checkbox" className="w-3 h-3 accent-primary" checked={!!editContactForm.is_primary} onChange={(e) => setEditContactForm((f) => ({ ...f, is_primary: e.target.checked }))} />
                                      <span className="text-xs text-muted-foreground">Primary contact for this account</span>
                                    </label>
                                    <div className="flex gap-1.5 pt-0.5">
                                      <Button size="sm" className="h-6 text-xs px-2" disabled={!editContactForm.name.trim() || updateContactMutation.isPending}
                                        onClick={() => updateContactMutation.mutate({ id: c.id, data: { name: editContactForm.name.trim(), email: editContactForm.email.trim() || undefined, phone: editContactForm.phone.trim() || undefined, phone_label: editContactForm.phone_label.trim() || undefined, mobile: editContactForm.mobile.trim() || undefined, mobile_label: editContactForm.mobile_label.trim() || undefined, address: editContactForm.address.trim() || undefined, notes: editContactForm.notes.trim() || undefined, sms_ok: editContactForm.sms_ok, is_primary: editContactForm.is_primary } })}>
                                        {updateContactMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                                      </Button>
                                      <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={() => setEditingContactId(null)}>Cancel</Button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <p className="text-sm font-medium flex items-center gap-1.5">
                                      <User className="w-3.5 h-3.5 text-muted-foreground" />
                                      {c.name}
                                      {c.is_primary && <span className="text-xs text-muted-foreground font-normal">(Primary)</span>}
                                      <CopyBtn value={c.name} />
                                      {isStaffOrManager && (
                                        <button className="text-muted-foreground hover:text-foreground p-0.5 rounded hover:bg-muted" onClick={() => { setEditingContactId(c.id); setEditContactForm({ name: c.name, email: c.email ?? "", phone: c.phone ?? "", phone_label: c.phone_label ?? "", mobile: c.mobile ?? "", mobile_label: c.mobile_label ?? "", address: c.address ?? "", notes: c.notes ?? "", sms_ok: !!c.sms_ok, is_primary: !!c.is_primary }); }} title="Edit contact">
                                          <Pencil className="w-3 h-3" />
                                        </button>
                                      )}
                                    </p>
                                    {c.email && (
                                      <div className="flex items-center gap-1 pl-5">
                                        <a href={`mailto:${c.email}`} className="text-xs text-primary hover:underline flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</a>
                                        <CopyBtn value={c.email} />
                                      </div>
                                    )}
                                    {c.phone && (
                                      <div className="text-xs text-muted-foreground flex items-center gap-1 pl-5">
                                        <span className="text-muted-foreground/60 shrink-0">{c.phone_label || "Phone"}:</span>{c.phone}<CopyBtn value={c.phone} />
                                      </div>
                                    )}
                                    {c.mobile && (
                                      <div className="text-xs text-muted-foreground flex items-center gap-1 pl-5">
                                        <span className="text-muted-foreground/60 shrink-0">{c.mobile_label || "Mobile"}:</span>{c.mobile}<CopyBtn value={c.mobile} />
                                        {isStaffOrManager && (
                                          <>
                                            <label className="flex items-center gap-1 ml-1 cursor-pointer select-none">
                                              <input type="checkbox" className="w-3 h-3 accent-primary" checked={!!c.sms_ok} onChange={(e) => patchSmsOkMutation.mutate({ contactId: c.id, sms_ok: e.target.checked })} />
                                              <span className="text-xs text-muted-foreground">SMS</span>
                                            </label>
                                            {c.sms_ok && (
                                              <button className="ml-1 flex items-center gap-0.5 text-xs text-primary hover:underline" onClick={() => { setSmsSendContact(c); setSmsDraftBody(""); setSmsConfirming(false); }} title="Send SMS">
                                                <MessageSquareText className="w-3 h-3" />Send
                                              </button>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    )}
                                    {c.address && (
                                      <div className="text-xs text-muted-foreground flex items-center gap-1 pl-5">
                                        <MapPin className="w-3 h-3 shrink-0" /><span>{c.address}</span><CopyBtn value={c.address} />
                                      </div>
                                    )}
                                    {c.notes && (
                                      <div className="text-xs text-muted-foreground flex items-start gap-1 pl-5">
                                        <FileText className="w-3 h-3 shrink-0 mt-0.5" /><span className="whitespace-pre-wrap break-words">{c.notes}</span><CopyBtn value={c.notes} />
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
    </>
  );


  return (
    <Layout>
      <div className="space-y-4">

        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/accounts")} className="mt-0.5 shrink-0">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                {isEditingName ? (
                  <div className="flex items-center gap-1">
                    <Input
                      autoFocus
                      className="h-8 text-xl font-heading font-bold w-72"
                      value={nameInput}
                      onChange={e => setNameInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter" && nameInput.trim()) saveNameMutation.mutate(nameInput.trim());
                        if (e.key === "Escape") setIsEditingName(false);
                      }}
                    />
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-green-600" onClick={() => nameInput.trim() && saveNameMutation.mutate(nameInput.trim())} disabled={saveNameMutation.isPending}>
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={() => setIsEditingName(false)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <h1 className="text-2xl font-heading font-bold">{account.account_name}</h1>
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground/50 hover:text-muted-foreground" onClick={() => { setNameInput(account.account_name ?? ""); setIsEditingName(true); }}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <CopyBtn value={account.account_name ?? ""} />
                  </>
                )}
                {canChangeActiveStatus ? (
                  <button
                    className={`text-xs px-2 py-0.5 rounded font-medium transition-colors ${account.is_active ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                    title={account.is_active ? "Make this client inactive" : "Reactivate this client"}
                    onClick={() => {
                      if (account.is_active) {
                        setPendingInactive({ id: account.id, account_name: account.account_name ?? "" });
                      } else {
                        reactivateMutation.mutate();
                      }
                    }}
                  >
                    {account.is_active ? "Active" : "Inactive"}
                  </button>
                ) : (
                  // staff/constractor see status cannot change it
                  // this is enforced on server anyways
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${account.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                    {account.is_active ? "Active" : "Inactive"}
                  </span>
                )}
                <Button variant="outline" size="sm" className="h-6 text-xs px-2 border-violet-200 text-violet-700 hover:bg-violet-50" onClick={() => setCatchMeUpOpen(true)}>
                  <Sparkles className="w-3 h-3 mr-1" />
                  Catch me up
                </Button>
                <Button variant="outline" size="sm" className="h-6 text-xs px-2" onClick={() => setShowPortalDialog(true)}>
                  <Link2 className="w-3 h-3 mr-1" />
                  Portal
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 text-xs px-2"
                  title="View the client portal exactly as this client sees it (read-only)"
                  onClick={() => {
                    localStorage.setItem("portal_preview", "true");
                    localStorage.setItem("portal_preview_return", account.id);
                    localStorage.setItem("portal_account_id", account.id);
                    window.location.href = "/portal";
                  }}
                >
                  <Eye className="w-3 h-3 mr-1" />
                  Preview
                </Button>
              </div>
              {account.entity_type && (
                <p className="text-sm text-muted-foreground mt-0.5">{account.entity_type}</p>
              )}
            </div>
          </div>
          {activeTab === "overview" && (
            <div className="flex items-center rounded-md border h-7 overflow-hidden shrink-0" title="Overview layout">
              <button
                type="button"
                onClick={() => setOverviewView("classic")}
                className={`px-2.5 h-full text-xs font-medium inline-flex items-center gap-1 transition-colors ${overviewView === "classic" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                title="Classic layout: sections in two columns"
              >
                <Rows3 className="w-3.5 h-3.5" /> Classic
              </button>
              <button
                type="button"
                onClick={() => setOverviewView("grid")}
                className={`px-2.5 h-full text-xs font-medium inline-flex items-center gap-1 border-l transition-colors ${overviewView === "grid" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                title="Grid layout: cards with the contact list always open"
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Grid
              </button>
            </div>
          )}
        </div>

        {/* ── Full-width tabbed layout ── */}
        <div>
            <Tabs
              value={activeTab}
              onValueChange={(v) => {
                setActiveTab(v);
                // Mirror the tab into the URL (preserving other params) so a later
                // notification deep-link carrying ?tab= is always a real param change.
                const params = new URLSearchParams(window.location.search);
                params.set("tab", v);
                const qs = params.toString();
                window.history.replaceState(null, "", window.location.pathname + (qs ? "?" + qs : ""));
              }}
            >
              <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0 mb-4">
                <TabsTrigger
                  value="overview"
                  className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-semibold data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger
                  value="messages"
                  className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-semibold data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground"
                >
                  Team Chat
                </TabsTrigger>
                <TabsTrigger
                  value="client-chat"
                  className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-semibold data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground"
                >
                  Client Chat
                </TabsTrigger>
                <TabsTrigger
                  value="emails"
                  className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-semibold data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground"
                  onClick={() => { if (!emailsLoading) refetchEmails(); }}
                >
                  <Inbox className="w-3.5 h-3.5 mr-1.5" />
                  Emails
                </TabsTrigger>
                <TabsTrigger
                  value="shared-inbox"
                  className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-semibold data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground"
                  onClick={() => { if (!emailsLoading) refetchEmails(); }}
                >
                  <Inbox className="w-3.5 h-3.5 mr-1.5" />
                  Shared Inbox
                  {(emailData?.sharedUnread ?? 0) > 0 && (
                    <span className="ml-1.5 text-xs bg-blue-100 text-blue-700 rounded-full px-1.5 py-0.5 font-medium">
                      {emailData!.sharedUnread}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="documents"
                  className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-semibold data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground"
                >
                  Documents
                </TabsTrigger>
                <TabsTrigger
                  value="scope"
                  className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-semibold data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground"
                >
                  Scope
                </TabsTrigger>
                <TabsTrigger
                  value="portal"
                  className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-semibold data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground"
                >
                  Portal
                  {unreadPortalActivityCount > 0 && (
                    <span className="ml-1.5 text-xs bg-amber-100 text-amber-700 rounded-full px-1.5 py-0.5 font-medium">
                      {unreadPortalActivityCount}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="tasks"
                  className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-semibold data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground"
                >
                  Tasks
                  {accountTasks.length > 0 && (
                    <span className="ml-1.5 text-xs bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 font-medium">
                      {accountTasks.length}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Overview tab — 70/30 grid */}
              <TabsContent value="overview" className="mt-0">
                <div className="grid w-full" style={{ gridTemplateColumns: overviewView === "grid" ? "2fr 1fr" : rightCollapsed ? "1fr 40px" : "70% 30%" }}>

                  {/* ── LEFT column: grid view (v1.5.75) — 2×2 cards ── */}
                  {overviewView === "grid" ? (
                  <div key="overview-left-grid" className="border-r p-4 min-w-0 grid grid-cols-2 gap-4">
                    <OverviewCard
                      title={showAllProjects ? "All Projects" : "Open Projects"}
                      count={showAllProjects ? projects.filter(p => !p.deleted_at).length : currentProjects.length + (showFutureProjects ? futureProjects.length : 0)}
                      expandKey={openProjectId}
                      action={
                        <select
                          className="h-6 text-xs border rounded bg-background px-1"
                          value={projectsFilterFromFlags(showAllProjects, showFutureProjects)}
                          onChange={(e) => {
                            const flags = flagsFromProjectsFilter(e.target.value as ProjectsFilter);
                            setShowAllProjects(flags.showAll);
                            setShowFutureProjects(flags.showFuture);
                          }}
                          title="Which projects to list"
                        >
                          <option value="open">Open</option>
                          <option value="open_future">Open + Future{futureProjects.length > 0 ? ` (${futureProjects.length})` : ""}</option>
                          <option value="all">All{closedProjects.length > 0 ? ` (+${closedProjects.length} closed)` : ""}</option>
                        </select>
                      }
                    >
                      {/* A ?openProject deep link must still reveal its task, which only the full row can do. */}
                      {openProjectId ? renderProjectsBody({ capped: false }) : renderProjectsCompact()}
                    </OverviewCard>
                    <OverviewCard
                      title="Client Notes"
                      count={notes.length}
                      action={
                        <Button size="sm" variant="ghost" className="h-6 text-xs text-primary px-2" onClick={() => openNoteDialog()}>
                          <Plus className="w-3 h-3 mr-1" /> Add
                        </Button>
                      }
                    >
                      {renderNotesBody()}
                    </OverviewCard>
                    <OverviewCard title="Financial Sources" count={financialSources.length}>
                      <AccountFinancialSources accountId={id} />
                    </OverviewCard>
                    <OverviewCard title="Recent Documents" count={recentUploads.length}>
                      <PortalActivityFeed variant="compact" items={recentUploads} loading={portalActivityLoading} accountId={account.id} emptyText="No client uploads yet." />
                    </OverviewCard>
                  </div>
                  ) : (
                  <div key="overview-left-classic" className="border-r p-4 divide-y min-w-0">
                  {/* ── LEFT column: classic (70%): Open Projects, Financial Sources, Time Activity ── */}

                    {/* Open Projects */}
                    <div className="py-2 first:pt-0">
                    <SideSection
                      title={showAllProjects ? "All Projects" : "Open Projects"}
                      defaultOpen={!!openProjectId}
                      count={showAllProjects ? projects.filter(p => !p.deleted_at).length : currentProjects.length + (showFutureProjects ? futureProjects.length : 0)}
                      action={
                        <div className="flex items-center gap-1">
                          {!showAllProjects && futureProjects.length > 0 && (
                            <Button variant="outline" size="sm" className="h-6 text-xs font-semibold text-foreground" onClick={() => setShowFutureProjects(v => !v)}>
                              {showFutureProjects ? "Hide Future" : `Show Future (${futureProjects.length})`}
                            </Button>
                          )}
                          {(showAllProjects || closedProjects.length > 0) && (
                            <Button variant="outline" size="sm" className="h-6 text-xs font-semibold text-foreground" onClick={() => setShowAllProjects(v => !v)}>
                              {showAllProjects ? "Hide Closed" : `Show Closed (${closedProjects.length})`}
                            </Button>
                          )}
                        </div>
                      }
                    >
                      {renderProjectsBody({ capped: true })}
                    </SideSection>
                    </div>

                    {/* Financial Sources */}
                    <div className="py-2">
                    <SideSection title="Financial Sources" defaultOpen={false} count={financialSources.length}>
                      <AccountFinancialSources accountId={id} />
                    </SideSection>
                    </div>

                    {/* Time Activity */}
                    <div className="py-2">
                    <SideSection title="Time Activity" defaultOpen={false}>
                      {recentActivities.length === 0 ? (
                        <p className="text-xs text-muted-foreground py-2">No recent activity.</p>
                      ) : (
                        <div>
                          {recentActivities.slice(0, 8).map((a) => (
                            <div key={a.id} className="flex items-center justify-between gap-2 py-1.5 border-b last:border-0">
                              <div className="min-w-0">
                                <p className="text-xs font-medium truncate">{a.projectName}</p>
                                <p className="text-[10px] text-muted-foreground truncate">
                                  {a.userName} · {fmt(a.date)}
                                </p>
                              </div>
                              <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                                {fmtDuration(a.durationMinutes)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </SideSection>
                    </div>

                  </div>
                  )}

                  {/* ── RIGHT column (30%): all sidebar sections ── */}
                  {overviewView === "classic" && rightCollapsed ? (
                  <div className="flex flex-col items-center pt-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-foreground"
                      onClick={() => setRightCollapsed(false)}
                      title="Show details panel"
                      aria-label="Expand details panel"
                    >
                      <PanelRightOpen className="w-4 h-4" />
                    </Button>
                  </div>
                  ) : (
                  <div className={overviewView === "grid" ? "min-w-0 space-y-3 p-4" : "min-w-0 divide-y p-4"}>

                    {overviewView === "classic" && (
                    <div className="flex justify-end pb-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={() => setRightCollapsed(true)}
                        title="Hide details panel"
                        aria-label="Collapse details panel"
                      >
                        <PanelRightClose className="w-4 h-4" />
                      </Button>
                    </div>
                    )}

                    {/* 1. Contacts */}
                    <div className="py-2 first:pt-0">
                      {overviewView === "grid" ? (
                        <OverviewCard title="Contacts" count={contacts.length} capPx={null} action={contactsAddButton}>
                          {contactsBody}
                        </OverviewCard>
                      ) : (
                      <SideSection title="Contacts" defaultOpen={false} count={showAllContacts ? 0 : contacts.length - 1} action={
                        <div className="flex items-center gap-1">
                          {contacts.length > 1 && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 text-xs font-semibold text-foreground"
                              onClick={() => setShowAllContacts(v => !v)}
                            >
                              {showAllContacts ? "Primary Only" : `+${contacts.length - 1} More`}
                            </Button>
                          )}
                          {contactsAddButton}
                        </div>
                      }>
                        {contactsBody}
                      </SideSection>
                      )}
                    </div>

                    {/* 2. Notes */}
                    {overviewView === "classic" && (
                    <div className="py-2">
                      <SideSection
                        title="Notes"
                        defaultOpen={false}
                        count={notes.length}
                        action={
                          <Button size="sm" variant="ghost" className="h-6 text-xs text-primary px-2" onClick={() => openNoteDialog()}>
                            <Plus className="w-3 h-3 mr-1" /> Add
                          </Button>
                        }
                      >
                        {renderNotesBody(5)}
                      </SideSection>
                    </div>
                    )}

                    {/* 3. Client Vault */}
                    {!isContractor && (
                      <div className="py-2">
                        <SideSection title="Client Vault" defaultOpen={false} count={vaultRecords.length}>
                          <AccountClientVault accountId={id} />
                        </SideSection>
                      </div>
                    )}

                    {/* 4. SMS */}
                    {isStaffOrManager && (
                      <div className="py-2">
                        <SideSection
                          title="SMS"
                          defaultOpen={false}
                          count={smsLog.length}
                          action={
                            contacts.some((c) => c.sms_ok && c.mobile) ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 text-xs text-primary px-2"
                                onClick={() => {
                                  const first = contacts.find((c) => c.sms_ok && c.mobile);
                                  if (first) { setSmsSendContact(first); setSmsDraftBody(""); setSmsConfirming(false); }
                                }}
                              >
                                <MessageSquareText className="w-3 h-3 mr-1" /> New
                              </Button>
                            ) : undefined
                          }
                        >
                          {!contacts.some((c) => c.sms_ok && c.mobile) ? (
                            // No SMS-enabled contacts — show mobile contacts with enable prompt
                            (() => {
                              const mobileOnly = contacts.filter((c) => c.mobile && !c.sms_ok);
                              if (mobileOnly.length === 0) {
                                return <p className="text-xs text-muted-foreground py-2">No contacts have a mobile number on file.</p>;
                              }
                              return (
                                <div className="py-1 space-y-2">
                                  <p className="text-xs text-muted-foreground">Enable SMS opt-in to start messaging:</p>
                                  <div className="space-y-1.5">
                                    {mobileOnly.map((c) => (
                                      <div key={c.id} className="flex items-center justify-between gap-2">
                                        <div className="min-w-0">
                                          <p className="text-xs font-medium truncate">{c.name}</p>
                                          <p className="text-[10px] text-muted-foreground">{c.mobile}</p>
                                        </div>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="h-6 text-xs shrink-0"
                                          disabled={patchSmsOkMutation.isPending}
                                          onClick={() => patchSmsOkMutation.mutate({ contactId: c.id, sms_ok: true })}
                                        >
                                          Enable SMS
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()
                          ) : smsLog.length === 0 ? (
                            <p className="text-xs text-muted-foreground py-2">No SMS messages yet.</p>
                          ) : (
                            <div className="space-y-1 max-h-60 overflow-y-auto">
                              {smsLog.slice(-10).map((msg) => {
                                const isOut = msg.direction === "outbound";
                                return (
                                  <div key={msg.id} className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
                                    <div className={`max-w-[85%] rounded-xl px-2.5 py-1.5 text-xs ${isOut ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                                      <p className="leading-snug">{msg.body}</p>
                                      <p className={`text-[9px] mt-0.5 ${isOut ? "text-primary-foreground/70 text-right" : "text-muted-foreground"}`}>
                                        {new Date(msg.created_at).toLocaleString()}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {financialSources.length > 0 && (() => {
                            const hasSmsContact = contacts.some((c) => c.sms_ok && c.mobile);
                            return (
                              <details className="mt-1.5 group">
                                <summary className="text-[11px] text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-1 select-none list-none">
                                  <span className="group-open:hidden">▶</span>
                                  <span className="hidden group-open:inline">▼</span>
                                  <span className="ml-0.5">{financialSources.length} financial source{financialSources.length !== 1 ? "s" : ""} on file</span>
                                </summary>
                                <div className="mt-1.5 space-y-1.5 pl-2">
                                  {financialSources.map((s) => (
                                    <div key={s.id} className="flex items-start gap-2 text-xs">
                                      <div className="min-w-0 flex-1">
                                        <span className="font-medium text-foreground">{s.account_nickname || s.institution_name}</span>
                                        {s.account_nickname && <span className="text-muted-foreground ml-1">{s.institution_name}</span>}
                                        {s.external_hint && <p className="text-muted-foreground text-[10px] mt-0.5">{s.external_hint}</p>}
                                      </div>
                                      <div className="flex items-center gap-1.5 shrink-0">
                                        {s.login_url && (
                                          <a href={s.login_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-[10px] mt-0.5">↗</a>
                                        )}
                                        {hasSmsContact && (
                                          <button
                                            type="button"
                                            title="Draft SMS requesting this source"
                                            className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors font-medium"
                                            onClick={() => {
                                              const name = s.account_nickname || s.institution_name;
                                              const hint = s.external_hint ? ` The hint is: ${s.external_hint}.` : "";
                                              setSmsReplyBody(`Hi, we need access to your ${name} account to complete your bookkeeping.${hint} Could you please send us the login info or verification code?`);
                                            }}
                                          >
                                            Request
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            );
                          })()}
                          {contacts.some((c) => c.sms_ok && c.mobile) && (() => {
                            const smsContacts = contacts.filter(c => c.sms_ok && c.mobile);
                            const lastContactId = [...smsLog].reverse().find(m => m.contact_id)?.contact_id;
                            const defaultTarget = contacts.find(c => c.id === lastContactId && c.sms_ok && c.mobile)
                              ?? smsContacts[0];
                            if (!defaultTarget) return null;
                            const replyTarget = smsContacts.find(c => c.id === smsReplyContactId) ?? defaultTarget;
                            return (
                              <div className="space-y-1.5 mt-2">
                                {smsContacts.length > 1 && (
                                  <select
                                    className="w-full h-7 rounded border bg-background px-2 text-xs"
                                    value={replyTarget.id}
                                    onChange={e => setSmsReplyContactId(e.target.value)}
                                  >
                                    {smsContacts.map(c => (
                                      <option key={c.id} value={c.id}>{c.name} — {c.mobile}</option>
                                    ))}
                                  </select>
                                )}
                              <div className="flex gap-1.5 items-end">
                                <textarea
                                  className="flex-1 rounded-lg border bg-background px-2.5 py-1.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-ring"
                                  rows={2}
                                  placeholder={`Message ${replyTarget.name}…`}
                                  value={smsReplyBody}
                                  maxLength={1600}
                                  onChange={e => setSmsReplyBody(e.target.value)}
                                  onKeyDown={async e => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault();
                                      if (!smsReplyBody.trim() || smsReplySending) return;
                                      setSmsReplySending(true);
                                      try {
                                        await api.sendSmsFromProfile({ to: replyTarget.mobile!, body: smsReplyBody.trim(), account_id: id!, contact_id: replyTarget.id });
                                        setSmsReplyBody("");
                                        refetchSmsLog();
                                      } catch { /* ignore */ } finally { setSmsReplySending(false); }
                                    }
                                  }}
                                />
                                <Button
                                  size="sm"
                                  className="h-8 px-2.5 shrink-0 text-xs"
                                  disabled={!smsReplyBody.trim() || smsReplySending}
                                  onClick={async () => {
                                    if (!smsReplyBody.trim() || smsReplySending) return;
                                    setSmsReplySending(true);
                                    try {
                                      await api.sendSmsFromProfile({ to: replyTarget.mobile!, body: smsReplyBody.trim(), account_id: id!, contact_id: replyTarget.id });
                                      setSmsReplyBody("");
                                      refetchSmsLog();
                                    } catch { /* ignore */ } finally { setSmsReplySending(false); }
                                  }}
                                >
                                  {smsReplySending ? "…" : "Send"}
                                </Button>
                              </div>
                              </div>
                            );
                          })()}
                        </SideSection>
                      </div>
                    )}

                    {/* 5. Meetings */}
                    <div className="py-2">
                      <SideSection
                        title="Meetings"
                        defaultOpen={false}
                        count={meetings.filter(m => m.status !== "cancelled" && m.scheduled_at && new Date(m.scheduled_at) > new Date()).length || undefined}
                        action={
                          <Button size="sm" variant="ghost" className="h-6 text-xs text-primary px-2" onClick={() => setShowScheduleMeeting(true)}>
                            <Plus className="w-3 h-3 mr-1" /> Schedule
                          </Button>
                        }
                      >
                        {meetings.filter(m => m.status !== "cancelled").length === 0 ? (
                          <p className="text-xs text-muted-foreground py-2">No meetings scheduled.</p>
                        ) : (
                          <div className="space-y-2">
                            {meetings.filter(m => m.status !== "cancelled").map((m) => {
                              const isPast = m.scheduled_at ? new Date(m.scheduled_at) < new Date() : false;
                              return (
                                <div key={m.id} className={`text-xs border rounded-md p-2 space-y-1 ${isPast ? "opacity-60" : ""}`}>
                                  <div className="flex items-start justify-between gap-1">
                                    <span className="font-medium truncate flex-1">{m.topic}</span>
                                    {m.transcript_text && (
                                      <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700">Transcript</span>
                                    )}
                                  </div>
                                  {m.scheduled_at && (
                                    <p className="text-muted-foreground">
                                      {new Date(m.scheduled_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                                      {" · "}{m.duration_minutes}min
                                    </p>
                                  )}
                                  {!isPast && (
                                    <div className="flex items-center gap-1.5 pt-0.5">
                                      {m.join_url && (
                                        <a href={m.join_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 hover:underline">Join</a>
                                      )}
                                      {isStaffOrManager && (
                                        <>
                                          <span className="text-muted-foreground">·</span>
                                          <button
                                            className="text-[10px] text-blue-600 hover:underline"
                                            disabled={startingMeetingId === m.id}
                                            onClick={() => handleStartMeeting(m)}
                                          >
                                            {startingMeetingId === m.id ? "Starting…" : "Start"}
                                          </button>
                                          <span className="text-muted-foreground">·</span>
                                          <button
                                            className="text-[10px] text-blue-600 hover:underline"
                                            onClick={() => setRescheduleMeeting(m)}
                                          >
                                            Reschedule
                                          </button>
                                          <span className="text-muted-foreground">·</span>
                                          <button
                                            className="text-[10px] text-rose-600 hover:underline"
                                            onClick={async () => {
                                              if (await askConfirm({
                                                title: "Cancel this meeting?",
                                                subject: "meeting",
                                                body: "will be cancelled.",
                                                lines: ["Attendees are notified and the Zoom meeting is removed."],
                                                confirmLabel: "Cancel meeting",
                                                cancelLabel: "Keep meeting",
                                                destructive: true,
                                              })) cancelMeetingMutation.mutate(m.id);
                                            }}
                                          >
                                            Cancel
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </SideSection>
                    </div>

                    {/* 5b. Claire Walker transcripts — a DIFFERENT corpus from
                        the Zoom meetings above, kept as its own section so the
                        two aren't mistaken for each other. */}
                    <div className="py-2">
                      <SideSection title="Meeting Transcripts (Claire)" defaultOpen={false}>
                        <ClaireTranscripts accountId={id} />
                      </SideSection>
                    </div>

                    {/* 6. Relationships */}
                    <div className="py-2">
                      <SideSection
                        title="Relationships"
                        defaultOpen={false}
                        count={relationships.length}
                        action={
                          <Button variant="ghost" size="sm" className="h-6 text-xs text-primary" onClick={() => setShowAddRelationship((v) => !v)}>
                            <Plus className="w-3 h-3 mr-1" />Add
                          </Button>
                        }
                      >
                        {showAddRelationship && (
                          <div className="mb-3 space-y-2 rounded-lg border bg-muted/20 p-2.5">
                            <div className="flex gap-1.5">
                              <select
                                className="border rounded px-2 py-1.5 text-xs bg-background flex-1 min-w-0"
                                value={newRelOtherAccountId}
                                onChange={(e) => setNewRelOtherAccountId(e.target.value)}
                              >
                                <option value="">Select account…</option>
                                {allAccounts
                                  .filter((a) => a.id !== id)
                                  .sort((a, b) => a.account_name.localeCompare(b.account_name))
                                  .map((a) => (
                                    <option key={a.id} value={a.id}>{a.account_name}</option>
                                  ))}
                              </select>
                              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => { setShowAddRelationship(false); setNewRelOtherAccountId(""); setNewRelLabel(""); }}>
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                            <div className="flex gap-1.5">
                              <div className="relative flex-1">
                                <Input
                                  placeholder="Label (e.g. Owner)"
                                  value={newRelLabel}
                                  onChange={(e) => setNewRelLabel(e.target.value)}
                                  list="rel-suggestions"
                                  className="text-xs h-7"
                                />
                                <datalist id="rel-suggestions">
                                  {RELATIONSHIP_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
                                </datalist>
                              </div>
                              <Button
                                size="sm"
                                className="h-7 px-3 text-xs shrink-0"
                                disabled={!newRelOtherAccountId || !newRelLabel.trim() || createRelMutation.isPending}
                                onClick={() => createRelMutation.mutate()}
                              >
                                {createRelMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                              </Button>
                            </div>
                          </div>
                        )}
                        {relationships.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No related accounts</p>
                        ) : (
                          <div className="space-y-1.5">
                            {relationships.map((r) => (
                              <div key={r.id} className="flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                  <a href={`/accounts/${r.otherId}`} className="text-sm font-medium text-primary hover:underline truncate block">{r.otherName}</a>
                                  <p className="text-xs text-muted-foreground">{r.label}</p>
                                </div>
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive shrink-0" onClick={() => deleteRelMutation.mutate(r.id)} disabled={deleteRelMutation.isPending}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </SideSection>
                    </div>

                    {/* 7. Services & Links */}
                    <div className="py-2">
                      <SideSection
                        title="Services & Links"
                        defaultOpen={false}
                        count={services.length}
                        action={
                          !isContractor ? (
                            <Button variant="ghost" size="sm" className="h-6 text-xs text-primary" onClick={() => setShowAddService(v => !v)}>
                              <Plus className="w-3 h-3 mr-1" />Add
                            </Button>
                          ) : undefined
                        }
                      >
                        {showAddService && (
                          <div className="mb-3 space-y-1.5 p-2 rounded-md border bg-muted/30">
                            <input className="w-full h-7 px-2 text-xs border rounded bg-background" placeholder="Service name *" value={newSvcName} onChange={e => setNewSvcName(e.target.value)} />
                            <input className="w-full h-7 px-2 text-xs border rounded bg-background" placeholder="URL (https://...)" value={newSvcUrl} onChange={e => setNewSvcUrl(e.target.value)} />
                            <select className="w-full h-7 px-2 text-xs border rounded bg-background" value={newSvcCategory} onChange={e => setNewSvcCategory(e.target.value)}>
                              <option value="payroll">Payroll</option>
                              <option value="project_mgmt">Project Management</option>
                              <option value="accounting">Accounting</option>
                              <option value="banking">Banking</option>
                              <option value="n8n">n8n Workflow</option>
                              <option value="smartsuite">SmartSuite</option>
                              <option value="other">Other</option>
                            </select>
                            <input className="w-full h-7 px-2 text-xs border rounded bg-background" placeholder="Description (optional)" value={newSvcDesc} onChange={e => setNewSvcDesc(e.target.value)} />
                            <div className="flex gap-1.5">
                              <Button size="sm" className="h-6 text-xs flex-1" disabled={!newSvcName.trim() || createServiceMutation.isPending} onClick={() => createServiceMutation.mutate()}>
                                {createServiceMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                              </Button>
                              <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => { setShowAddService(false); setNewSvcName(""); setNewSvcUrl(""); setNewSvcCategory("other"); setNewSvcDesc(""); }}>Cancel</Button>
                            </div>
                          </div>
                        )}
                        {services.length === 0 && !showAddService ? (
                          <p className="text-sm text-muted-foreground">No services added yet</p>
                        ) : (
                          <div className="space-y-2">
                            {services.map((svc) => {
                              const catIcon = svc.category === "payroll" ? <DollarSign className="w-3.5 h-3.5" /> :
                                svc.category === "project_mgmt" ? <FolderKanban className="w-3.5 h-3.5" /> :
                                svc.category === "accounting" ? <Calculator className="w-3.5 h-3.5" /> :
                                svc.category === "banking" ? <Building2 className="w-3.5 h-3.5" /> :
                                svc.category === "n8n" ? <Zap className="w-3.5 h-3.5" /> :
                                svc.category === "smartsuite" ? <Grid3X3 className="w-3.5 h-3.5" /> :
                                <Link2 className="w-3.5 h-3.5" />;
                              return (
                                <div key={svc.id}>
                                  {editingServiceId === svc.id ? (
                                    <div className="rounded-lg border bg-muted/20 p-2 space-y-1.5">
                                      <input className="w-full h-7 px-2 text-xs border rounded bg-background" placeholder="Service name *" value={editSvcForm.name} onChange={e => setEditSvcForm(f => ({ ...f, name: e.target.value }))} />
                                      <input className="w-full h-7 px-2 text-xs border rounded bg-background" placeholder="URL (https://...)" value={editSvcForm.url} onChange={e => setEditSvcForm(f => ({ ...f, url: e.target.value }))} />
                                      <select className="w-full h-7 px-2 text-xs border rounded bg-background" value={editSvcForm.category} onChange={e => setEditSvcForm(f => ({ ...f, category: e.target.value }))}>
                                        <option value="payroll">Payroll</option>
                                        <option value="project_mgmt">Project Management</option>
                                        <option value="accounting">Accounting</option>
                                        <option value="banking">Banking</option>
                                        <option value="n8n">n8n Workflow</option>
                                        <option value="smartsuite">SmartSuite</option>
                                        <option value="other">Other</option>
                                      </select>
                                      <input className="w-full h-7 px-2 text-xs border rounded bg-background" placeholder="Description (optional)" value={editSvcForm.description} onChange={e => setEditSvcForm(f => ({ ...f, description: e.target.value }))} />
                                      <div className="flex gap-1.5 pt-0.5">
                                        <Button size="sm" className="h-6 text-xs px-2" disabled={!editSvcForm.name.trim() || updateServiceMutation.isPending} onClick={() => updateServiceMutation.mutate(svc.id)}>
                                          {updateServiceMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                                        </Button>
                                        <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setEditingServiceId(null)}>Cancel</Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="group flex items-start gap-2">
                                      <span className="text-muted-foreground shrink-0 mt-0.5">{catIcon}</span>
                                      <div className="min-w-0 flex-1">
                                        {svc.url ? (
                                          <a href={svc.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary hover:underline truncate block">{svc.name}</a>
                                        ) : (
                                          <span className="text-sm font-medium truncate block">{svc.name}</span>
                                        )}
                                        {svc.description && <p className="text-xs text-muted-foreground truncate">{svc.description}</p>}
                                      </div>
                                      {!isContractor && (
                                        <div className="flex items-center gap-0.5 opacity-90 shrink-0">
                                          <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-foreground" onClick={() => { setEditingServiceId(svc.id); setEditSvcForm({ name: svc.name, url: svc.url ?? "", category: svc.category ?? "other", description: svc.description ?? "" }); }}>
                                            <Pencil className="w-3 h-3" />
                                          </Button>
                                          <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={async () => {
                                            if (await askConfirm({
                                              title: "Delete this service?",
                                              subject: "service",
                                              name: svc.name,
                                              body: "will be removed from this client.",
                                              confirmLabel: "Delete",
                                              destructive: true,
                                            })) deleteServiceMutation.mutate(svc.id);
                                          }}>
                                            <Trash2 className="w-3 h-3" />
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </SideSection>
                    </div>

                    {/* 8. About */}
                    <div className="py-2">
                      <SideSection title="About" defaultOpen={false} action={
                        <Button variant="ghost" size="sm" className="h-6 text-xs text-primary" onClick={() => {
                          setAboutForm({
                            account_name: account.account_name ?? "",
                            entity_type: account.entity_type ?? "",
                            ein: account.ein ?? "",
                            last_4_ssn: account.last_4_ssn ?? "",
                            address_1: account.address_1 ?? "",
                            city: account.city ?? "",
                            state: account.state ?? "",
                            zip: account.zip ?? "",
                            website: account.website ?? "",
                            formation_date: account.formation_date ?? "",
                            s_election_date: account.s_election_date ?? "",
                            cs_client_id: account.cs_client_id ?? "",
                            timezone: account.timezone ?? "",
                          });
                          setEditingAbout(v => !v);
                        }}>
                          <Plus className="w-3 h-3 mr-1" />{editingAbout ? "Cancel" : "Edit"}
                        </Button>
                      }>
                        {editingAbout ? (
                          <div className="space-y-1.5">
                            {[
                              { key: "account_name", label: "Name" },
                              { key: "entity_type", label: "Entity type" },
                              { key: "ein", label: "EIN" },
                              { key: "last_4_ssn", label: "SSN (last 4)" },
                              { key: "cs_client_id", label: "CS Client ID" },
                              { key: "address_1", label: "Address" },
                              { key: "city", label: "City" },
                              { key: "state", label: "State" },
                              { key: "zip", label: "ZIP" },
                              { key: "website", label: "Website" },
                              { key: "formation_date", label: "Formed", type: "date" },
                              { key: "s_election_date", label: "S-Election", type: "date" },
                            ].map(({ key, label, type }) => (
                              <div key={key}>
                                <label className="text-xs text-muted-foreground">{label}</label>
                                <input type={type ?? "text"} className="w-full h-7 px-2 text-xs border rounded bg-background mt-0.5" value={aboutForm[key] ?? ""} onChange={e => setAboutForm(prev => ({ ...prev, [key]: e.target.value }))} />
                              </div>
                            ))}
                            <div>
                              <label className="text-xs text-muted-foreground">Time zone</label>
                              <select className="w-full h-7 px-2 text-xs border rounded bg-background mt-0.5" value={aboutForm.timezone ?? ""} onChange={e => setAboutForm(prev => ({ ...prev, timezone: e.target.value }))}>
                                <option value="">— not set —</option>
                                <option value="America/New_York">Eastern (ET)</option>
                                <option value="America/Chicago">Central (CT)</option>
                                <option value="America/Denver">Mountain (MT)</option>
                                <option value="America/Los_Angeles">Pacific (PT)</option>
                              </select>
                            </div>
                            <div className="flex gap-1.5 pt-1">
                              <Button size="sm" className="h-6 text-xs flex-1" disabled={updateAboutMutation.isPending} onClick={() => updateAboutMutation.mutate()}>Save</Button>
                              <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setEditingAbout(false)}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <dl className="space-y-1.5 text-sm">
                            {account.entity_type && <div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">Entity</dt><dd>{account.entity_type}</dd></div>}
                            {account.timezone && <div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">Time zone</dt><dd>{{ "America/New_York": "Eastern (ET)", "America/Chicago": "Central (CT)", "America/Denver": "Mountain (MT)", "America/Los_Angeles": "Pacific (PT)" }[account.timezone] ?? account.timezone}</dd></div>}
                            {account.ein && <div className="flex gap-2 items-center"><dt className="text-muted-foreground w-24 shrink-0">EIN</dt><dd className="font-mono flex items-center gap-1">{account.ein}<CopyBtn value={account.ein} /></dd></div>}
                            {account.last_4_ssn && <div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">SSN (last 4)</dt><dd className="font-mono">***-**-{account.last_4_ssn}</dd></div>}
                            {account.cs_client_id && <div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">CS Client ID</dt><dd className="font-mono">{account.cs_client_id}</dd></div>}
                            {account.formation_date && <div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">Formed</dt><dd>{fmt(account.formation_date)}</dd></div>}
                            {account.s_election_date && <div className="flex gap-2"><dt className="text-muted-foreground w-24 shrink-0">S-Election</dt><dd>{fmt(account.s_election_date)}</dd></div>}
                            {hasAddress && (
                              <div className="flex gap-2 items-start">
                                <dt className="text-muted-foreground w-24 shrink-0 flex items-start gap-1"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />Address</dt>
                                <dd className="leading-snug flex-1">
                                  {account.address_1 && <span>{account.address_1}<br /></span>}
                                  {(account.city || account.state || account.zip) && <span>{[account.city, account.state, account.zip].filter(Boolean).join(", ")}</span>}
                                </dd>
                                <CopyBtn value={[account.address_1, [account.city, account.state, account.zip].filter(Boolean).join(", ")].filter(Boolean).join("\n")} />
                              </div>
                            )}
                            {account.website && (
                              <div className="flex gap-2">
                                <dt className="text-muted-foreground w-24 shrink-0 flex items-center gap-1"><Globe className="w-3.5 h-3.5" />Website</dt>
                                <dd><a href={account.website.startsWith("http") ? account.website : `https://${account.website}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">{account.website}<ExternalLink className="w-3 h-3" /></a></dd>
                              </div>
                            )}
                            {!account.entity_type && !account.timezone && !account.ein && !account.last_4_ssn && !hasAddress && !account.website && !account.formation_date && !account.s_election_date && !account.cs_client_id && (
                              <p className="text-xs text-muted-foreground">No details on file. Click Edit to add.</p>
                            )}
                          </dl>
                        )}
                      </SideSection>
                    </div>

                    {/* 9. Resources */}
                    <div className="py-2">
                      <SideSection
                        title="Resources"
                        defaultOpen={false}
                        count={resources.length}
                        action={
                          <Button variant="ghost" size="sm" className="h-6 text-xs text-primary" onClick={() => setShowAddResource((v) => !v)}>
                            <Plus className="w-3 h-3 mr-1" />Add
                          </Button>
                        }
                      >
                        {showAddResource && (
                          <div className="mb-2.5 space-y-1.5 rounded-lg border bg-muted/20 p-2.5">
                            <Input placeholder="Label (e.g. QBO Login)" value={newResLabel} onChange={(e) => setNewResLabel(e.target.value)} className="text-xs h-7" />
                            <Input placeholder="URL" value={newResUrl} onChange={(e) => setNewResUrl(e.target.value)} className="text-xs h-7" />
                            <div className="flex gap-1.5 justify-end">
                              <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => { setShowAddResource(false); setNewResLabel(""); setNewResUrl(""); }}>Cancel</Button>
                              <Button size="sm" className="h-6 text-xs" disabled={!newResLabel.trim() || !newResUrl.trim() || createResMutation.isPending} onClick={() => createResMutation.mutate()}>
                                {createResMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                              </Button>
                            </div>
                          </div>
                        )}
                        {!hasResources && !showAddResource ? (
                          <p className="text-sm text-muted-foreground">No resources added</p>
                        ) : (
                          <div className="space-y-1.5">
                            {account.smartvault_url && (
                              <a href={account.smartvault_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1.5 truncate">
                                <ExternalLink className="w-3.5 h-3.5 shrink-0" />SmartVault Client Folder
                              </a>
                            )}
                            {account.sv_client_id && user?.role !== "contractor" && (
                              <div className="mt-1 space-y-1">
                                <div className="flex items-center gap-1.5">
                                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                  <span className="text-xs text-emerald-700 font-medium">SV Provisioned</span>
                                  <code className="text-xs text-muted-foreground font-mono ml-1 truncate max-w-[120px]" title={account.sv_client_id}>{account.sv_client_id.slice(0, 12)}…</code>
                                </div>
                                <Button variant="ghost" size="sm" className="h-6 text-xs gap-1 px-1.5 text-primary" onClick={() => setSvPanelOpen(true)}>
                                  <HardDrive className="w-3 h-3" />Browse vault
                                </Button>
                              </div>
                            )}
                            {!account.sv_client_id && user?.role === "admin" && (
                              <div className="mt-1 space-y-1.5">
                                <div className="flex items-center gap-1.5">
                                  <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                                  <span className="text-xs text-amber-700">Not provisioned in SV</span>
                                  <Button variant="ghost" size="sm" className="h-6 text-xs gap-1 px-1.5 ml-auto" onClick={() => provisionSvMutation.mutate()} disabled={provisionSvMutation.isPending}>
                                    {provisionSvMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}Provision
                                  </Button>
                                </div>
                              </div>
                            )}
                            {resources.map((r) => (
                              <div key={r.id} className="flex items-center justify-between gap-2">
                                <a href={r.url.startsWith("http") ? r.url : `https://${r.url}`} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1.5 min-w-0 truncate">
                                  <ExternalLink className="w-3.5 h-3.5 shrink-0" />{r.label}
                                </a>
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive shrink-0" onClick={() => deleteResMutation.mutate(r.id)} disabled={deleteResMutation.isPending}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </SideSection>
                    </div>

                    {/* 10. Assignees */}
                    <div className="py-2">
                      <SideSection
                        title="Assignees"
                        defaultOpen={false}
                        count={assignees.length}
                        action={
                          canEditAssignees ? (
                            <Button variant="ghost" size="sm" className="h-6 text-xs text-primary" onClick={() => setShowAddAssignee((v) => !v)}>
                              <Plus className="w-3 h-3 mr-1" />Add
                            </Button>
                          ) : undefined
                        }
                      >
                        {showAddAssignee && (
                          <div className="mb-2 flex gap-1.5">
                            <select className="border rounded px-2 py-1 text-xs bg-background flex-1 min-w-0" value={newAssigneeUserId} onChange={(e) => setNewAssigneeUserId(e.target.value)}>
                              <option value="">Select staff…</option>
                              {availableUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                            <Button size="sm" className="h-7 px-2 text-xs shrink-0" disabled={!newAssigneeUserId || addAssigneeMutation.isPending} onClick={() => addAssigneeMutation.mutate()}>
                              {addAssigneeMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add"}
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => { setShowAddAssignee(false); setNewAssigneeUserId(""); }}>
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                        {assignees.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No staff assigned</p>
                        ) : (
                          <div className="space-y-2">
                            {assignees.map((a) => (
                              <div key={a.userId} className="space-y-1">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-1.5 min-w-0">
                                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                      <span className="text-xs font-semibold text-primary">{a.name.charAt(0)}</span>
                                    </div>
                                    <span className="text-sm truncate">{a.name}</span>
                                  </div>
                                  {canEditAssignees && (
                                    <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive shrink-0" onClick={() => removeAssigneeMutation.mutate(a.userId)} disabled={removeAssigneeMutation.isPending}>
                                      <X className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                                <select className="w-full h-6 text-xs border rounded bg-background px-1 text-muted-foreground disabled:opacity-60" value={a.workflowRole ?? ""} disabled={!canEditAssignees} onChange={(e) => workflowRoleMutation.mutate({ userId: a.userId, workflowRole: e.target.value || null })}>
                                  <option value="">— workflow role —</option>
                                  <option value="account_manager">Account Manager</option>
                                  <option value="preparer">Preparer</option>
                                  <option value="checker">Checker</option>
                                  <option value="reviewer">Reviewer</option>
                                  <option value="assembler">Assembler</option>
                                  <option value="signer">Signer</option>
                                </select>
                              </div>
                            ))}
                          </div>
                        )}
                      </SideSection>
                    </div>

                    {/* 11. E-Signatures */}
                    <div className="py-2">
                      <SideSection
                        title="E-Signatures"
                        defaultOpen={false}
                        count={signatureRequests.filter((r) => ["pending", "awaiting_signature"].includes(r.status)).length || undefined}
                        action={
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 text-xs text-primary px-2"
                            onClick={() => setShowSendSignature(true)}
                          >
                            <SendIcon className="w-3 h-3 mr-1" /> Send
                          </Button>
                        }
                      >
                        {signatureRequests.length === 0 ? (
                          <p className="text-xs text-muted-foreground py-2">No signature requests yet.</p>
                        ) : (
                          <div className="space-y-2">
                            {signatureRequests.slice(0, 5).map((sr) => {
                              const isActive = ["pending", "awaiting_signature"].includes(sr.status);
                              const isDone = sr.status === "completed";
                              return (
                                <div key={sr.id} className="text-xs border rounded-md p-2 space-y-1">
                                  <div className="flex items-start justify-between gap-1">
                                    <span className="font-medium truncate">{sr.title}</span>
                                    <span className={cn(
                                      "shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-medium",
                                      isDone ? "bg-green-100 text-green-700" :
                                      isActive ? "bg-amber-100 text-amber-700" :
                                      "bg-gray-100 text-gray-500"
                                    )}>
                                      {isDone ? "Signed" : sr.status === "awaiting_signature" ? "Awaiting" : sr.status}
                                    </span>
                                  </div>
                                  <div className="text-muted-foreground space-y-0.5">
                                    {sr.signers.map((s, i) => (
                                      <div key={i} className="flex items-center gap-1">
                                        {s.status === "completed"
                                          ? <CheckCircle2Icon className="w-3 h-3 text-green-600" />
                                          : <ClockIcon className="w-3 h-3 text-amber-500" />}
                                        <span>{s.name}</span>
                                      </div>
                                    ))}
                                  </div>
                                  {isActive && (
                                    <div className="flex items-center gap-1 pt-0.5">
                                      <button
                                        className="text-[10px] text-blue-600 hover:underline"
                                        onClick={() => syncSignatureMutation.mutate(sr.id)}
                                      >
                                        Sync
                                      </button>
                                      <span className="text-muted-foreground">·</span>
                                      <button
                                        className="text-[10px] text-blue-600 hover:underline"
                                        onClick={() => remindSignatureMutation.mutate(sr.id)}
                                      >
                                        Remind
                                      </button>
                                      <span className="text-muted-foreground">·</span>
                                      <button
                                        className="text-[10px] text-rose-600 hover:underline"
                                        onClick={async () => {
                                          if (await askConfirm({
                                            title: "Void this signature request?",
                                            subject: "signature request",
                                            body: "will be voided.",
                                            lines: ["The signer will no longer be able to sign."],
                                            confirmLabel: "Void request",
                                            destructive: true,
                                          })) voidSignatureMutation.mutate(sr.id);
                                        }}
                                      >
                                        Void
                                      </button>
                                    </div>
                                  )}
                                  {sr.signwell_document_url && (
                                    <a href={sr.signwell_document_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5">
                                      <ExternalLinkIcon className="w-2.5 h-2.5" /> View signed doc
                                    </a>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </SideSection>
                    </div>

                  </div>
                  )}

                </div>
              </TabsContent>


              {/* Tasks tab */}
              <TabsContent value="tasks" className="mt-0">
                <div className="rounded-lg border bg-card shadow-sm">
                  <div className="px-4 py-3 border-b flex items-center justify-between">
                    <span className="text-sm font-bold text-foreground tracking-tight">
                      {showCompletedTasks ? "All Tasks" : "Open Tasks"}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setTaskSortOrder(v => v === "asc" ? "desc" : "asc")}
                        className="text-xs px-2.5 py-1 rounded border border-border text-muted-foreground hover:bg-muted transition-colors flex items-center gap-1"
                        title={taskSortOrder === "asc" ? "Sorted: earliest first — click to reverse" : "Sorted: latest first — click to reverse"}
                      >
                        <ArrowUpDown className="w-3 h-3" />
                        Due {taskSortOrder === "asc" ? "↑" : "↓"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCompletedTasks((v) => !v)}
                        className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                          showCompletedTasks
                            ? "bg-primary/10 border-primary text-primary"
                            : "border-border text-muted-foreground hover:bg-muted"
                        }`}
                      >
                        {showCompletedTasks ? "Hide completed" : "Show completed"}
                      </button>
                    </div>
                  </div>
                  <div>
                    {accountTasks.length === 0 ? (
                      <div className="py-10 text-center text-sm text-muted-foreground">
                        <ListTodo className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        {showCompletedTasks ? "No tasks" : "No open tasks"}
                      </div>
                    ) : (() => {
                      // Group tasks by project
                      const byProject = new Map<string, { projectId: string; projectTitle: string; tasks: ProjectTask[] }>();
                      for (const task of accountTasks) {
                        const key = task.project_id;
                        if (!byProject.has(key)) {
                          byProject.set(key, { projectId: key, projectTitle: task.project_title ?? "Ungrouped", tasks: [] });
                        }
                        byProject.get(key)!.tasks.push(task);
                      }
                      const groups = [...byProject.values()].sort((a, b) => {
                        // Sort project groups by their earliest upcoming due date, then alpha
                        const aNext = a.tasks.filter(t => t.due_date && t.status !== "done" && t.status !== "completed").map(t => t.due_date!).sort()[0];
                        const bNext = b.tasks.filter(t => t.due_date && t.status !== "done" && t.status !== "completed").map(t => t.due_date!).sort()[0];
                        if (aNext && bNext) return aNext.localeCompare(bNext);
                        if (aNext) return -1;
                        if (bNext) return 1;
                        return a.projectTitle.localeCompare(b.projectTitle);
                      });
                      return (
                        <div>
                          {groups.map((group) => {
                            const isCollapsed = collapsedTaskProjects.has(group.projectId);
                            const openCount = group.tasks.filter(t => t.status !== "done" && t.status !== "completed").length;
                            const nextDue = group.tasks
                              .filter(t => t.due_date && t.status !== "done" && t.status !== "completed")
                              .map(t => t.due_date!)
                              .sort()[0];
                            const nextDueLabel = nextDue ? format(parseISO(nextDue), "MMM d") : null;
                            const isOverdue = nextDue ? nextDue < new Date().toISOString().slice(0, 10) : false;
                            const sortedTasks = [...group.tasks].sort((a, b) => {
                              if (!a.due_date && !b.due_date) return 0;
                              if (!a.due_date) return taskSortOrder === "asc" ? 1 : -1;
                              if (!b.due_date) return taskSortOrder === "asc" ? -1 : 1;
                              return taskSortOrder === "asc"
                                ? a.due_date.localeCompare(b.due_date)
                                : b.due_date.localeCompare(a.due_date);
                            });
                            return (
                              <div key={group.projectId} className="border-b last:border-0">
                                <button
                                  type="button"
                                  onClick={() => setCollapsedTaskProjects(prev => {
                                    const next = new Set(prev);
                                    if (next.has(group.projectId)) next.delete(group.projectId);
                                    else next.add(group.projectId);
                                    return next;
                                  })}
                                  className="w-full flex items-center gap-2 px-4 py-2 bg-indigo-50 hover:bg-indigo-100/70 border-b border-indigo-100 transition-colors text-left"
                                >
                                  <ChevronRight className={`w-3.5 h-3.5 text-indigo-400 shrink-0 transition-transform duration-150 ${isCollapsed ? "" : "rotate-90"}`} />
                                  <span className="text-sm font-bold text-indigo-900 flex-1 min-w-0 truncate">{group.projectTitle}</span>
                                  <span className="text-xs font-bold text-indigo-800 bg-indigo-100 border border-indigo-200 rounded-full px-2 py-0.5 shrink-0">
                                    {openCount}/{group.tasks.length}
                                  </span>
                                  {nextDueLabel && (
                                    <span className={`text-xs font-semibold shrink-0 px-2 py-0.5 rounded border ${isOverdue ? "text-red-700 bg-red-50 border-red-200" : "text-indigo-700 bg-white border-indigo-200"}`}>
                                      {isOverdue ? "Overdue · " : "Next · "}{nextDueLabel}
                                    </span>
                                  )}
                                </button>
                                {!isCollapsed && sortedTasks.map((task) => (
                                  <TaskDetailRow
                                    key={task.id}
                                    task={{ ...task, project_title: undefined }}
                                    accountId={id!}
                                    onStatusChange={(taskId) => {
                                      if (!showCompletedTasks) {
                                        queryClient.setQueryData<ProjectTask[]>(
                                          ["account-tasks", id, showCompletedTasks],
                                          (old) => old ? old.filter((t) => t.id !== taskId) : old
                                        );
                                      }
                                      queryClient.invalidateQueries({ queryKey: ["account-tasks", id] });
                                    }}
                                    onDelete={(taskId) => {
                                      queryClient.setQueryData<ProjectTask[]>(
                                        ["account-tasks", id, showCompletedTasks],
                                        (old) => old ? old.filter((t) => t.id !== taskId) : old
                                      );
                                      queryClient.invalidateQueries({ queryKey: ["account-tasks", id] });
                                    }}
                                    onUpdate={() => {
                                      queryClient.invalidateQueries({ queryKey: ["account-tasks", id] });
                                    }}
                                  />
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </TabsContent>

              {/* Team Chat tab — internal project-level messages, never visible to client */}
              <TabsContent value="messages" className="mt-0">
                <div className="rounded-lg border bg-card shadow-sm flex overflow-hidden" style={{ height: 540 }}>
                  {/* ── Left panel: project list ── */}
                  <div className="w-56 shrink-0 border-r flex flex-col bg-muted/20">
                    {/* Search */}
                    <div className="px-2 py-2 border-b">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                        <input
                          className="w-full pl-6 pr-2 py-1 text-xs rounded border bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                          placeholder="Search projects…"
                          value={teamChatSearch}
                          onChange={(e) => setTeamChatSearch(e.target.value)}
                        />
                      </div>
                    </div>
                    {/* Project list */}
                    <div className="flex-1 overflow-y-auto">
                      {(() => {
                        const allProjects = projects.filter((p) => !p.deleted_at);
                        const filtered = teamChatSearch.trim()
                          ? allProjects.filter((p) => (p.title ?? "").toLowerCase().includes(teamChatSearch.toLowerCase()))
                          : allProjects;
                        const sorted = filtered
                          .filter((p) => p.status !== "closed" && p.status !== "completed")
                          .sort((a, b) => (b.due_date ?? "").localeCompare(a.due_date ?? ""));
                        if (sorted.length === 0) {
                          return <p className="text-xs text-muted-foreground px-3 py-4">No projects found</p>;
                        }
                        return sorted.map((p) => {
                          const isClosed = p.status === "closed" || p.status === "completed";
                          const dateLabel = p.due_date
                            ? (() => { try { return format(new Date(p.due_date + "T12:00:00"), "MMM ''yy"); } catch { return ""; } })()
                            : "";
                          return (
                            <button
                              key={p.id}
                              onClick={() => setSelectedMsgProjectId(p.id)}
                              className={`w-full text-left px-3 py-2.5 border-b border-border/40 transition-colors ${
                                selectedMsgProjectId === p.id
                                  ? "bg-primary/10 border-l-2 border-l-primary"
                                  : "hover:bg-muted/50"
                              }`}
                            >
                              <div className="flex items-start justify-between gap-1">
                                <span className={`text-xs font-medium leading-tight ${isClosed ? "text-muted-foreground" : "text-foreground"}`}>
                                  {p.title}
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                {dateLabel && <span className="text-xs text-muted-foreground">{dateLabel}</span>}
                                {isClosed && (
                                  <span className="text-xs bg-muted text-muted-foreground px-1 rounded">Closed</span>
                                )}
                              </div>
                            </button>
                          );
                        });
                      })()}
                    </div>
                  </div>

                  {/* ── Right panel: thread ── */}
                  <div className="flex-1 flex flex-col min-w-0">
                    {!selectedMsgProjectId ? (
                      <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                        <div className="text-center">
                          <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
                          Select a project to view team chat
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Thread header */}
                        <div className="px-4 py-2.5 border-b shrink-0">
                          <span className="text-sm font-semibold">
                            {projects.find((p) => p.id === selectedMsgProjectId)?.title ?? ""}
                          </span>
                        </div>
                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                          {messagesLoading ? (
                            <div className="py-6 text-center">
                              <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                            </div>
                          ) : projectMessages.length === 0 ? (
                            <div className="py-8 text-center text-sm text-muted-foreground">
                              No messages yet — start the conversation below
                            </div>
                          ) : (
                            projectMessages.map((m) => (
                              <MessageRow
                                key={m.id}
                                message={m}
                                fetchAttachments={getMessageAttachments}
                                onDeleteAttachment={(aid) => deleteTeamChatAttachmentMutation.mutate(aid)}
                                expanded={!!tcExpandedAttachments[m.id]}
                                onToggleExpand={() => setTcExpandedAttachments((prev) => ({ ...prev, [m.id]: !prev[m.id] }))}
                                onLike={() => likeTeamMessageMutation.mutate(m.id)}
                                onEdit={(messageId, body) => editTeamMessageMutation.mutateAsync({ messageId, body })}
                                savingEdit={editTeamMessageMutation.isPending}
                              />
                            ))
                          )}
                        </div>
                        {/* Compose */}
                        <div className="px-4 pb-4 border-t pt-3 space-y-2 shrink-0">
                          {/* @mention autocomplete */}
                          {tcMentionQuery !== null && tcMentionMatches.length > 0 && (
                            <div className="border rounded-md bg-popover shadow-md overflow-hidden">
                              <p className="text-[10px] text-muted-foreground px-2 pt-1.5 pb-0.5 font-medium uppercase tracking-wide">Mention</p>
                              {tcMentionMatches.map((u: any, i: number) => (
                                <button
                                  key={u.id}
                                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-muted/50 transition-colors ${i === tcMentionIndex ? "bg-muted" : ""}`}
                                  onMouseDown={(e) => { e.preventDefault(); tcInsertMention(u); }}
                                  onMouseEnter={() => setTcMentionIndex(i)}
                                >
                                  <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-[10px] font-bold flex items-center justify-center shrink-0">
                                    {(u.name?.[0] ?? "?").toUpperCase()}
                                  </span>
                                  <span>{u.name}</span>
                                </button>
                              ))}
                            </div>
                          )}
                          <textarea
                            ref={tcTextareaRef}
                            rows={3}
                            placeholder="Internal team message… Type @ to mention someone, ⌘+Enter to send"
                            value={messageBody}
                            onChange={tcHandleBodyChange}
                            onPaste={tcHandlePaste}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              const files = Array.from(e.dataTransfer.files);
                              if (files.length) setTcFileQueue((q) => [...q, ...files]);
                            }}
                            onKeyDown={(e) => {
                              if (tcMentionQuery !== null && tcMentionMatches.length > 0) {
                                if (e.key === "ArrowDown") { e.preventDefault(); setTcMentionIndex(i => Math.min(i + 1, tcMentionMatches.length - 1)); return; }
                                if (e.key === "ArrowUp") { e.preventDefault(); setTcMentionIndex(i => Math.max(i - 1, 0)); return; }
                                if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); tcInsertMention(tcMentionMatches[tcMentionIndex]); return; }
                                if (e.key === "Escape") { setTcMentionQuery(null); return; }
                              }
                              if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && (messageBody.trim() || tcFileQueue.length > 0)) {
                                e.preventDefault();
                                postMessageMutation.mutate();
                              }
                            }}
                            className="w-full resize-none text-sm rounded-md border border-input bg-background px-3 py-2 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                          />
                          {/* File queue preview */}
                          {tcFileQueue.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {tcFileQueue.map((f, i) => (
                                <div key={i} className="flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs max-w-[200px]">
                                  {f.type.startsWith("image/") ? (
                                    <Image className="w-3 h-3 shrink-0 text-blue-500" />
                                  ) : (
                                    <FileText className="w-3 h-3 shrink-0 text-muted-foreground" />
                                  )}
                                  <span className="truncate">{f.name}</span>
                                  <button onClick={() => setTcFileQueue((q) => q.filter((_, j) => j !== i))}>
                                    <X className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="text-muted-foreground hover:text-foreground transition-colors"
                                title="Attach file"
                                onClick={() => tcFileInputRef.current?.click()}
                              >
                                <Paperclip className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                className="text-muted-foreground hover:text-foreground transition-colors"
                                title="Mention someone (@)"
                                onClick={() => {
                                  const ta = tcTextareaRef.current;
                                  if (!ta) return;
                                  const cursor = ta.selectionStart ?? messageBody.length;
                                  const next = messageBody.slice(0, cursor) + "@" + messageBody.slice(cursor);
                                  setMessageBody(next);
                                  setTcMentionQuery("");
                                  requestAnimationFrame(() => {
                                    ta.focus();
                                    ta.setSelectionRange(cursor + 1, cursor + 1);
                                  });
                                }}
                              >
                                <AtSign className="w-4 h-4" />
                              </button>
                            </div>
                            <input
                              ref={tcFileInputRef}
                              type="file"
                              multiple
                              accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx"
                              className="hidden"
                              onChange={tcHandleFileChange}
                            />
                            <Button
                              size="sm"
                              className="h-8 px-3 text-xs"
                              disabled={(!messageBody.trim() && tcFileQueue.length === 0) || postMessageMutation.isPending || tcUploading}
                              onClick={() => postMessageMutation.mutate()}
                            >
                              {(postMessageMutation.isPending || tcUploading) ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <><Send className="w-3.5 h-3.5 mr-1" />Send</>
                              )}
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* ── Client Chat tab ── */}
              <TabsContent value="client-chat" className="mt-0">
                <div className="rounded-lg border bg-card shadow-sm flex overflow-hidden" style={{ height: 540 }}>
                  {/* Topic list */}
                  <div className="w-48 shrink-0 border-r flex flex-col">
                    <div className="px-3 py-2 border-b flex items-center justify-between">
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Topics</span>
                      <button
                        className="text-primary hover:text-primary/80"
                        title="New topic"
                        onClick={() => setShowNewTopicInput(v => !v)}
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {showNewTopicInput && (
                      <div className="px-2 py-2 border-b flex gap-1">
                        <Input
                          autoFocus
                          placeholder="Topic name…"
                          value={newTopicName}
                          onChange={(e) => setNewTopicName(e.target.value)}
                          className="h-7 text-xs"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && newTopicName.trim()) createTopicMutation.mutate(newTopicName.trim());
                            if (e.key === "Escape") { setShowNewTopicInput(false); setNewTopicName(""); }
                          }}
                        />
                        <button
                          className="shrink-0 h-7 px-2 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50"
                          disabled={!newTopicName.trim() || createTopicMutation.isPending}
                          onClick={() => { if (newTopicName.trim()) createTopicMutation.mutate(newTopicName.trim()); }}
                        >
                          Add
                        </button>
                      </div>
                    )}
                    <div className="flex-1 overflow-y-auto">
                      {topicsLoading ? (
                        <div className="flex items-center justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
                      ) : clientTopics.length === 0 ? (
                        <p className="text-xs text-muted-foreground px-3 py-4">No topics yet. Click + to create one.</p>
                      ) : clientTopics.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => setSelectedTopicId(t.id)}
                          className={`w-full text-left px-3 py-2 text-sm truncate transition-colors ${
                            selectedTopicId === t.id
                              ? "bg-primary/10 text-primary font-medium"
                              : "hover:bg-muted text-foreground"
                          }`}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Message thread */}
                  <div className="flex-1 flex flex-col min-w-0">
                    {!selectedTopicId ? (
                      <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
                        <div className="text-center">
                          <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
                          Select a topic to view the conversation
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                          {topicMessagesLoading ? (
                            <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                          ) : topicMessages.length === 0 ? (
                            <div className="py-8 text-center text-sm text-muted-foreground">
                              No messages yet — start the conversation below
                            </div>
                          ) : (
                            topicMessages.map((m) => {
                              const isFromClient = !!m.author_portal_email;
                              // check if author
                              const canEditMsg = !isContractor && !!user?.id && m.author_user_id === user.id;
                              const isEditingMsg = editingClientMsgId === m.id;
                              const editTrimmed = editingClientMsgBody.trim();
                              return (
                                <div
                                  key={m.id}
                                  className={`rounded-lg px-3 py-2 text-sm space-y-0.5 ${
                                    isFromClient
                                      ? "bg-primary/10 border border-primary/20 ml-4"
                                      : "bg-muted border"
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold">
                                      {isFromClient ? (m.author_name ?? "Client") : (m.author_name ?? "Staff")}
                                    </span>
                                    {isFromClient && (
                                      <span className="text-xs text-primary font-medium">Client</span>
                                    )}
                                    <span className="text-xs text-muted-foreground ml-auto">
                                      {(() => { try { const d = new Date(m.created_at); return isNaN(d.getTime()) ? "" : format(d, "MMM d, h:mm a"); } catch { return ""; } })()}
                                    </span>
                                    {m.edited_at && (
                                      <span
                                        className="text-xs text-muted-foreground italic"
                                        title={(() => { try { const d = new Date(m.edited_at); return isNaN(d.getTime()) ? "This message was edited" : `Edited ${format(d, "MMM d, h:mm a")}`; } catch { return "This message was edited"; } })()}
                                      >
                                        (edited)
                                      </span>
                                    )}
                                    {/* don't give contractors the button */}
                                    {!isContractor && (
                                      <LikeButton
                                        likedByMe={m.likedByMe}
                                        likeCount={m.likeCount}
                                        likers={m.likers}
                                        onClick={() => likeClientTopicMessageMutation.mutate(m.id)}
                                      />
                                    )}
                                    {canEditMsg && !isEditingMsg && (
                                      <button
                                        className="text-muted-foreground hover:text-foreground transition-colors"
                                        title="Edit your message"
                                        onClick={() => { setEditingClientMsgId(m.id); setEditingClientMsgBody(m.body); }}
                                      >
                                        <Pencil className="w-3 h-3" />
                                      </button>
                                    )}
                                    <button
                                      className="text-muted-foreground hover:text-foreground transition-colors"
                                      title="Attachments"
                                      onClick={() => {
                                        setClientAttExpanded((prev) => ({ ...prev, [m.id]: !prev[m.id] }));
                                        fetchClientMsgAttachments(m.id);
                                      }}
                                    >
                                      <Paperclip className="w-3 h-3" />
                                    </button>
                                  </div>
                                  {isEditingMsg ? (
                                    <div className="space-y-1.5 pt-1">
                                      <Textarea
                                        value={editingClientMsgBody}
                                        onChange={(e) => setEditingClientMsgBody(e.target.value)}
                                        rows={3}
                                        className="text-sm resize-none"
                                        autoFocus
                                        onKeyDown={(e) => {
                                          if (e.key === "Escape") { e.preventDefault(); setEditingClientMsgId(null); }
                                          if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && editTrimmed && editTrimmed !== m.body) {
                                            e.preventDefault();
                                            editClientTopicMessageMutation.mutate({ messageId: m.id, body: editTrimmed });
                                          }
                                        }}
                                      />
                                      <div className="flex gap-1.5">
                                        <Button
                                          size="sm"
                                          className="h-6 text-xs px-2"
                                          onClick={() => editClientTopicMessageMutation.mutate({ messageId: m.id, body: editTrimmed })}
                                          disabled={!editTrimmed || editTrimmed === m.body || editClientTopicMessageMutation.isPending}
                                        >
                                          {editClientTopicMessageMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                        </Button>
                                        <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => setEditingClientMsgId(null)}>
                                          <X className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="whitespace-pre-wrap">{m.body}</p>
                                  )}
                                  {clientAttExpanded[m.id] && (
                                    <div className="mt-1.5">
                                      {clientMsgAttachments[m.id] === undefined ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                                      ) : clientMsgAttachments[m.id]!.length === 0 ? (
                                        <p className="text-xs text-muted-foreground italic">No attachments</p>
                                      ) : (
                                        <div className="flex flex-wrap gap-2">
                                          {clientMsgAttachments[m.id]!.map((a) => (
                                            <div key={a.id} className="group relative flex items-center gap-1.5 px-2 py-1.5 bg-background border rounded text-xs">
                                              {a.content_type?.startsWith("image/") && a.signed_url ? (
                                                <a href={a.signed_url} target="_blank" rel="noreferrer">
                                                  <img src={a.signed_url} alt={a.file_name} className="h-12 w-12 object-cover rounded" />
                                                </a>
                                              ) : (
                                                <a href={a.signed_url ?? "#"} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:underline">
                                                  <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                                  <span className="truncate max-w-[120px]">{a.file_name}</span>
                                                </a>
                                              )}
                                              {a.uploaded_by_portal_email && !a.uploaded_by_user_id && (
                                                <span className="text-xs text-primary ml-1">Client</span>
                                              )}
                                              <button
                                                className="absolute -top-1.5 -right-1.5 flex bg-background border rounded-full w-4 h-4 items-center justify-center text-muted-foreground/40 hover:text-destructive"
                                                onClick={async () => {
                                                  await deleteClientTopicMessageAttachment(a.id);
                                                  setClientMsgAttachments((prev) => ({
                                                    ...prev,
                                                    [m.id]: (prev[m.id] ?? []).filter((x) => x.id !== a.id),
                                                  }));
                                                }}
                                              >
                                                <X className="w-2.5 h-2.5" />
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                        <div className="px-4 pb-4 border-t pt-3 space-y-2">
                          {isContractor ? (
                            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                              <p className="font-medium">View-only mode</p>
                              <p className="text-xs">Contractors cannot send client messages. Contact your team for client communication needs.</p>
                            </div>
                          ) : (
                            <Textarea
                              rows={3}
                              placeholder="Message to client… (⌘+Enter to send)"
                              value={clientChatBody}
                              onChange={(e) => setClientChatBody(e.target.value)}
                              className="resize-none text-sm"
                              onPaste={handleClientPaste}
                              onKeyDown={(e) => {
                                const canSend = (clientChatBody.trim() || clientFileQueue.length > 0) && !postClientMsgMutation.isPending && !clientUploading;
                                if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canSend) {
                                  e.preventDefault();
                                  postClientMsgMutation.mutate();
                                }
                              }}
                            />
                          )}
                          {clientFileQueue.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {clientFileQueue.map((f, i) => (
                                <div key={i} className="flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs max-w-[180px]">
                                  {f.type.startsWith("image/") ? (
                                    <Image className="w-3 h-3 shrink-0 text-blue-500" />
                                  ) : (
                                    <FileText className="w-3 h-3 shrink-0 text-muted-foreground" />
                                  )}
                                  <span className="truncate">{f.name}</span>
                                  <button onClick={() => setClientFileQueue((q) => q.filter((_, j) => j !== i))}>
                                    <X className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          {!isContractor && (
                            <div className="flex items-center justify-between">
                              <button
                                type="button"
                                className="text-muted-foreground hover:text-foreground transition-colors"
                                title="Attach file"
                                onClick={() => clientFileInputRef.current?.click()}
                              >
                                <Paperclip className="w-4 h-4" />
                              </button>
                              <input
                                ref={clientFileInputRef}
                                type="file"
                                multiple
                                accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx"
                                className="hidden"
                                onChange={handleClientFileChange}
                              />
                              <Button
                                size="sm"
                                className="h-8 px-3 text-xs"
                                disabled={!(clientChatBody.trim() || clientFileQueue.length > 0) || postClientMsgMutation.isPending || clientUploading}
                                onClick={() => postClientMsgMutation.mutate()}
                              >
                                {(postClientMsgMutation.isPending || clientUploading) ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <><Send className="w-3.5 h-3.5 mr-1" />Send</>
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* ── Emails tab ── */}
              <TabsContent value="emails" className="mt-0">
                <div className="rounded-lg border bg-card shadow-sm p-4 space-y-3">
                  {/* Header row */}
                  <div className="flex items-center justify-between gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs gap-1"
                      onClick={() => refetchEmails()}
                      disabled={emailsLoading}
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${emailsLoading ? "animate-spin" : ""}`} />
                      Refresh
                    </Button>
                  </div>

                  {/* Search + date filters */}
                  {emailData && emailData.emails.filter(e => !e.isSharedMailbox).length > 0 && (
                    <div className="flex flex-wrap gap-2 items-center">
                      <div className="relative flex-1 min-w-[180px]">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                        <input
                          type="text"
                          placeholder="Search subject, sender…"
                          className="w-full h-7 pl-7 pr-2 text-xs border rounded bg-background"
                          value={emailSearch}
                          onChange={e => { setEmailSearch(e.target.value); setEmailShowCount(25); }}
                        />
                      </div>
                      <input
                        type="date"
                        className="h-7 px-2 text-xs border rounded bg-background"
                        value={emailDateFrom}
                        onChange={e => { setEmailDateFrom(e.target.value); setEmailShowCount(25); }}
                        title="From date"
                      />
                      <span className="text-xs text-muted-foreground">–</span>
                      <input
                        type="date"
                        className="h-7 px-2 text-xs border rounded bg-background"
                        value={emailDateTo}
                        onChange={e => { setEmailDateTo(e.target.value); setEmailShowCount(25); }}
                        title="To date"
                      />
                      <select
                        className="h-7 px-2 text-xs border rounded bg-background"
                        value={emailSort}
                        onChange={e => setEmailSort(e.target.value as typeof emailSort)}
                      >
                        <option value="date-desc">Newest first</option>
                        <option value="date-asc">Oldest first</option>
                        <option value="sender">Sender A–Z</option>
                        <option value="pinned">Pinned first</option>
                      </select>
                      {(emailSearch || emailDateFrom || emailDateTo) && (
                        <button
                          className="text-xs text-muted-foreground hover:text-foreground"
                          onClick={() => { setEmailSearch(""); setEmailDateFrom(""); setEmailDateTo(""); setEmailShowCount(25); }}
                        >Clear</button>
                      )}
                    </div>
                  )}

                  {emailsLoading && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Searching mailboxes…
                    </div>
                  )}

                  {emailError && (
                    <div className="text-sm text-destructive py-4 text-center">
                      {(emailError as Error).message.includes("not configured")
                        ? "Email integration not configured. Ask your admin to set up the Microsoft 365 connection."
                        : (emailError as Error).message}
                    </div>
                  )}

                  {!emailsLoading && !emailError && emailData?.warning && (
                    <p className="text-sm text-amber-600">{emailData.warning}</p>
                  )}

                  {!emailsLoading && !emailError && emailData && emailData.emails.filter(e => !e.isSharedMailbox).length === 0 && !emailData?.warning && (
                    <p className="text-sm text-muted-foreground py-4 text-center">No individual emails found for this client.</p>
                  )}

                  {!emailsLoading && !emailError && !emailData && (
                    <p className="text-sm text-muted-foreground py-4 text-center">Click "Refresh" or open this tab to load emails.</p>
                  )}

                  {(() => {
                    const allEmails = (emailData?.emails ?? []).filter(e => !e.isSharedMailbox);
                    const filtered = allEmails.filter(e => {
                      const q = emailSearch.toLowerCase();
                      if (q && !e.subject?.toLowerCase().includes(q) &&
                          !e.from?.emailAddress?.name?.toLowerCase().includes(q) &&
                          !e.from?.emailAddress?.address?.toLowerCase().includes(q)) return false;
                      if (emailDateFrom && new Date(e.receivedDateTime) < new Date(emailDateFrom)) return false;
                      if (emailDateTo && new Date(e.receivedDateTime) > new Date(emailDateTo + "T23:59:59")) return false;
                      return true;
                    });
                    const sorted = [...filtered].sort((a, b) => {
                      if (emailSort === "date-asc") return new Date(a.receivedDateTime).getTime() - new Date(b.receivedDateTime).getTime();
                      if (emailSort === "sender") return (a.from?.emailAddress?.name || a.from?.emailAddress?.address || "").localeCompare(b.from?.emailAddress?.name || b.from?.emailAddress?.address || "");
                      if (emailSort === "pinned")
                        return (pinnedEmailsMap.has(b.id) ? 1 : 0) - (pinnedEmailsMap.has(a.id) ? 1 : 0) ||
                          new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime();
                      return new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime(); // date-desc default
                    });
                    const visible = sorted.slice(0, emailShowCount);
                    return (
                      <>
                        {sorted.length === 0 && allEmails.length > 0 && (
                          <p className="text-sm text-muted-foreground py-4 text-center">No emails match your filters.</p>
                        )}
                        {visible.map((email: ClientEmail) => renderEmailRow(email))}
                        {sorted.length > emailShowCount && (
                          <button
                            className="w-full text-xs text-muted-foreground hover:text-foreground py-2 border rounded-md"
                            onClick={() => setEmailShowCount(c => c + 25)}
                          >
                            Show more ({sorted.length - emailShowCount} remaining)
                          </button>
                        )}
                        {sorted.length > 0 && (
                          <p className="text-xs text-muted-foreground text-center pt-1">
                            Showing {Math.min(emailShowCount, sorted.length)} of {sorted.length}{emailSearch || emailDateFrom || emailDateTo ? " filtered" : ""} emails
                          </p>
                        )}
                      </>
                    );
                  })()}

                  {/* Filtered (noise-filter) bucket — set aside, never deleted, default collapsed.
                      Per-tab since the unification: this bucket shows only contact-matched rows;
                      the Shared Inbox tab has its own bucket for mapped-mailbox rows. */}
                  {(() => {
                    const bucket = (emailData?.filtered ?? []).filter(e => !e.isSharedMailbox);
                    if (!bucket.length) return null;
                    return (
                      <div className="border rounded-md overflow-hidden opacity-80">
                        <button
                          className="w-full text-left px-3 py-2 bg-muted/30 hover:bg-muted/50 transition-colors flex items-center gap-2 text-xs font-medium text-muted-foreground"
                          onClick={() => setFilteredEmailsExpanded(v => !v)}
                        >
                          {filteredEmailsExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          Filtered ({bucket.length})
                        </button>
                        {filteredEmailsExpanded && (
                          <div className="p-2 space-y-2 bg-muted/10">
                            {bucket.map((email: ClientEmail) => renderEmailRow(email))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </TabsContent>

              {/* ── Shared Inbox tab ── */}
              <TabsContent value="shared-inbox" className="mt-0">
                <div className="rounded-lg border bg-card shadow-sm p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    {/* Contractors can't send as a shared mailbox — no compose. */}
                    {!isContractor ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs gap-1"
                        onClick={() => setComposeOpen(true)}
                      >
                        New email
                      </Button>
                    ) : (
                      <span />
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs gap-1"
                      onClick={() => { sharedInboxSticky.reset(); sharedInboxSelection.clear(); olderPages.reset(); refetchEmails(); }}
                      disabled={emailsLoading}
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${emailsLoading ? "animate-spin" : ""}`} />
                      Refresh
                    </Button>
                  </div>

                  {emailData && emailData.emails.filter(e => e.isSharedMailbox).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {([
                        { key: "all", label: "All" },
                        { key: "unread", label: "Unread" },
                        { key: "mine", label: "Mine" },
                        { key: "unclaimed", label: "Unclaimed" },
                        { key: "starred", label: "Starred" },
                      ] as { key: EmailPill; label: string }[]).map(f => (
                        <Button
                          key={f.key}
                          size="sm"
                          variant={sharedInboxPill === f.key ? "default" : "outline"}
                          className="h-7 px-2.5 text-xs"
                          onClick={() => setSharedInboxPill(f.key)}
                        >
                          {f.label}
                        </Button>
                      ))}
                    </div>
                  )}

                  {emailData && emailData.emails.filter(e => e.isSharedMailbox).length > 0 && (
                    <div className="flex flex-wrap gap-2 items-center">
                      <div className="relative flex-1 min-w-[180px]">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                        <input
                          type="text"
                          placeholder="Search subject, sender…"
                          className="w-full h-7 pl-7 pr-2 text-xs border rounded bg-background"
                          value={emailSearch}
                          onChange={e => { setEmailSearch(e.target.value); setEmailShowCount(25); }}
                        />
                      </div>
                      <input type="date" className="h-7 px-2 text-xs border rounded bg-background" value={emailDateFrom} onChange={e => { setEmailDateFrom(e.target.value); setEmailShowCount(25); }} title="From date" />
                      <span className="text-xs text-muted-foreground">–</span>
                      <input type="date" className="h-7 px-2 text-xs border rounded bg-background" value={emailDateTo} onChange={e => { setEmailDateTo(e.target.value); setEmailShowCount(25); }} title="To date" />
                      <select className="h-7 px-2 text-xs border rounded bg-background" value={emailSort} onChange={e => setEmailSort(e.target.value as typeof emailSort)}>
                        <option value="date-desc">Newest first</option>
                        <option value="date-asc">Oldest first</option>
                        <option value="sender">Sender A–Z</option>
                        <option value="pinned">Pinned first</option>
                      </select>
                      {(emailSearch || emailDateFrom || emailDateTo) && (
                        <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => { setEmailSearch(""); setEmailDateFrom(""); setEmailDateTo(""); setEmailShowCount(25); }}>Clear</button>
                      )}
                    </div>
                  )}

                  {emailsLoading && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
                      <Loader2 className="w-4 h-4 animate-spin" />Searching mailboxes…
                    </div>
                  )}
                  {emailError && (
                    <div className="text-sm text-destructive py-4 text-center">{(emailError as Error).message}</div>
                  )}
                  {!emailsLoading && !emailError && emailData?.warning && (
                    <p className="text-sm text-destructive py-2 px-3 bg-destructive/10 rounded border border-destructive/20">
                      {emailData.warning}
                    </p>
                  )}
                  {!emailsLoading && !emailError && emailData && emailData.emails.filter(e => e.isSharedMailbox).length === 0 && (
                    <p className="text-sm text-muted-foreground py-4 text-center">No shared inbox emails found for this client.</p>
                  )}
                  {!emailsLoading && !emailError && !emailData && (
                    <p className="text-sm text-muted-foreground py-4 text-center">Click "Refresh" to load emails.</p>
                  )}

                  {/* Count = selection ∩ visible — search/date filters hide rows the action
                      will not reach (review finding, v1.5.62). */}
                  {sharedInboxSelection.visibleSelected.length > 0 && (
                    <BulkEmailActionBar
                      count={sharedInboxSelection.visibleSelected.length}
                      users={assignableUsers}
                      currentUserId={user?.id ?? ""}
                      knownCategories={knownCategories}
                      busy={emailActions.bulkBusy}
                      onMarkRead={(isRead) => runSharedInboxBulk({ type: "read", isRead })}
                      onAssign={(u) => runSharedInboxBulk({ type: "assign", user: u })}
                      onFlag={(flagged) => runSharedInboxBulk({ type: "flag", flagged })}
                      onCategorize={(label) => runSharedInboxBulk({ type: "categorize", label })}
                      onClear={sharedInboxSelection.clear}
                    />
                  )}

                  {sharedInbox.sorted.length === 0 && sharedInbox.all.length > 0 && (
                    <p className="text-sm text-muted-foreground py-4 text-center">No emails match your filters.</p>
                  )}
                  {sharedInbox.visible.map((email: ClientEmail) =>
                    renderEmailRow(email, {
                      selected: sharedInboxSelection.isSelected(email.id),
                      active: sharedInboxSelection.active,
                      onToggle: (shiftKey: boolean) => sharedInboxSelection.toggle(email.id, shiftKey),
                    })
                  )}
                  {sharedInbox.sorted.length > emailShowCount && (
                    <button className="w-full text-xs text-muted-foreground hover:text-foreground py-2 border rounded-md" onClick={() => setEmailShowCount(c => c + 25)}>
                      Show more ({sharedInbox.sorted.length - emailShowCount} remaining)
                    </button>
                  )}
                  {sharedInbox.sorted.length > 0 && (
                    <p className="text-xs text-muted-foreground text-center pt-1">
                      Showing {Math.min(emailShowCount, sharedInbox.sorted.length)} of {sharedInbox.sorted.length}{emailSearch || emailDateFrom || emailDateTo ? " filtered" : ""} emails
                    </p>
                  )}
                  {/* "Load older" pages the retention archive past this tab's 100-row window.
                      "Show more" above only reveals rows already fetched; this one fetches. */}
                  {(() => {
                    const mailboxes = sharedInboxTargets.map((t) => t.mailbox);
                    if (mailboxes.length === 0) return null;
                    if (olderPages.exhaustedAll(mailboxes)) {
                      return (
                        <p className="text-xs text-muted-foreground text-center pt-1">No older emails.</p>
                      );
                    }
                    // The badge counts the whole archive; the list only ever holds what has been
                    // fetched. A positive gap is the mail the My Accounts bubble is pointing at and
                    // the user otherwise has no way to find. Worded as "not in the loaded window"
                    // rather than an exact claim: sharedUnread is de-noised and dupe-adjusted while
                    // this local count is not, so the two can differ by a row or two.
                    const loadedUnread = sharedInbox.all.filter((e) => !e.isRead).length;
                    const missingUnread = (emailData?.sharedUnread ?? 0) - loadedUnread;
                    return (
                      <>
                        {missingUnread > 0 && (
                          <p className="text-xs text-amber-700 text-center pt-1">
                            {missingUnread} unread not in the loaded window — load older to reach them.
                          </p>
                        )}
                        <button
                          className="w-full text-xs text-muted-foreground hover:text-foreground py-2 border rounded-md disabled:opacity-50"
                          disabled={olderPages.loadingAny}
                          onClick={loadOlderSharedInbox}
                        >
                          {olderPages.loadingAny ? "Loading older emails…" : "Load older"}
                        </button>
                      </>
                    );
                  })()}

                  {/* Filtered (noise) bucket — mapped-mailbox rows only (per-tab since unification). */}
                  {(() => {
                    const bucket = (emailData?.filtered ?? []).filter(e => e.isSharedMailbox);
                    if (!bucket.length) return null;
                    return (
                      <div className="border rounded-md overflow-hidden opacity-80">
                        <button
                          className="w-full text-left px-3 py-2 bg-muted/30 hover:bg-muted/50 transition-colors flex items-center gap-2 text-xs font-medium text-muted-foreground"
                          onClick={() => setFilteredEmailsExpanded(v => !v)}
                        >
                          {filteredEmailsExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          Filtered ({bucket.length})
                        </button>
                        {filteredEmailsExpanded && (
                          <div className="p-2 space-y-2 bg-muted/10">
                            {bucket.map((email: ClientEmail) => renderEmailRow(email))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </TabsContent>

              {/* ── Documents tab ── */}
              <TabsContent value="documents" className="mt-0">
                <ClientDocuments accountId={account.id} />
              </TabsContent>

              {/* ── Portal tab — client uploads + comments ── */}
              <TabsContent value="portal" className="mt-0">
                <PortalActivityFeed items={portalActivity} loading={portalActivityLoading} accountId={account.id} />
              </TabsContent>

              {/* ── Scope tab — machine-extracted commitments + red flags (migration 115) ── */}
              <TabsContent value="scope" className="mt-0">
                <ClientScopeItems accountId={account.id} />
              </TabsContent>

            </Tabs>
        </div>
      </div>

      {showPortalDialog && (
        <PortalDialog account={account} onClose={() => setShowPortalDialog(false)} />
      )}

      {catchMeUpOpen && (
        <CatchMeUpModal
          accountId={account.id}
          accountName={account.account_name}
          onClose={() => setCatchMeUpOpen(false)}
        />
      )}

      {/* SMS send modal */}
      <Dialog open={!!smsSendContact} onOpenChange={(open) => { if (!open) { setSmsSendContact(null); setSmsConfirming(false); setSmsDraftBody(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquareText className="w-4 h-4 text-primary" />
              Send SMS
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const smsContacts = contacts.filter((c) => c.sms_ok && c.mobile);
            if (smsContacts.length <= 1) return null;
            return (
              <div className="flex items-center gap-2 -mt-1">
                <span className="text-xs text-muted-foreground shrink-0">To:</span>
                <select
                  className="flex-1 h-7 rounded border bg-background px-2 text-xs"
                  value={smsSendContact?.id ?? ""}
                  onChange={(e) => {
                    const c = smsContacts.find((x) => x.id === e.target.value);
                    if (c) { setSmsSendContact(c); setSmsConfirming(false); }
                  }}
                >
                  {smsContacts.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} — {c.mobile}</option>
                  ))}
                </select>
              </div>
            );
          })()}

          {!smsConfirming ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Quick presets</p>
                <div className="space-y-1.5">
                  {SMS_PRESETS.map((preset, i) => (
                    <button
                      key={i}
                      className="w-full text-left text-xs rounded border px-2.5 py-2 hover:bg-muted/50 transition-colors"
                      onClick={() => setSmsDraftBody(preset)}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <Textarea
                  placeholder="Or type a custom message…"
                  className="text-sm resize-none"
                  rows={3}
                  value={smsDraftBody}
                  onChange={(e) => setSmsDraftBody(e.target.value)}
                  maxLength={1600}
                />
                <p className="text-xs text-muted-foreground text-right">{smsDraftBody.length}/1600</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border bg-muted/30 p-3 text-sm">{smsDraftBody}</div>
              <p className="text-sm text-muted-foreground">
                Send to <strong>{smsSendContact?.name}</strong> at <strong>{smsSendContact?.mobile}</strong>?
              </p>
            </div>
          )}

          <DialogFooter className="gap-2">
            {smsConfirming ? (
              <>
                <Button variant="outline" size="sm" onClick={() => setSmsConfirming(false)} disabled={smsSending}>
                  Back
                </Button>
                <Button
                  size="sm"
                  disabled={smsSending}
                  onClick={async () => {
                    if (!smsSendContact?.mobile || !smsDraftBody.trim() || !id) return;
                    setSmsSending(true);
                    try {
                      await api.sendSmsFromProfile({
                        to: smsSendContact.mobile,
                        body: smsDraftBody.trim(),
                        account_id: id,
                        contact_id: smsSendContact.id,
                      });
                      toast({ title: "SMS sent!" });
                      queryClient.invalidateQueries({ queryKey: ["sms-log", id] });
                      setSmsSendContact(null);
                      setSmsConfirming(false);
                      setSmsDraftBody("");
                    } catch (err: any) {
                      toast({ title: "Failed to send SMS", description: err.message, variant: "destructive" });
                    } finally {
                      setSmsSending(false);
                    }
                  }}
                >
                  {smsSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                  {smsSending ? "Sending…" : "Send SMS"}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => { setSmsSendContact(null); setSmsDraftBody(""); }}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={!smsDraftBody.trim()}
                  onClick={() => setSmsConfirming(true)}
                >
                  Review & Send
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* SmartVault Panel — non-contractor staff, wide slide-out vault browser */}
      {user?.role !== "contractor" && account && (
        <SmartVaultPanel
          open={svPanelOpen}
          onClose={() => setSvPanelOpen(false)}
          accountId={id!}
          clientName={account.account_name}
          svClientId={account.sv_client_id}
        />
      )}

      {/* Claim / reassign with notes + route-to-project (shared with /inbox) */}
      <ClaimEmailDialog
        email={claimDialogEmail}
        users={assignableUsers}
        actions={emailActions}
        onClose={() => setClaimDialogEmail(null)}
      />

      {/* Forward to arbitrary recipients (shared with /inbox + pop-out) */}
      <EmailForwardDialog email={forwardDialogEmail} onClose={() => setForwardDialogEmail(null)} />

      {/* Compose-from-scratch, defaulting From to this account's mapped mailbox */}
      <ComposeEmailDialog
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        fromOptions={sendableMailboxes}
        defaultFrom={accountMappedMailbox}
        defaultAccountId={id ?? null}
        contactSuggestions={contacts.filter((c) => !!c.email).map((c) => ({ name: c.name ?? c.email!, email: c.email! }))}
        onSend={async (payload) => { await emailApi.composeEmail(payload); }}
      />

      {/* Reply to a client email as the per-client mailbox (C2) */}
      <EmailReplyModal
        email={replyEmail?.email ?? null}
        accountId={id!}
        open={!!replyEmail}
        onClose={() => setReplyEmail(null)}
        onSent={() => { refetchEmails(); }}
        replyAll={replyEmail?.replyAll}
      />

      {/* Note create / edit dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={(o) => { if (!o) setNoteDialogOpen(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{noteDialogNote ? "Edit Note" : "New Note"}</DialogTitle>
            <DialogDescription>
              {noteDialogNote ? "Update this client note." : "Add a note to this client's profile."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Title (optional)</label>
              <Input
                placeholder="Note title…"
                value={noteDialogTitle}
                onChange={(e) => setNoteDialogTitle(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Content</label>
              <Textarea
                placeholder="Write your note here…"
                rows={6}
                className="resize-none text-sm"
                value={noteDialogBody}
                onChange={(e) => setNoteDialogBody(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="note-pinned"
                checked={noteDialogPinned}
                onCheckedChange={(v) => setNoteDialogPinned(!!v)}
              />
              <label htmlFor="note-pinned" className="text-sm cursor-pointer select-none">
                Pin this note (shows at top)
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
            <Button
              disabled={noteDialogSaving || (!noteDialogTitle.trim() && !noteDialogBody.trim())}
              onClick={saveNote}
            >
              {noteDialogSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {noteDialogNote ? "Save Changes" : "Add Note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {showScheduleMeeting && account && (
        <ScheduleMeetingModal
          accountId={id!}
          accountName={account.account_name}
          contacts={contacts}
          onClose={() => setShowScheduleMeeting(false)}
        />
      )}
      {rescheduleMeeting && (
        <RescheduleMeetingDialog
          meeting={rescheduleMeeting}
          saving={rescheduleMeetingMutation.isPending}
          onClose={() => setRescheduleMeeting(null)}
          onSave={(patch) => rescheduleMeetingMutation.mutate({ meetingId: rescheduleMeeting.id, ...patch })}
        />
      )}
      {showSendSignature && account && (
        <SendForSignatureModal
          accountId={id!}
          accountName={account.account_name}
          templates={agreementTemplates}
          onClose={() => setShowSendSignature(false)}
          onSent={() => {
            setShowSendSignature(false);
            queryClient.invalidateQueries({ queryKey: ["account-signature-requests", id] });
          }}
        />
      )}
      <MakeClientInactiveDialog
        account={pendingInactive}
        onClose={() => setPendingInactive(null)}
        invalidateKeys={[["account", id]]}
      />
    </Layout>
  );
}
